import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  X,
  Save,
  Mic,
  Check,
  Sparkles,
  RotateCcw,
  Search,
  Trash2,
  Users,
  Maximize2,
  Minimize2,
  User,
  Folder,
  CheckSquare,
  ClipboardList,
  Coffee,
  Car,
  Wrench,
  Package,
  Edit,
  Building2,
  WifiOff,
  Wifi,
  Info,
  Calendar,
  Clock,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { usePermissions } from "../contexts/PermissionsContext";
import { useTranslation } from "../i18n/hooks/useTranslation";
import {
  GoogleAddressAutocomplete,
  AddressDetails,
} from "./common/GoogleAddressAutocomplete";
import { AIDescriptionField } from "./common/AIDescriptionField";
import { offlineSync } from "../utils/offlineSync";
import { offlineDataCache, CACHE_KEYS } from "../utils/offlineDataCache";
import { ConfirmDialog } from "./common/ConfirmDialog";
import { formatDate as formatDateUtil } from "../utils/dateUtils";
import { useTokenCheck } from "../hooks/useTokenCheck";

interface TimesheetFormProps {
  onClose: () => void;
  onSave?: (detail?: { dates?: string[] }) => void;
  editingTimesheet?: any;
  selectedStaff?: string[];
  onStaffChange?: (staffIds: string[]) => void;
  staffList?: any[];
  preselectedDate?: Date | null;
  aiTimeEntries?: any[] | null;
  preselectedProjectId?: string | null;
  preselectedTaskId?: string | null;
}

interface PayPeriodDay {
  date: Date;
  dateString: string;
  hasTimesheet: boolean;
  isToday: boolean;
}

export const TimesheetForm: React.FC<TimesheetFormProps> = ({
  onClose,
  onSave,
  editingTimesheet,
  selectedStaff = [],
  onStaffChange,
  staffList = [],
  preselectedDate,
  aiTimeEntries = null,
  preselectedProjectId = null,
  preselectedTaskId = null,
}) => {
  const { handleTokenError } = useTokenCheck();
  console.log("🎯 TimesheetForm mounted - Checking Google Maps API key...");
  console.log(
    "API Key from env:",
    process.env.REACT_APP_GOOGLE_MAPS_API_KEY ? "SET ✅" : "NOT SET ❌",
  );
  const { user, refreshUser } = useAuth();
  const { canAddAll } = usePermissions();
  const { t, language } = useTranslation();
  const isSystemAdmin = user?.role === "System Administrator";

  // Date locale for formatting
  const dateLocale = language === "zh" ? "zh-CN" : "en-US";

  // Check if user can add timesheets for others
  const canAddForOthers = isSystemAdmin || canAddAll("Timesheets");

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingSyncCount, setPendingSyncCount] = useState(0);

  // Pay period and date selection
  const [payPeriodDays, setPayPeriodDays] = useState<PayPeriodDay[]>([]);
  const [currentPeriodOffset, setCurrentPeriodOffset] = useState<number>(0);
  const [periodOffsetInitialized, setPeriodOffsetInitialized] =
    useState<boolean>(false);
  const todayRef = useRef<HTMLButtonElement>(null);

  // Helper function to get local date string (YYYY-MM-DD) - avoids timezone issues
  const getLocalDateString = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const todayStr = getLocalDateString(new Date());

  // Use preselectedDate if provided, otherwise use editingTimesheet date or today
  const initialDate = preselectedDate
    ? preselectedDate.toISOString().split("T")[0]
    : editingTimesheet?.date
      ? new Date(editingTimesheet.date).toISOString().split("T")[0]
      : todayStr;

  const [selectedDate, setSelectedDate] = useState<string>(initialDate);

  // Multi-day selection mode state
  const [multiDayMode, setMultiDayMode] = useState<boolean>(false);
  const [selectedDays, setSelectedDays] = useState<Set<string>>(new Set());
  const multiDayLongPressTimerRef = useRef<NodeJS.Timeout | null>(null);
  const multiDayLongPressStartRef = useRef<{
    dateString: string;
    startTime: number;
  } | null>(null);

  // Update selectedDate when preselectedDate changes (e.g., from clicking a day circle)
  useEffect(() => {
    if (preselectedDate) {
      const dateStr = getLocalDateString(preselectedDate);
      console.log(
        "[TIMESHEET FORM] Setting date from preselectedDate:",
        dateStr,
      );
      setSelectedDate(dateStr);

      // Calculate which pay period this date falls into and set offset
      // This ensures the pay period calendar shows the correct week/fortnight
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const preselectedDateNormalized = new Date(preselectedDate);
      preselectedDateNormalized.setHours(0, 0, 0, 0);

      // Calculate days difference between today and preselected date
      const daysDiff = Math.floor(
        (preselectedDateNormalized.getTime() - today.getTime()) /
          (1000 * 60 * 60 * 24),
      );

      // Assume weekly pay period (7 days) - will be refined by fetchPayPeriod
      // Calculate approximate period offset
      const periodLength = 7; // Default to weekly, fetchPayPeriod will use actual settings
      const periodOffset = Math.floor(daysDiff / periodLength);

      console.log(
        "[TIMESHEET FORM] Calculated period offset:",
        periodOffset,
        "for date:",
        dateStr,
      );
      setCurrentPeriodOffset(periodOffset);
      setPeriodOffsetInitialized(true);
    } else {
      // No preselected date, use current period (offset 0)
      setPeriodOffsetInitialized(true);
    }
  }, [preselectedDate]);

  // Time selection
  const [workHourStart, setWorkHourStart] = useState<string>("08:00");
  const [workHourEnd, setWorkHourEnd] = useState<string>("18:00");
  const [selectedStartTime, setSelectedStartTime] = useState<string>("");
  const [selectedEndTime, setSelectedEndTime] = useState<string>("");
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [dragStartTime, setDragStartTime] = useState<string>("");
  const [tapSelectionMode, setTapSelectionMode] = useState<boolean>(false); // Tap-to-select mode for mobile
  const tapTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const longPressTimerRef = useRef<NodeJS.Timeout | null>(null);
  const touchStartPositionRef = useRef<{ x: number; y: number } | null>(null);
  const [isDragMode, setIsDragMode] = useState<boolean>(false); // Long-press drag mode for mobile

  // Details page state
  const [showDetails, setShowDetails] = useState<boolean>(false);
  const [entryType, setEntryType] = useState<
    "work" | "general" | "break" | "travel"
  >("work");
  const [selectedProject, setSelectedProject] = useState<string>("");
  const [selectedTask, setSelectedTask] = useState<string>("");
  const [projects, setProjects] = useState<any[]>([]);
  const [tasks, setTasks] = useState<any[]>([]);
  const [generalWorkItems, setGeneralWorkItems] = useState<any[]>([]);
  const [selectedGeneralWorkItem, setSelectedGeneralWorkItem] =
    useState<string>("");
  const [equipment, setEquipment] = useState<any[]>([]);
  const [selectedEquipment, setSelectedEquipment] = useState<string[]>([]);
  const [materials, setMaterials] = useState<any[]>([]);
  const [selectedMaterials, setSelectedMaterials] = useState<
    Array<{ _id: string; quantity: number; unit: string; name: string }>
  >([]);
  // Material quantity modal state
  const [showMaterialQuantityModal, setShowMaterialQuantityModal] =
    useState<boolean>(false);
  const [pendingMaterial, setPendingMaterial] = useState<any>(null);
  const [materialQuantity, setMaterialQuantity] = useState<string>("");
  const [notes, setNotes] = useState<string>("");
  const [travelStartLocation, setTravelStartLocation] = useState<string>("");
  const [travelEndLocation, setTravelEndLocation] = useState<string>("");
  const [selectedStartQuickAddress, setSelectedStartQuickAddress] = useState<
    string | null
  >(null); // 'office', 'home', project._id, or qa.id
  const [selectedEndQuickAddress, setSelectedEndQuickAddress] = useState<
    string | null
  >(null);
  const [travelDistance, setTravelDistance] = useState<number>(0);
  const [travelDistanceUnit, setTravelDistanceUnit] = useState<"km" | "miles">(
    "km",
  );
  const [isCalculatingDistance, setIsCalculatingDistance] =
    useState<boolean>(false);
  const [isRecording, setIsRecording] = useState<boolean>(false);
  const [isFormattingAI, setIsFormattingAI] = useState<boolean>(false);
  const [originalNotes, setOriginalNotes] = useState<string>("");
  const [aiFormattedNotes, setAiFormattedNotes] = useState<string>("");
  const [showAIPreview, setShowAIPreview] = useState<boolean>(false);
  const [taskSelectionConfirmed, setTaskSelectionConfirmed] =
    useState<boolean>(false);

  // Confirmation dialog state
  const [showConfirmDialog, setShowConfirmDialog] = useState<boolean>(false);
  const [confirmCallback, setConfirmCallback] = useState<(() => void) | null>(
    null,
  );
  const [confirmMessage, setConfirmMessage] = useState<string>("");

  // Submission summary dialog state
  const [showSubmissionSummary, setShowSubmissionSummary] =
    useState<boolean>(false);
  const [expandedSummaryDates, setExpandedSummaryDates] = useState<Set<string>>(
    new Set(),
  );

  // Timesheets for the selected date
  const [dateTimesheets, setDateTimesheets] = useState<any[]>([]);

  // Local work entries (not saved yet)
  const [localWorkEntries, setLocalWorkEntries] = useState<any[]>([]);

  // Editing work entry state
  const [editingWorkEntry, setEditingWorkEntry] = useState<any>(null);
  const [isDraggingEntry, setIsDraggingEntry] = useState<boolean>(false);
  const [dragEntryMode, setDragEntryMode] = useState<
    "resize-start" | "resize-end" | null
  >(null);
  const [dragEntryStartY, setDragEntryStartY] = useState<number>(0);
  const [dragEntryOriginalStart, setDragEntryOriginalStart] =
    useState<string>("");
  const [dragEntryOriginalEnd, setDragEntryOriginalEnd] = useState<string>("");
  const [editingEntryData, setEditingEntryData] = useState<any>(null); // Store entry being edited in details
  const [pendingEntryChanges, setPendingEntryChanges] = useState<{
    timesheetId: string;
    entryIndex: number;
    startTime: string;
    endTime: string;
    hours: number;
  } | null>(null);

  // Staff groups
  const [staffGroups, setStaffGroups] = useState<any[]>([]);
  const [selectedStaffGroups, setSelectedStaffGroups] = useState<any[]>([]); // Track selected groups

  // Account data for office address
  const [account, setAccount] = useState<any>(null);

  // Custom quick addresses
  const [customQuickAddresses, setCustomQuickAddresses] = useState<any[]>([]);
  const [addressToDelete, setAddressToDelete] = useState<string | null>(null);
  const [showSaveAddressDialog, setShowSaveAddressDialog] =
    useState<boolean>(false);
  const [addressToSave, setAddressToSave] = useState<string>("");
  const [addressName, setAddressName] = useState<string>("");
  const [addressSaveType, setAddressSaveType] = useState<"start" | "end">(
    "start",
  );

  // Edit address dialogs
  const [showEditOfficeDialog, setShowEditOfficeDialog] =
    useState<boolean>(false);
  const [showEditHomeDialog, setShowEditHomeDialog] = useState<boolean>(false);
  const [editAddressType, setEditAddressType] = useState<"start" | "end">(
    "start",
  );
  const [officeAddress, setOfficeAddress] = useState<AddressDetails | null>(
    null,
  );
  const [homeAddress, setHomeAddress] = useState<AddressDetails | null>(null);

  // Refs for drag handlers to avoid dependency issues
  const editingWorkEntryRef = useRef<any>(null);
  const dragEntryModeRef = useRef<"resize-start" | "resize-end" | null>(null);
  const entryClickTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const justFinishedDragRef = useRef<boolean>(false);
  const lastMouseDownWasScrollbarRef = useRef<boolean>(false);
  const lastManualAddressRef = useRef<{
    address: string;
    type: "start" | "end";
  }>({ address: "", type: "start" });

  useEffect(() => {
    editingWorkEntryRef.current = editingWorkEntry;
    dragEntryModeRef.current = dragEntryMode;
  }, [editingWorkEntry, dragEntryMode]);

  // Search states
  const [projectSearch, setProjectSearch] = useState<string>("");
  const [taskSearch, setTaskSearch] = useState<string>("");
  const [equipmentSearch, setEquipmentSearch] = useState<string>("");
  const [materialSearch, setMaterialSearch] = useState<string>("");
  const [staffSearch, setStaffSearch] = useState<string>("");
  const [showProjectSearch, setShowProjectSearch] = useState<boolean>(false);
  const [showTaskSearch, setShowTaskSearch] = useState<boolean>(false);
  const [showEquipmentSearch, setShowEquipmentSearch] =
    useState<boolean>(false);
  const [showMaterialSearch, setShowMaterialSearch] = useState<boolean>(false);
  const [showStaffSearch, setShowStaffSearch] = useState<boolean>(false);
  const [staffExpanded, setStaffExpanded] = useState<boolean>(false);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [showAllStaffWithTimesheets, setShowAllStaffWithTimesheets] =
    useState<boolean>(false);

  const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";

  // Fetch account data for office address and custom quick addresses
  useEffect(() => {
    const fetchAccount = async () => {
      const accountId = user?.currentAccountId || user?.accountId;
      if (!accountId) return;

      // Check cache first (for offline support)
      const cached = offlineDataCache.get<any>(CACHE_KEYS.ACCOUNT);
      if (cached) {
        console.log("[TimesheetForm] Using cached account data");
        setAccount(cached);
      }

      // If online, try to fetch fresh data
      if (isOnline) {
        try {
          const token = localStorage.getItem("token");
          const response = await fetch(`${apiUrl}/api/accounts/${accountId}`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });

          if (response.ok) {
            const data = await response.json();
            // Cache the account data (includes office address)
            offlineDataCache.set(CACHE_KEYS.ACCOUNT, data.account);
            setAccount(data.account);
          }
        } catch (error) {
          console.error("Failed to fetch account:", error);
          // If fetch fails and we don't have cached data, keep null
          if (!cached) {
            setAccount(null);
          }
        }
      } else {
        // Offline and no cache - keep null
        if (!cached) {
          console.warn(
            "[TimesheetForm] Offline and no cached account data available",
          );
          setAccount(null);
        }
      }
    };

    const fetchCustomQuickAddresses = async () => {
      const userId = user?._id;
      if (!userId) return;

      // Check cache first (for offline support)
      const cached = offlineDataCache.get<any[]>(
        CACHE_KEYS.USER_CUSTOM_QUICK_ADDRESSES(userId),
      );
      if (cached) {
        console.log(
          "[TimesheetForm] Using cached custom quick addresses:",
          cached.length,
        );
        setCustomQuickAddresses(cached);
      }

      // If online, try to fetch fresh data
      if (isOnline) {
        try {
          const token = localStorage.getItem("token");
          const response = await fetch(`${apiUrl}/api/users/${userId}`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });

          if (response.ok) {
            const data = await response.json();
            const addresses = data.data?.customQuickAddresses || [];
            // Cache the fresh data
            offlineDataCache.set(
              CACHE_KEYS.USER_CUSTOM_QUICK_ADDRESSES(userId),
              addresses,
            );
            setCustomQuickAddresses(addresses);
          }
        } catch (error) {
          console.error("Failed to fetch custom quick addresses:", error);
          // If fetch fails and we don't have cached data, keep empty array
          if (!cached) {
            setCustomQuickAddresses([]);
          }
        }
      } else {
        // Offline and no cache - keep empty array
        if (!cached) {
          console.warn(
            "[TimesheetForm] Offline and no cached custom quick addresses available",
          );
          setCustomQuickAddresses([]);
        }
      }
    };

    fetchAccount();
    fetchCustomQuickAddresses();
  }, [user, apiUrl]);

  useEffect(() => {
    // Only fetch after period offset is initialized
    if (!periodOffsetInitialized) return;

    console.log(
      "[TIMESHEET FORM] Fetching pay period with offset:",
      currentPeriodOffset,
    );
    fetchPayPeriod(currentPeriodOffset);
    fetchProjects();
    fetchEquipment();
    fetchMaterials();
    fetchGeneralWorkItems();
  }, [currentPeriodOffset, periodOffsetInitialized]);

  useEffect(() => {
    // Force select today on mount if not editing and no preselected date
    if (payPeriodDays.length > 0 && !editingTimesheet && !preselectedDate) {
      const today = payPeriodDays.find((day) => day.isToday);
      if (today) {
        // Always set to today to ensure UI is synced, even if already set
        setSelectedDate(today.dateString);
        // Scroll today into view immediately
        setTimeout(() => {
          const todayElement = document.querySelector('[data-is-today="true"]');
          if (todayElement) {
            todayElement.scrollIntoView({
              behavior: "auto",
              block: "nearest",
              inline: "center",
            });
          }
        }, 0);
      }
    }
  }, [payPeriodDays, editingTimesheet, preselectedDate]);

  useEffect(() => {
    // Fetch tasks when project is selected
    if (selectedProject) {
      fetchTasks(selectedProject);
      setTaskSelectionConfirmed(false); // Reset when project changes
    } else {
      setTasks([]);
      setSelectedTask("");
      setTaskSelectionConfirmed(false);
    }
  }, [selectedProject]);

  useEffect(() => {
    // Fetch timesheets when selected date or staff changes
    if (selectedDate) {
      fetchDateTimesheets(selectedDate);
    }
  }, [selectedDate, selectedStaff]);

  useEffect(() => {
    // Fetch staff groups on mount
    fetchStaffGroups();
  }, []);

  // Load Google Places API
  // Google Maps API is loaded globally by GoogleAddressAutocomplete component
  // No need to load it here or manually initialize autocomplete to avoid conflicts

  // Get quick address based on selection
  const getQuickAddress = async (type: string): Promise<string> => {
    try {
      if (type === "project" && selectedProject) {
        const project = projects.find((p: any) => p._id === selectedProject);

        // First check if project has siteAddress (preferred)
        if (project?.siteAddress) {
          const addr = project.siteAddress;
          return (
            addr.formatted ||
            [addr.street, addr.city, addr.state, addr.postalCode, addr.country]
              .filter(Boolean)
              .join(", ")
          );
        }

        // Otherwise, try to get address from client/contact
        if (project?.clientId) {
          const clientId =
            typeof project.clientId === "object"
              ? project.clientId._id
              : project.clientId;

          // Check cache first (for offline support)
          const cachedContact = offlineDataCache.get<any>(
            CACHE_KEYS.CONTACT(clientId),
          );
          if (cachedContact) {
            console.log(
              "[TimesheetForm] Using cached contact data for project address",
            );
            const addr = cachedContact.address;
            if (addr) {
              return [
                addr.street,
                addr.city,
                addr.state,
                addr.postalCode,
                addr.country,
              ]
                .filter(Boolean)
                .join(", ");
            }
          }

          // If online, try to fetch fresh data
          if (isOnline) {
            const token = localStorage.getItem("token");
            const response = await fetch(`${apiUrl}/api/contacts/${clientId}`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            if (response.ok) {
              const data = await response.json();
              const contact = data.data;
              // Cache the contact data (includes address)
              if (contact) {
                offlineDataCache.set(CACHE_KEYS.CONTACT(clientId), contact);
              }
              const addr = contact?.address;
              if (addr) {
                return [
                  addr.street,
                  addr.city,
                  addr.state,
                  addr.postalCode,
                  addr.country,
                ]
                  .filter(Boolean)
                  .join(", ");
              }
            }
          }
        }
      } else if (type === "office" && account?.address) {
        const addr = account.address;
        return (
          addr.formatted ||
          [addr.street, addr.city, addr.state, addr.postalCode, addr.country]
            .filter(Boolean)
            .join(", ")
        );
      } else if (type === "home") {
        // Fetch user's home address from user profile if available
        // Note: User data is typically cached in AuthContext, but we can fetch if needed
        if (isOnline) {
          const token = localStorage.getItem("token");
          const userId = user?._id;
          if (userId) {
            const response = await fetch(`${apiUrl}/api/users/${userId}`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            if (response.ok) {
              const data = await response.json();
              const addr = data.user?.address;
              if (addr) {
                return [
                  addr.street,
                  addr.city,
                  addr.state,
                  addr.postalCode,
                  addr.country,
                ]
                  .filter(Boolean)
                  .join(", ");
              }
            }
          }
        }
        // Fallback to account address if no home address
        if (account?.address) {
          const addr = account.address;
          return (
            addr.formatted ||
            [addr.street, addr.city, addr.state, addr.postalCode, addr.country]
              .filter(Boolean)
              .join(", ")
          );
        }
      }
    } catch (error) {
      console.error("Error fetching quick address:", error);
    }
    return "";
  };

  // Calculate distance between two addresses and determine unit based on client country
  const calculateDistance = async (startAddr: string, endAddr: string) => {
    if (!startAddr || !endAddr) {
      return;
    }

    // Disable distance calculation when offline
    if (!isOnline) {
      console.log("[TIMESHEET FORM] Distance calculation disabled - offline");
      setMessage(
        t("timesheet.distanceCalculationOffline") ||
          "Distance calculation unavailable offline",
      );
      return;
    }

    setIsCalculatingDistance(true);

    try {
      // First ensure Google Maps API is loaded
      if (!window.google?.maps) {
        console.log("🔍 [DISTANCE] Google Maps API not loaded, waiting...");
        let retries = 0;
        const maxRetries = 50;
        while (!window.google?.maps && retries < maxRetries) {
          await new Promise((resolve) => setTimeout(resolve, 100));
          retries++;
        }
        if (!window.google?.maps) {
          console.error(
            "❌ [DISTANCE] Google Maps API not available after waiting",
          );
          setIsCalculatingDistance(false);
          setMessage("Google Maps API not loaded. Please refresh the page.");
          return;
        }
      }

      // Test API key access by checking the script tag
      const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
      const scriptTag = document.querySelector(
        'script[src*="maps.googleapis.com"]',
      ) as HTMLScriptElement;
      const scriptSrc = scriptTag?.src;

      console.log("🔑 [DISTANCE] API Key Check:", {
        apiKeyInEnv: !!apiKey,
        apiKeyPrefix: apiKey ? apiKey.substring(0, 10) : "NOT SET",
        scriptSrc,
        scriptHasKey: scriptSrc?.includes("key="),
        currentOrigin: window.location.origin,
      });

      // If script doesn't have key, that's a problem
      if (scriptSrc && !scriptSrc.includes("key=")) {
        console.error(
          "❌ [DISTANCE] Google Maps script loaded without API key!",
        );
        setIsCalculatingDistance(false);
        setMessage(
          "Google Maps API key not found in script. Please check your environment variables.",
        );
        return;
      }

      // Use Routes API (REST) instead of legacy DirectionsService
      // Routes API is the new recommended API for distance/route calculations
      console.log(
        "🔍 [DISTANCE] Using Routes API (REST) for distance calculation",
      );

      // Determine distance unit based on client country
      let unitToUse: "km" | "miles" = "km"; // Default to km

      // Get project and client info to determine country
      if (selectedProject && projects.length > 0) {
        const project = projects.find((p: any) => p._id === selectedProject);
        if (project?.clientId) {
          const clientId =
            typeof project.clientId === "object"
              ? project.clientId._id
              : project.clientId;

          // Fetch client data to get country
          const token = localStorage.getItem("token");
          try {
            const response = await fetch(`${apiUrl}/api/contacts/${clientId}`, {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            });

            if (response.ok) {
              const data = await response.json();
              const country = data.data?.address?.country?.toLowerCase();

              // Use miles for US, UK, and a few other countries
              const milesCountries = [
                "us",
                "usa",
                "united states",
                "uk",
                "united kingdom",
                "gb",
                "liberia",
                "myanmar",
                "burma",
              ];
              if (country && milesCountries.some((c) => country.includes(c))) {
                unitToUse = "miles";
              }
            }
          } catch (err) {
            console.warn("Could not fetch client country, using km as default");
          }
        }
      }

      setTravelDistanceUnit(unitToUse);

      // Calculate distance using Google Routes API (REST)
      const routesApiUrl =
        "https://routes.googleapis.com/directions/v2:computeRoutes";

      console.log("🔍 [DISTANCE] Calling Routes API:", {
        origin: startAddr,
        destination: endAddr,
        travelMode: "DRIVE",
        unitSystem: unitToUse,
        apiKey: apiKey ? `${apiKey.substring(0, 10)}...` : "NOT SET",
        currentUrl: window.location.href,
      });

      try {
        const routesResponse = await fetch(routesApiUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Goog-Api-Key": apiKey || "",
            "X-Goog-FieldMask":
              "routes.duration,routes.distanceMeters,routes.legs.distanceMeters",
          },
          body: JSON.stringify({
            origin: {
              address: startAddr,
            },
            destination: {
              address: endAddr,
            },
            travelMode: "DRIVE",
            routingPreference: "TRAFFIC_AWARE",
            units: unitToUse === "miles" ? "IMPERIAL" : "METRIC",
          }),
        });

        const routesData = await routesResponse.json();

        console.log("📊 [DISTANCE] FULL Routes API Response:", {
          status: routesResponse.status,
          statusText: routesResponse.statusText,
          fullResponse: routesData,
          responseString: JSON.stringify(routesData, null, 2),
          responseKeys: routesData ? Object.keys(routesData) : [],
        });

        setIsCalculatingDistance(false);

        if (routesResponse.ok && routesData?.routes?.[0]) {
          const route = routesData.routes[0];
          const distanceMeters =
            route.distanceMeters || route.legs?.[0]?.distanceMeters || 0;

          if (distanceMeters > 0) {
            if (unitToUse === "km") {
              setTravelDistance(Math.round((distanceMeters / 1000) * 10) / 10); // Round to 1 decimal
            } else {
              const distanceInMiles = distanceMeters / 1609.34;
              setTravelDistance(Math.round(distanceInMiles * 10) / 10); // Round to 1 decimal
            }
          } else {
            setMessage("Could not calculate distance from route data.");
          }
        } else {
          const errorMsg =
            routesData?.error?.message ||
            routesData?.error?.status ||
            `HTTP ${routesResponse.status}`;
          console.error("❌ [DISTANCE] Routes API Error:", {
            status: routesResponse.status,
            statusText: routesResponse.statusText,
            error: routesData?.error,
            fullResponse: routesData,
          });

          if (routesResponse.status === 403 || routesResponse.status === 401) {
            setMessage(
              `Routes API denied: ${errorMsg}. Please ensure Routes API is enabled in Google Cloud Console.`,
            );
          } else if (routesResponse.status === 429) {
            setMessage(
              "Distance calculation quota exceeded. Please try again later.",
            );
          } else {
            setMessage(
              t("timesheet.distanceCalculationFailed") ||
                `Could not calculate distance: ${errorMsg}`,
            );
          }
        }
      } catch (error: any) {
        setIsCalculatingDistance(false);
        console.error("❌ [DISTANCE] Routes API Request Error:", error);
        setMessage(
          `Distance calculation failed: ${error.message || "Network error"}`,
        );
      }
    } catch (error) {
      console.error("Error calculating distance:", error);
      setIsCalculatingDistance(false);
    }
  };

  // Save custom quick address
  const saveCustomQuickAddress = async (name: string, address: string) => {
    try {
      const token = localStorage.getItem("token");
      const userId = user?._id;
      if (!userId) return;

      const response = await fetch(
        `${apiUrl}/api/users/${userId}/quick-addresses`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ name, address }),
        },
      );

      if (response.ok) {
        const data = await response.json();
        const updatedAddresses = [...customQuickAddresses, data.data];
        setCustomQuickAddresses(updatedAddresses);
        // Update cache
        offlineDataCache.set(
          CACHE_KEYS.USER_CUSTOM_QUICK_ADDRESSES(userId),
          updatedAddresses,
        );
        setMessage(
          t("timesheet.quickAddressSaved") ||
            "Quick address saved successfully",
        );
      }
    } catch (error) {
      console.error("Error saving quick address:", error);
      setMessage(
        t("timesheet.quickAddressSaveFailed") || "Failed to save quick address",
      );
    }
  };

  // Delete custom quick address
  const deleteCustomQuickAddress = async (addressId: string) => {
    try {
      const token = localStorage.getItem("token");
      const userId = user?._id;
      if (!userId) return;

      const response = await fetch(
        `${apiUrl}/api/users/${userId}/quick-addresses/${addressId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response.ok) {
        const updatedAddresses = customQuickAddresses.filter(
          (addr) => addr.id !== addressId,
        );
        setCustomQuickAddresses(updatedAddresses);
        // Update cache
        offlineDataCache.set(
          CACHE_KEYS.USER_CUSTOM_QUICK_ADDRESSES(userId),
          updatedAddresses,
        );
        setMessage(
          t("timesheet.quickAddressDeleted") ||
            "Quick address deleted successfully",
        );
      }
    } catch (error) {
      console.error("Error deleting quick address:", error);
      setMessage(
        t("timesheet.quickAddressDeleteFailed") ||
          "Failed to delete quick address",
      );
    }
  };

  // Save office address
  const saveOfficeAddress = async () => {
    if (!officeAddress || !officeAddress.formatted) return;

    try {
      const token = localStorage.getItem("token");
      const accountId = account?._id;
      if (!accountId) return;

      const response = await fetch(`${apiUrl}/api/accounts/${accountId}`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ address: officeAddress }),
      });

      if (response.ok) {
        const data = await response.json();
        const updatedAccount = { ...account, address: officeAddress };
        setAccount(updatedAccount);
        // Update cache with new account data (includes office address)
        offlineDataCache.set(CACHE_KEYS.ACCOUNT, updatedAccount);
        setMessage(
          t("timesheet.officeAddressSaved") ||
            "Office address saved successfully",
        );
        setShowEditOfficeDialog(false);

        // Apply the address to the travel field
        const addressStr = officeAddress.formatted;
        if (editAddressType === "start") {
          setTravelStartLocation(addressStr);
          if (travelEndLocation) {
            calculateDistance(addressStr, travelEndLocation);
          }
        } else {
          setTravelEndLocation(addressStr);
          if (travelStartLocation) {
            calculateDistance(travelStartLocation, addressStr);
          }
        }
      }
    } catch (error) {
      console.error("Error saving office address:", error);
      setMessage(
        t("timesheet.officeAddressSaveFailed") ||
          "Failed to save office address",
      );
    }
  };

  // Save home address
  const saveHomeAddress = async () => {
    if (!homeAddress || !homeAddress.formatted) return;

    try {
      const token = localStorage.getItem("token");
      const userId = user?._id;
      if (!userId) return;

      const response = await fetch(
        `${apiUrl}/api/users/${userId}`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ address: homeAddress }),
        },
      );

      if (response.ok) {
        setMessage(
          t("timesheet.homeAddressSaved") || "Home address saved successfully",
        );
        setShowEditHomeDialog(false);

        // Refresh user data to update the local user object with the saved address
        await refreshUser();

        // Apply the address to the travel field
        const addressStr = homeAddress.formatted;
        if (editAddressType === "start") {
          setTravelStartLocation(addressStr);
          setSelectedStartQuickAddress("home");
          if (travelEndLocation) {
            calculateDistance(addressStr, travelEndLocation);
          }
        } else {
          setTravelEndLocation(addressStr);
          setSelectedEndQuickAddress("home");
          if (travelStartLocation) {
            calculateDistance(travelStartLocation, addressStr);
          }
        }
      }
    } catch (error) {
      console.error("Error saving home address:", error);
      setMessage(
        t("timesheet.homeAddressSaveFailed") || "Failed to save home address",
      );
    }
  };

  // Handle address input blur - check if it's a new manual address
  const handleAddressBlur = (address: string, type: "start" | "end") => {
    if (!address || address.length < 5) return;

    // Check if this address matches any existing quick addresses
    const existsInQuick = customQuickAddresses.some(
      (qa) => qa.address === address,
    );
    const isSystemAddress = [
      account?.address &&
        [
          account.address.street,
          account.address.city,
          account.address.state,
          account.address.postalCode,
          account.address.country,
        ]
          .filter(Boolean)
          .join(", "),
    ].includes(address);

    // Only prompt if it's a new address and not a system address
    if (
      !existsInQuick &&
      !isSystemAddress &&
      address !== lastManualAddressRef.current.address
    ) {
      lastManualAddressRef.current = { address, type };
      setAddressToSave(address);
      setAddressSaveType(type);
      setAddressName("");
      setShowSaveAddressDialog(true);
    }
  };

  // Deselect staff who already have timesheets for the selected date (no exception - prevent duplicate timesheets)
  useEffect(() => {
    if (!onStaffChange) return; // Only when creating new timesheets

    const staffWithTimesheets = (
      Array.isArray(dateTimesheets) ? dateTimesheets : []
    )
      .map((ts: any) => {
        const id = ts.staffId?._id ?? ts.staffId;
        return id != null ? String(id) : "";
      })
      .filter(Boolean);

    if (staffWithTimesheets.length > 0) {
      const newSelectedStaff = selectedStaff.filter(
        (id) => !staffWithTimesheets.includes(String(id)),
      );
      if (newSelectedStaff.length !== selectedStaff.length) {
        onStaffChange(newSelectedStaff);
      }
    }
  }, [dateTimesheets, selectedDate]);

  // Default to start/end times when timesheet is loaded
  useEffect(() => {
    if (editingTimesheet && dateTimesheets.length > 0) {
      const timesheet = dateTimesheets.find(
        (ts: any) => ts._id === editingTimesheet._id,
      );
      if (timesheet) {
        // Get earliest start and latest end from all work entries
        const allStartTimes: Date[] = [];
        const allEndTimes: Date[] = [];

        if (timesheet.workEntries && timesheet.workEntries.length > 0) {
          timesheet.workEntries.forEach((entry: any) => {
            if (entry.startTime) allStartTimes.push(new Date(entry.startTime));
            if (entry.endTime) allEndTimes.push(new Date(entry.endTime));
          });
        } else if (timesheet.startTime && timesheet.endTime) {
          allStartTimes.push(new Date(timesheet.startTime));
          allEndTimes.push(new Date(timesheet.endTime));
        }

        if (allStartTimes.length > 0 && allEndTimes.length > 0) {
          const earliestStart = new Date(
            Math.min(...allStartTimes.map((d) => d.getTime())),
          );
          const latestEnd = new Date(
            Math.max(...allEndTimes.map((d) => d.getTime())),
          );

          const startHour = String(earliestStart.getHours()).padStart(2, "0");
          const startMin = String(earliestStart.getMinutes()).padStart(2, "0");
          const endHour = String(latestEnd.getHours()).padStart(2, "0");
          const endMin = String(latestEnd.getMinutes()).padStart(2, "0");

          setSelectedStartTime(`${startHour}:${startMin}`);
          setSelectedEndTime(`${endHour}:${endMin}`);

          console.log("🔄 [LOAD] Defaulted to timesheet times:", {
            startTime: `${startHour}:${startMin}`,
            endTime: `${endHour}:${endMin}`,
          });
        }
      }
    }
  }, [editingTimesheet, dateTimesheets]);

  // Global mouse listeners for dragging entries
  useEffect(() => {
    if (!isDraggingEntry) return;

    const handleGlobalMouseMove = (e: MouseEvent) => {
      if (!editingWorkEntryRef.current || !dragEntryModeRef.current) return;

      e.preventDefault();
      e.stopPropagation();

      // Find which time block we're over
      const timeBlocks = Array.from(
        document.querySelectorAll("[data-time]"),
      ) as HTMLElement[];
      const mouseY = e.clientY;

      let closestTimeBlock: HTMLElement | null = null;
      let closestDistance = Infinity;

      timeBlocks.forEach((block) => {
        const rect = block.getBoundingClientRect();
        const blockCenterY = rect.top + rect.height / 2;
        const distance = Math.abs(mouseY - blockCenterY);

        if (distance < closestDistance) {
          closestDistance = distance;
          closestTimeBlock = block;
        }
      });

      if (closestTimeBlock !== null && closestTimeBlock !== undefined) {
        const newTime = (closestTimeBlock as HTMLElement).getAttribute(
          "data-time",
        );
        if (newTime) {
          const { entry, timesheet, entryIndex, isLocal } =
            editingWorkEntryRef.current;
          const startDate = new Date(entry.startTime);
          const endDate = new Date(entry.endTime);
          let newStartTime = `${String(startDate.getHours()).padStart(2, "0")}:${String(startDate.getMinutes()).padStart(2, "0")}`;
          let newEndTime = `${String(endDate.getHours()).padStart(2, "0")}:${String(endDate.getMinutes()).padStart(2, "0")}`;

          if (dragEntryModeRef.current === "resize-start") {
            newStartTime = newTime;
            if (newStartTime >= newEndTime) {
              newStartTime = newEndTime;
            }
          } else if (dragEntryModeRef.current === "resize-end") {
            newEndTime = newTime;
            if (newEndTime <= newStartTime) {
              newEndTime = newStartTime;
            }
          }

          const [newStartHour, newStartMin] = newStartTime
            .split(":")
            .map(Number);
          const [newEndHour, newEndMin] = newEndTime.split(":").map(Number);
          const newStartDate = new Date(startDate);
          newStartDate.setHours(newStartHour, newStartMin, 0, 0);
          const newEndDate = new Date(endDate);
          newEndDate.setHours(newEndHour, newEndMin, 0, 0);

          // Update local state for visual feedback
          setEditingWorkEntry({
            ...editingWorkEntryRef.current,
            entry: {
              ...entry,
              startTime: newStartDate.toISOString(),
              endTime: newEndDate.toISOString(),
            },
          });
        }
      }
    };

    const handleGlobalMouseUp = async () => {
      if (!editingWorkEntryRef.current || !dragEntryModeRef.current) return;

      setIsDraggingEntry(false);
      setDragEntryMode(null);

      // Mark that we just finished a drag to prevent click handlers from firing
      justFinishedDragRef.current = true;
      setTimeout(() => {
        justFinishedDragRef.current = false;
      }, 300); // 300ms should be enough to prevent accidental clicks

      const { entry, timesheet, entryIndex, isLocal } =
        editingWorkEntryRef.current;

      // Calculate new times
      const startDate = new Date(entry.startTime);
      const endDate = new Date(entry.endTime);
      const [startHour, startMin] = [
        startDate.getHours(),
        startDate.getMinutes(),
      ];
      const [endHour, endMin] = [endDate.getHours(), endDate.getMinutes()];

      const totalMinutes = endHour * 60 + endMin - (startHour * 60 + startMin);
      const totalHours = totalMinutes / 60;

      // Validate minimum duration (15 minutes)
      if (totalMinutes < 15) {
        setMessage("Minimum entry duration is 15 minutes");
        setEditingWorkEntry(null);
        return;
      }

      // Handle local (unsaved) entries
      if (isLocal) {
        const [year, month, day] = selectedDate.split("-").map(Number);
        const startTimeDate = new Date(
          year,
          month - 1,
          day,
          startHour,
          startMin,
          0,
          0,
        );
        const endTimeDate = new Date(
          year,
          month - 1,
          day,
          endHour,
          endMin,
          0,
          0,
        );

        const updatedEntries = [...localWorkEntries];
        if (entryIndex >= 0 && entryIndex < updatedEntries.length) {
          updatedEntries[entryIndex] = {
            ...updatedEntries[entryIndex],
            startTime: startTimeDate.toISOString(),
            endTime: endTimeDate.toISOString(),
            startTimeLocal: `${String(startHour).padStart(2, "0")}:${String(startMin).padStart(2, "0")}`,
            endTimeLocal: `${String(endHour).padStart(2, "0")}:${String(endMin).padStart(2, "0")}`,
            workHours: totalHours,
            totalHours: totalHours,
          };

          setLocalWorkEntries(updatedEntries);

          console.log("✅ [LOCAL ENTRY DRAG] Updated local work entry:", {
            entryIndex,
            newStartTime: startTimeDate.toISOString(),
            newEndTime: endTimeDate.toISOString(),
            totalHours,
          });
        }
        setEditingWorkEntry(null);
        return;
      }

      // Store pending changes instead of auto-saving (saved entries)
      // User must click save button to apply changes
      setPendingEntryChanges({
        timesheetId: timesheet._id,
        entryIndex: entryIndex,
        startTime: entry.startTime,
        endTime: entry.endTime,
        hours: totalHours,
      });

      console.log("📝 [ENTRY DRAG] Stored pending changes (not saved yet):", {
        entryIndex,
        newStartTime: entry.startTime,
        newEndTime: entry.endTime,
        totalHours,
      });

      setEditingWorkEntry(null);
    };

    document.addEventListener("mousemove", handleGlobalMouseMove);
    document.addEventListener("mouseup", handleGlobalMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleGlobalMouseMove);
      document.removeEventListener("mouseup", handleGlobalMouseUp);
    };
  }, [isDraggingEntry]);

  // 🤖 AI Time Entries Pre-fill
  // Convert AI-generated time entries into TimesheetForm format
  useEffect(() => {
    if (aiTimeEntries && aiTimeEntries.length > 0 && selectedDate) {
      console.log(
        "[TIMESHEET FORM] 🕐 Processing AI time entries:",
        aiTimeEntries,
      );

      const convertedEntries = aiTimeEntries.map((aiEntry, index) => {
        // Parse time strings (e.g., "08:00", "12:00") and combine with selectedDate
        const [startHour, startMin] = aiEntry.startTime.split(":").map(Number);
        const [endHour, endMin] = aiEntry.endTime.split(":").map(Number);

        const startDate = new Date(selectedDate);
        startDate.setHours(startHour, startMin, 0, 0);

        const endDate = new Date(selectedDate);
        endDate.setHours(endHour, endMin, 0, 0);

        const entry: any = {
          _id: `ai-generated-${index}`,
          entryType: aiEntry.type === "break" ? "break" : "work",
          startTime: startDate.toISOString(),
          endTime: endDate.toISOString(),
          notes: aiEntry.description || "",
          equipment: [],
          materials: [],
        };

        // Add project/task for work entries
        if (aiEntry.type === "work" && preselectedProjectId) {
          entry.projectId = preselectedProjectId;
          if (preselectedTaskId) {
            entry.taskId = preselectedTaskId;
          }
        }

        return entry;
      });

      console.log(
        "[TIMESHEET FORM] ✅ Converted AI entries to form format:",
        convertedEntries,
      );
      setLocalWorkEntries(convertedEntries);
    }
  }, [aiTimeEntries, selectedDate, preselectedProjectId, preselectedTaskId]);

  const fetchProjects = async () => {
    // Check cache first (for offline support)
    const cached = offlineDataCache.get<any[]>(CACHE_KEYS.PROJECTS);
    if (cached) {
      console.log("[TimesheetForm] Using cached projects:", cached.length);
      setProjects(cached);
    }

    // If online, try to fetch fresh data
    if (isOnline) {
      try {
        const token = localStorage.getItem("token");
        const response = await fetch(`${apiUrl}/api/projects`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await response.json();
        if (data.success) {
          const projects = data.data.filter((p: any) => p.isActive);
          // Cache the fresh data
          offlineDataCache.set(CACHE_KEYS.PROJECTS, projects);
          setProjects(projects);

          // Pre-fetch and cache tasks for all projects (for offline support)
          console.log("[TimesheetForm] Pre-fetching tasks for all projects...");
          projects.forEach((project: any) => {
            if (project._id) {
              fetch(`${apiUrl}/api/tasks/project/${project._id}`, {
                headers: { Authorization: `Bearer ${token}` },
              })
                .then((res) => res.json())
                .then((taskData) => {
                  if (taskData.success) {
                    const tasks = taskData.data.filter((t: any) => t.isActive);
                    offlineDataCache.set(CACHE_KEYS.TASKS(project._id), tasks);
                    console.log(
                      `[TimesheetForm] Cached ${tasks.length} tasks for project ${project._id}`,
                    );
                  }
                })
                .catch((err) =>
                  console.warn(
                    `[TimesheetForm] Failed to pre-fetch tasks for project ${project._id}:`,
                    err,
                  ),
                );
            }
          });
        }
      } catch (error) {
        console.error("Failed to fetch projects:", error);
        // If fetch fails and we don't have cached data, keep empty array
        if (!cached) {
          setProjects([]);
        }
      }
    } else {
      // Offline and no cache - show empty array
      if (!cached) {
        console.warn(
          "[TimesheetForm] Offline and no cached projects available",
        );
        setProjects([]);
      }
    }
  };

  const fetchTasks = async (projectId: string) => {
    if (!projectId) {
      setTasks([]);
      return;
    }

    // Check cache first (for offline support)
    const cached = offlineDataCache.get<any[]>(CACHE_KEYS.TASKS(projectId));
    if (cached) {
      console.log(
        "[TimesheetForm] Using cached tasks for project:",
        projectId,
        cached.length,
      );
      setTasks(cached);
      // If we have cached data, still try to refresh if online (but don't wait)
      if (isOnline) {
        // Fetch in background to update cache
        fetch(`${apiUrl}/api/tasks/project/${projectId}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        })
          .then((res) => res.json())
          .then((data) => {
            if (data.success) {
              const tasks = data.data.filter((t: any) => t.isActive);
              offlineDataCache.set(CACHE_KEYS.TASKS(projectId), tasks);
              setTasks(tasks);
            }
          })
          .catch((err) => console.error("Background task fetch failed:", err));
      }
      return;
    }

    // If online, try to fetch fresh data
    if (isOnline) {
      try {
        const token = localStorage.getItem("token");
        const response = await fetch(
          `${apiUrl}/api/tasks/project/${projectId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        const data = await response.json();
        if (data.success) {
          const tasks = data.data.filter((t: any) => t.isActive);
          console.log(
            "[TimesheetForm] Fetched and caching tasks for project:",
            projectId,
            tasks.length,
          );
          // Cache the fresh data
          offlineDataCache.set(CACHE_KEYS.TASKS(projectId), tasks);
          setTasks(tasks);
        } else {
          console.warn(
            "[TimesheetForm] Task fetch returned unsuccessful:",
            data,
          );
          setTasks([]);
        }
      } catch (error) {
        console.error("Failed to fetch tasks:", error);
        // If fetch fails and we don't have cached data, set empty array
        setTasks([]);
      }
    } else {
      // Offline and no cache - show empty array
      console.warn(
        "[TimesheetForm] Offline and no cached tasks available for project:",
        projectId,
      );
      setTasks([]);
    }
  };

  const fetchEquipment = async () => {
    // Check cache first (for offline support)
    const cached = offlineDataCache.get<any[]>(CACHE_KEYS.EQUIPMENT);
    if (cached) {
      console.log("[TimesheetForm] Using cached equipment:", cached.length);
      setEquipment(cached);
    }

    // If online, try to fetch fresh data
    if (isOnline) {
      try {
        const token = localStorage.getItem("token");
        const response = await fetch(`${apiUrl}/api/equipment`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await response.json();
        if (data.success) {
          const equipment = data.data || [];
          // Cache the fresh data
          offlineDataCache.set(CACHE_KEYS.EQUIPMENT, equipment);
          setEquipment(equipment);
        }
      } catch (error) {
        console.error("Failed to fetch equipment:", error);
        // If fetch fails and we don't have cached data, set empty array
        if (!cached) {
          setEquipment([]);
        }
      }
    } else {
      // Offline and no cache - show empty array
      if (!cached) {
        setEquipment([]);
      }
    }
  };

  const fetchMaterials = async () => {
    // Check cache first (for offline support)
    const cached = offlineDataCache.get<any[]>(CACHE_KEYS.MATERIALS);
    if (cached) {
      console.log("[TimesheetForm] Using cached materials:", cached.length);
      setMaterials(cached);
    }

    // If online, try to fetch fresh data
    if (isOnline) {
      try {
        const token = localStorage.getItem("token");
        const response = await fetch(`${apiUrl}/api/materials`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await response.json();
        if (data.success) {
          const materials = data.data || [];
          // Cache the fresh data
          offlineDataCache.set(CACHE_KEYS.MATERIALS, materials);
          setMaterials(materials);
        }
      } catch (error) {
        console.error("Failed to fetch materials:", error);
        // If fetch fails and we don't have cached data, set empty array
        if (!cached) {
          setMaterials([]);
        }
      }
    } else {
      // Offline and no cache - show empty array
      if (!cached) {
        setMaterials([]);
      }
    }
  };

  const fetchGeneralWorkItems = async () => {
    // Check cache first (for offline support)
    const cached = offlineDataCache.get<any[]>(CACHE_KEYS.GENERAL_WORK_ITEMS);
    if (cached) {
      console.log(
        "[TimesheetForm] Using cached general work items:",
        cached.length,
      );
      setGeneralWorkItems(cached);
    }

    // If online, try to fetch fresh data
    if (isOnline) {
      try {
        const token = localStorage.getItem("token");
        const response = await fetch(`${apiUrl}/api/general-work-items`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await response.json();
        if (data.success) {
          const items = data.data.filter((item: any) => item.isActive);
          // Cache the fresh data
          offlineDataCache.set(CACHE_KEYS.GENERAL_WORK_ITEMS, items);
          setGeneralWorkItems(items);
        }
      } catch (error) {
        console.error("Failed to fetch general work items:", error);
        // If fetch fails and we don't have cached data, set empty array
        if (!cached) {
          setGeneralWorkItems([]);
        }
      }
    } else {
      // Offline and no cache - show empty array
      if (!cached) {
        setGeneralWorkItems([]);
      }
    }
  };

  const fetchStaffGroups = async () => {
    // Check cache first (for offline support)
    const cached = offlineDataCache.get<any[]>(CACHE_KEYS.STAFF_GROUPS);
    if (cached) {
      console.log("[TimesheetForm] Using cached staff groups:", cached.length);
      setStaffGroups(cached);
    }

    // If online, try to fetch fresh data
    if (isOnline) {
      try {
        const token = localStorage.getItem("token");
        const response = await fetch(`${apiUrl}/api/staff-groups`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await response.json();
        if (data.success) {
          const groups = data.data || [];
          // Cache the fresh data
          offlineDataCache.set(CACHE_KEYS.STAFF_GROUPS, groups);
          setStaffGroups(groups);
        }
      } catch (error) {
        console.error("Failed to fetch staff groups:", error);
        // If fetch fails and we don't have cached data, keep empty array
        if (!cached) {
          setStaffGroups([]);
        }
      }
    } else {
      // Offline and no cache - show empty array
      if (!cached) {
        console.warn(
          "[TimesheetForm] Offline and no cached staff groups available",
        );
        setStaffGroups([]);
      }
    }
  };

  const fetchDateTimesheets = async (dateStr: string) => {
    // In creation mode we need ALL timesheets for the date to know which staff to disable.
    // Only use staff filter when editing (no onStaffChange).
    const isCreationMode = !!onStaffChange;
    const staffKey = isCreationMode
      ? "all"
      : selectedStaff && selectedStaff.length > 0
        ? selectedStaff.sort().join(",")
        : "all";
    const cacheKey = CACHE_KEYS.TIMESHEETS_BY_STAFF(
      dateStr,
      staffKey.split(",").filter(Boolean),
    );

    // Check cache first (for offline support)
    const cached = offlineDataCache.get<any[]>(cacheKey);
    if (cached) {
      console.log(
        "[TimesheetForm] Using cached timesheets for date:",
        dateStr,
        cached.length,
      );
      setDateTimesheets(cached);
    }

    // If online, try to fetch fresh data
    if (isOnline) {
      try {
        const token = localStorage.getItem("token");

        // Build query params
        let url = `${apiUrl}/api/timesheets?date=${dateStr}`;

        // Only filter by staff when editing. When creating, fetch all so we can disable staff who already have timesheets.
        if (!isCreationMode && selectedStaff && selectedStaff.length > 0) {
          selectedStaff.forEach((staffId) => {
            url += `&staffId=${staffId}`;
          });
        }

        console.log("📥 [FETCH] Fetching timesheets, URL:", url);
        console.log(
          "📥 [FETCH] Date:",
          dateStr,
          "Selected staff:",
          selectedStaff,
        );

        const response = await fetch(url, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await response.json();

        if (data.success) {
          // Handle different response structures - ensure we always get an array
          let timesheets: any[] = [];
          if (Array.isArray(data.data)) {
            timesheets = data.data;
          } else if (data.data && Array.isArray(data.data.timesheets)) {
            timesheets = data.data.timesheets;
          } else if (data.data && Array.isArray(data.data.data)) {
            timesheets = data.data.data;
          }

          // Final safety check - ensure it's always an array
          if (!Array.isArray(timesheets)) {
            timesheets = [];
          }

          // Cache the fresh data
          offlineDataCache.set(cacheKey, timesheets);

          console.log("✅ [FETCH] Fetched timesheets for date:", dateStr);
          console.log("✅ [FETCH] Timesheets count:", timesheets.length);
          setDateTimesheets(timesheets);
        } else {
          console.error("❌ [FETCH] Failed to fetch timesheets:", data);
          if (!cached) {
            setDateTimesheets([]);
          }
        }
      } catch (error) {
        console.error("❌ [FETCH] Error fetching date timesheets:", error);
        if (!cached) {
          setDateTimesheets([]);
        }
      }
    } else {
      // Offline and no cache - show empty array
      if (!cached) {
        console.warn(
          "[TimesheetForm] Offline and no cached timesheets available for date:",
          dateStr,
        );
        setDateTimesheets([]);
      }
    }
  };

  const fetchPayPeriod = async (offset: number = 0) => {
    // Helper to create local date string (YYYY-MM-DD) without timezone conversion
    const getLocalDateString = (date: Date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    // Check cache first (for offline support)
    const cachedPayPeriod = offlineDataCache.get<any>(
      CACHE_KEYS.PAY_PERIOD(offset),
    );
    if (cachedPayPeriod) {
      console.log(
        "[TimesheetForm] Using cached pay period for offset:",
        offset,
      );

      // Log cached period details for debugging
      const cachedDays = cachedPayPeriod.days || [];
      const actualToday = getLocalDateString(new Date());

      // Convert to PayPeriodDay format for comparison
      const restoredDays: PayPeriodDay[] = cachedDays.map((day: any) => ({
        ...day,
        date: new Date(day.date),
      }));
      const cachedToday = restoredDays.find((d) => d.isToday);

      console.log(
        "📅 [PAY PERIOD DEBUG] ============================================",
      );
      console.log("📅 [PAY PERIOD DEBUG] Using CACHED pay period data");
      console.log("📅 [PAY PERIOD DEBUG] Offset:", offset);
      console.log(
        "📅 [PAY PERIOD DEBUG] Actual Today (local):",
        actualToday,
        formatDateUtil(new Date()),
      );
      console.log(
        "📅 [PAY PERIOD DEBUG] Cached period has",
        restoredDays.length,
        "days",
      );
      if (restoredDays.length > 0) {
        console.log(
          "📅 [PAY PERIOD DEBUG] Cached period range:",
          restoredDays[0].dateString,
          "to",
          restoredDays[restoredDays.length - 1].dateString,
        );
        console.log(
          "📅 [PAY PERIOD DEBUG] All cached days:",
          restoredDays
            .map((d) => `${d.dateString}${d.isToday ? " ⭐(TODAY)⭐" : ""}`)
            .join(", "),
        );
      }
      if (cachedToday) {
        console.log(
          '📅 [PAY PERIOD DEBUG] ⚠️ Cached "Today" is:',
          cachedToday.dateString,
          "(should be",
          actualToday + ")",
        );
        if (cachedToday.dateString !== actualToday) {
          console.log(
            '📅 [PAY PERIOD DEBUG] ❌ MISMATCH! Cache has wrong "Today" date - cache will be invalidated',
          );
          // Invalidate cache if "Today" is wrong
          localStorage.removeItem(
            `3pm-offline-data-${CACHE_KEYS.PAY_PERIOD(offset)}`,
          );
          // Continue to recalculate
        } else {
          console.log(
            '📅 [PAY PERIOD DEBUG] ✅ Cache "Today" matches actual today - using cache',
          );
          setPayPeriodDays(restoredDays);
          setWorkHourStart(cachedPayPeriod.workHourStart);
          setWorkHourEnd(cachedPayPeriod.workHourEnd);
          return;
        }
      } else {
        console.log(
          '📅 [PAY PERIOD DEBUG] ⚠️ No day marked as "Today" in cached period - recalculating',
        );
        // Invalidate cache and recalculate
        localStorage.removeItem(
          `3pm-offline-data-${CACHE_KEYS.PAY_PERIOD(offset)}`,
        );
      }
      console.log(
        "📅 [PAY PERIOD DEBUG] ============================================",
      );
      // Cache invalid or missing "Today", continue to recalculate below
    }

    try {
      const token = localStorage.getItem("token");
      let accountId = null;
      let accountSettings = null;

      // First try to get from localStorage
      const userStr = localStorage.getItem("user");
      if (userStr) {
        try {
          const user = JSON.parse(userStr);
          accountId =
            user?.account?._id ||
            user?.account ||
            user?.currentAccountId ||
            user?.accountId;
        } catch (e) {
          console.error("Failed to parse user from localStorage:", e);
        }
      }

      // Check cache for account settings
      const cachedSettings = offlineDataCache.get<any>(
        CACHE_KEYS.ACCOUNT_SETTINGS,
      );
      if (cachedSettings && cachedSettings.accountId === accountId) {
        accountSettings = cachedSettings;
        console.log("[TimesheetForm] Using cached account settings");
      }

      // If online and no cached settings, fetch account settings
      if (isOnline && !accountSettings) {
        // If not in localStorage, fetch current user from API
        if (!accountId) {
          try {
            const userRes = await fetch(`${apiUrl}/api/auth/me`, {
              headers: { Authorization: `Bearer ${token}` },
            });

            if (userRes.ok) {
              const userData = await userRes.json();
              const user = userData.user || userData.data;
              accountId =
                user?.account?._id ||
                user?.account ||
                user?.currentAccountId ||
                user?.accountId;

              // Save to localStorage for future use
              if (user) {
                localStorage.setItem("user", JSON.stringify(user));
              }
            }
          } catch (e) {
            console.error("Failed to fetch current user:", e);
          }
        }

        if (accountId) {
          try {
            const accountRes = await fetch(
              `${apiUrl}/api/accounts/${accountId}`,
              {
                headers: { Authorization: `Bearer ${token}` },
              },
            );

            if (accountRes.ok) {
              const accountData = await accountRes.json();
              accountSettings = {
                accountId,
                ...accountData.account,
              };
              // Cache account settings
              offlineDataCache.set(
                CACHE_KEYS.ACCOUNT_SETTINGS,
                accountSettings,
              );
            }
          } catch (e) {
            console.error("Failed to fetch account settings:", e);
          }
        }
      }

      // Use cached settings if available, otherwise use defaults
      if (!accountSettings && cachedSettings) {
        accountSettings = cachedSettings;
      }

      if (!accountSettings) {
        console.warn(
          "[TimesheetForm] No account settings available, using defaults",
        );
        // Use defaults for offline
        accountSettings = {
          accountId: accountId || "unknown",
          payrollSettings: {
            defaultPayFrequency: "weekly",
            payPeriodEndDay: 0,
            defaultStartTime: "08:00",
            defaultEndTime: "18:00",
          },
        };
      }

      // Get pay period settings
      const settings = accountSettings?.payrollSettings || {};
      const frequency = settings.defaultPayFrequency || "weekly";
      const payPeriodEndDay = settings.payPeriodEndDay ?? 0;

      // Set work hours
      const workStart = settings.defaultStartTime || "08:00";
      const workEnd = settings.defaultEndTime || "18:00";
      setWorkHourStart(workStart);
      setWorkHourEnd(workEnd);

      // Calculate current pay period that contains today
      // This matches the backend calculatePayPeriod logic exactly
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todayStr = getLocalDateString(today);
      const daysInPeriod =
        frequency === "weekly" ? 7 : frequency === "fortnightly" ? 14 : 30;

      console.log(
        "📅 [PAY PERIOD DEBUG] ============================================",
      );
      console.log("📅 [PAY PERIOD DEBUG] Offset:", offset);
      console.log(
        "📅 [PAY PERIOD DEBUG] Actual Today (local):",
        todayStr,
        formatDateUtil(today),
      );
      console.log(
        "📅 [PAY PERIOD DEBUG] Pay Period End Day:",
        payPeriodEndDay,
        "(0=Sunday, 1=Monday, etc.)",
      );
      console.log(
        "📅 [PAY PERIOD DEBUG] Frequency:",
        frequency,
        "Days in period:",
        daysInPeriod,
      );

      // Find the pay period end day that contains today
      const todayDayOfWeek = today.getDay();
      let daysUntilEndDay = (payPeriodEndDay - todayDayOfWeek + 7) % 7;
      console.log(
        "📅 [PAY PERIOD DEBUG] Today day of week:",
        todayDayOfWeek,
        "(0=Sunday, 1=Monday, etc.)",
      );
      console.log(
        "📅 [PAY PERIOD DEBUG] Initial daysUntilEndDay:",
        daysUntilEndDay,
      );

      // Handle edge case: if daysUntilEndDay is 0, check if today actually IS the end day
      // If not, it means we need to go to next week's end day
      if (daysUntilEndDay === 0 && todayDayOfWeek === payPeriodEndDay) {
        daysUntilEndDay = 0; // Today IS the end day
        console.log("📅 [PAY PERIOD DEBUG] Today IS the end day, using today");
      } else if (daysUntilEndDay === 0) {
        daysUntilEndDay = 7; // Next week's end day
        console.log(
          "📅 [PAY PERIOD DEBUG] daysUntilEndDay was 0 but today is not end day, using next week (7 days)",
        );
      }
      console.log(
        "📅 [PAY PERIOD DEBUG] Final daysUntilEndDay:",
        daysUntilEndDay,
      );

      // Calculate period end (the end day of the period containing today)
      let periodEnd = new Date(today);
      periodEnd.setDate(today.getDate() + daysUntilEndDay);
      periodEnd.setHours(23, 59, 59, 999);

      // Calculate period start (daysInPeriod days before the end, inclusive)
      let periodStart = new Date(periodEnd);
      periodStart.setDate(periodEnd.getDate() - daysInPeriod + 1);
      periodStart.setHours(0, 0, 0, 0);

      console.log("📅 [PAY PERIOD DEBUG] Period BEFORE offset:");
      console.log(
        "📅 [PAY PERIOD DEBUG]   Start:",
        getLocalDateString(periodStart),
        formatDateUtil(periodStart),
      );
      console.log(
        "📅 [PAY PERIOD DEBUG]   End:",
        getLocalDateString(periodEnd),
        formatDateUtil(periodEnd),
      );

      // Apply offset for navigation (previous/next periods)
      if (offset !== 0) {
        const offsetDays = offset * daysInPeriod;
        const adjustedPeriodStart = new Date(periodStart);
        const adjustedPeriodEnd = new Date(periodEnd);
        adjustedPeriodStart.setDate(periodStart.getDate() + offsetDays);
        adjustedPeriodEnd.setDate(periodEnd.getDate() + offsetDays);

        console.log(
          "📅 [PAY PERIOD DEBUG] Applying offset:",
          offset,
          "periods =",
          offsetDays,
          "days",
        );

        // Replace the original dates with adjusted ones
        periodStart = adjustedPeriodStart;
        periodEnd = adjustedPeriodEnd;
      }

      console.log("📅 [PAY PERIOD DEBUG] Period AFTER offset:");
      console.log(
        "📅 [PAY PERIOD DEBUG]   Start:",
        getLocalDateString(periodStart),
        formatDateUtil(periodStart),
      );
      console.log(
        "📅 [PAY PERIOD DEBUG]   End:",
        getLocalDateString(periodEnd),
        formatDateUtil(periodEnd),
      );

      // Fetch timesheets for the period (check cache first)
      const dateFrom = periodStart.toISOString().split("T")[0];
      const dateTo = periodEnd.toISOString().split("T")[0];
      const periodCacheKey = `timesheets-period-${dateFrom}-${dateTo}`;

      let existingTimesheets = new Set<string>();
      const cachedPeriodTimesheets =
        offlineDataCache.get<any[]>(periodCacheKey);

      if (cachedPeriodTimesheets) {
        console.log(
          "[TimesheetForm] Using cached timesheets for period:",
          dateFrom,
          "to",
          dateTo,
        );
        existingTimesheets = new Set(
          cachedPeriodTimesheets.map((ts: any) => {
            const date = new Date(ts.date);
            return getLocalDateString(date);
          }),
        );
      }

      // If online, try to fetch fresh timesheets for the period
      if (isOnline) {
        try {
          const params = new URLSearchParams({
            dateFrom,
            dateTo,
          });

          const timesheetsRes = await fetch(
            `${apiUrl}/api/timesheets?${params}`,
            {
              headers: { Authorization: `Bearer ${token}` },
            },
          );

          if (timesheetsRes.ok) {
            const timesheetsData = await timesheetsRes.json();
            const timesheets = timesheetsData.data?.timesheets || [];

            // Cache the timesheets
            offlineDataCache.set(periodCacheKey, timesheets);

            existingTimesheets = new Set(
              timesheets.map((ts: any) => {
                const date = new Date(ts.date);
                return getLocalDateString(date);
              }),
            );
          }
        } catch (error) {
          console.error("Failed to fetch timesheets for period:", error);
          // Continue with cached data if available
        }
      }

      // Generate days array
      const days: PayPeriodDay[] = [];
      const currentDay = new Date(periodStart);
      const todayStrForComparison = getLocalDateString(today);

      console.log("📅 [PAY PERIOD DEBUG] Generating days array...");
      console.log(
        "📅 [PAY PERIOD DEBUG] Today string for comparison:",
        todayStrForComparison,
      );

      while (currentDay <= periodEnd) {
        const dateString = getLocalDateString(currentDay);
        const isToday = dateString === todayStrForComparison;

        if (isToday) {
          console.log(
            "📅 [PAY PERIOD DEBUG] ✅ Marking as TODAY:",
            dateString,
            currentDay.toLocaleDateString("en-US", {
              weekday: "long",
              year: "numeric",
              month: "long",
              day: "numeric",
            }),
          );
        }

        days.push({
          date: new Date(currentDay),
          dateString,
          hasTimesheet: existingTimesheets.has(dateString),
          isToday,
        });
        currentDay.setDate(currentDay.getDate() + 1);
      }

      console.log("📅 [PAY PERIOD DEBUG] Generated", days.length, "days");
      console.log(
        "📅 [PAY PERIOD DEBUG] Days marked as Today:",
        days
          .filter((d) => d.isToday)
          .map((d) => `${d.dateString} (${formatDateUtil(d.date)})`),
      );
      console.log(
        "📅 [PAY PERIOD DEBUG] ============================================",
      );

      setPayPeriodDays(days);

      // Cache the pay period data (convert Date objects to ISO strings for storage)
      const daysForCache = days.map((day) => ({
        ...day,
        date: day.date.toISOString(), // Convert Date to string for storage
      }));
      offlineDataCache.set(CACHE_KEYS.PAY_PERIOD(offset), {
        days: daysForCache,
        workHourStart: workStart,
        workHourEnd: workEnd,
        frequency,
        payPeriodEndDay,
      });

      console.log(
        "📅 [PAY PERIOD DEBUG] Cached pay period for offset:",
        offset,
      );
      console.log(
        "📅 [PAY PERIOD DEBUG] Cache key:",
        CACHE_KEYS.PAY_PERIOD(offset),
      );
    } catch (error) {
      console.error("Failed to fetch pay period:", error);
      // If offline and we have cached data, use it
      if (!isOnline && cachedPayPeriod) {
        // Convert date strings back to Date objects
        const restoredDays: PayPeriodDay[] = cachedPayPeriod.days.map(
          (day: any) => ({
            ...day,
            date: new Date(day.date),
          }),
        );
        setPayPeriodDays(restoredDays);
        setWorkHourStart(cachedPayPeriod.workHourStart);
        setWorkHourEnd(cachedPayPeriod.workHourEnd);
      }
    }
  };

  // Generate time blocks (in 15-minute increments)
  const generateTimeBlocks = () => {
    const blocks: string[] = [];

    // Generate all 24 hours in 15-minute increments (00:00 to 23:45)
    for (let hour = 0; hour < 24; hour++) {
      blocks.push(`${String(hour).padStart(2, "0")}:00`);
      blocks.push(`${String(hour).padStart(2, "0")}:15`);
      blocks.push(`${String(hour).padStart(2, "0")}:30`);
      blocks.push(`${String(hour).padStart(2, "0")}:45`);
    }

    return blocks;
  };

  const timeBlocks = generateTimeBlocks();
  const timelineRef = useRef<HTMLDivElement>(null);
  const labelsContainerRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to default start time when form is shown
  useEffect(() => {
    if (
      !showDetails &&
      workHourStart &&
      timelineRef.current &&
      labelsContainerRef.current
    ) {
      // Use setTimeout to ensure DOM is rendered
      setTimeout(() => {
        if (
          timelineRef.current &&
          labelsContainerRef.current &&
          workHourStart
        ) {
          const [startHour, startMin] = workHourStart.split(":").map(Number);
          // Calculate scroll position - each hour = 30px (720px total / 24 hours)
          // Each 15-min block = 7.5px (30px / 4 blocks per hour)
          const scrollPosition = startHour * 30 + (startMin / 15) * 7.5 - 30; // Offset to show 1 hour before
          timelineRef.current.scrollTop = scrollPosition;
          labelsContainerRef.current.scrollTop = scrollPosition;
        }
      }, 100);
    }
  }, [showDetails, workHourStart]);

  // Scroll to 8am when entering fullscreen mode
  useEffect(() => {
    if (isFullscreen && timelineRef.current && labelsContainerRef.current) {
      setTimeout(() => {
        if (timelineRef.current && labelsContainerRef.current) {
          // Scroll to 8am (8 * 30px = 240px from top)
          const scrollPosition = 8 * 30;
          timelineRef.current.scrollTop = scrollPosition;
          labelsContainerRef.current.scrollTop = scrollPosition;
        }
      }, 100);
    }
  }, [isFullscreen]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (tapTimeoutRef.current) {
        clearTimeout(tapTimeoutRef.current);
      }
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current);
      }
    };
  }, []);

  const formatTimeDisplay = (time: string) => {
    const [hour, min] = time.split(":").map(Number);
    const period = hour >= 12 ? "PM" : "AM";
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${displayHour}:${String(min).padStart(2, "0")}${period}`;
  };

  // When editing a timesheet, use the timesheet's staff so the timeline and add-entry work without a visible staff selector
  const effectiveStaff = useMemo(() => {
    if (editingTimesheet && dateTimesheets.length > 0) {
      const ts = dateTimesheets.find(
        (t: any) => t._id === editingTimesheet._id,
      );
      if (ts) {
        const id = ts.staffId?._id ?? ts.staffId;
        return id != null ? [String(id)] : [];
      }
    }
    return selectedStaff || [];
  }, [editingTimesheet, dateTimesheets, selectedStaff]);

  // Approved timesheets are read-only: no add/edit/save
  const isApprovedReadOnly = useMemo(() => {
    if (!editingTimesheet || dateTimesheets.length === 0) return false;
    const ts = dateTimesheets.find(
      (t: any) => t._id === editingTimesheet._id,
    );
    return ts ? ts.status === "approved" : false;
  }, [editingTimesheet, dateTimesheets]);

  const isTimeInRange = (time: string) => {
    if (!selectedStartTime || !selectedEndTime) return false;
    return time >= selectedStartTime && time < selectedEndTime;
  };

  const handleMouseDown = (time: string, e?: React.MouseEvent) => {
    lastMouseDownWasScrollbarRef.current = false;
    if (isApprovedReadOnly) return;
    // Don't allow selection if no staff is selected (use effectiveStaff so editing works)
    if (!effectiveStaff || effectiveStaff.length === 0) {
      return;
    }

    // Ignore clicks on the scrollbar - they would wrongly create a work entry
    if (e) {
      const el = timelineRef.current;
      if (el) {
        const rect = el.getBoundingClientRect();
        const clickX = e.clientX - rect.left;
        const scrollbarWidth = el.offsetWidth - el.clientWidth;
        // Classic scrollbar: occupies space; overlay scrollbar: ~25px on right
        const scrollbarZone = 25;
        const isScrollbarClick =
          scrollbarWidth > 0
            ? clickX >= el.clientWidth
            : clickX >= rect.width - scrollbarZone;
        if (isScrollbarClick) {
          lastMouseDownWasScrollbarRef.current = true;
          return;
        }
      }
    }

    setIsDragging(true);
    setDragStartTime(time);
    setSelectedStartTime(time);
    // Set initial end time to +15 minutes (end of the clicked interval)
    const [hour, min] = time.split(":").map(Number);
    const totalMinutes = hour * 60 + min + 15;
    const endHour = Math.floor(totalMinutes / 60);
    const endMin = totalMinutes % 60;
    const initialEndTime = `${String(endHour).padStart(2, "0")}:${String(endMin).padStart(2, "0")}`;
    setSelectedEndTime(initialEndTime);
  };

  const handleMouseEnter = (time: string, e?: React.MouseEvent) => {
    if (isApprovedReadOnly) return;
    // Don't allow if no staff is selected (use effectiveStaff so editing works)
    if (!effectiveStaff || effectiveStaff.length === 0) {
      return;
    }

    if (isDragging && dragStartTime) {
      // Calculate end time as the end of the 15-minute interval (add 15 minutes)
      const [hour, min] = time.split(":").map(Number);
      const totalMinutes = hour * 60 + min + 15; // Add 15 minutes to get end of interval
      const endHour = Math.floor(totalMinutes / 60);
      const endMin = totalMinutes % 60;
      const adjustedEndTime = `${String(endHour).padStart(2, "0")}:${String(endMin).padStart(2, "0")}`;

      // Update selection range as we drag
      if (time >= dragStartTime) {
        setSelectedStartTime(dragStartTime);
        setSelectedEndTime(adjustedEndTime);
      } else {
        const [dragHour, dragMin] = dragStartTime.split(":").map(Number);
        const dragTotalMinutes = dragHour * 60 + dragMin + 15;
        const dragEndHour = Math.floor(dragTotalMinutes / 60);
        const dragEndMin = dragTotalMinutes % 60;
        const adjustedDragEndTime = `${String(dragEndHour).padStart(2, "0")}:${String(dragEndMin).padStart(2, "0")}`;

        setSelectedStartTime(time);
        setSelectedEndTime(adjustedDragEndTime);
      }
    }
  };

  // Mousedown on the timeline container (e.g. scrollbar) - time blocks don't receive it when scrollbar is clicked
  const handleTimelineMouseDown = (e: React.MouseEvent) => {
    // Ignore if mousedown was on a time block - that's a valid selection
    if ((e.target as HTMLElement).closest?.("[data-time]")) return;
    const el = timelineRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const scrollbarWidth = el.offsetWidth - el.clientWidth;
    const scrollbarZone = 25;
    const isScrollbarClick =
      scrollbarWidth > 0
        ? clickX >= el.clientWidth
        : clickX >= rect.width - scrollbarZone;
    if (isScrollbarClick) {
      lastMouseDownWasScrollbarRef.current = true;
      setSelectedStartTime("");
      setSelectedEndTime("");
    }
  };

  const handleMouseUp = (e?: React.MouseEvent | MouseEvent) => {
    const wasScrollbarClick = lastMouseDownWasScrollbarRef.current;
    lastMouseDownWasScrollbarRef.current = false;
    setIsDragging(false);

    // Don't open details if the click was on the scrollbar
    if (wasScrollbarClick) {
      setSelectedStartTime("");
      setSelectedEndTime("");
      return;
    }

    // Don't process if clicking on a button or interactive element
    if (e) {
      const target = e.target as HTMLElement;
      // Check if click was on a button, or inside a button, or on an entry's interactive elements
      if (
        target.tagName === "BUTTON" ||
        target.closest("button") ||
        target.closest("[data-entry-button]") ||
        target.closest(".pointer-events-auto")
      ) {
        return;
      }
    }

    if (isApprovedReadOnly) return;
    // Don't process if no staff is selected (use effectiveStaff so editing works)
    if (!effectiveStaff || effectiveStaff.length === 0) {
      setSelectedStartTime("");
      setSelectedEndTime("");
      return;
    }

    // Don't open details if we just finished dragging an entry resize handle
    // Also check if editingWorkEntry exists (entry drag in progress or just finished)
    if (justFinishedDragRef.current || isDraggingEntry || editingWorkEntry) {
      return;
    }

    // If a time range was selected, validate and open details page
    if (selectedStartTime && selectedEndTime) {
      // Ensure start time is before end time (swap if needed)
      const [startHour, startMin] = selectedStartTime.split(":").map(Number);
      const [endHour, endMin] = selectedEndTime.split(":").map(Number);
      const startMinutes = startHour * 60 + startMin;
      const endMinutes = endHour * 60 + endMin;

      // Determine correct start and end times
      let finalStartTime = selectedStartTime;
      let finalEndTime = selectedEndTime;

      if (startMinutes >= endMinutes) {
        // Swap times so start is always before end
        finalStartTime = selectedEndTime;
        finalEndTime = selectedStartTime;
        setSelectedStartTime(finalStartTime);
        setSelectedEndTime(finalEndTime);
      }

      // Check if this is a single click (no drag) - create a default 30-minute entry
      const duration = Math.abs(endMinutes - startMinutes);
      if (duration === 15) {
        // Single click (just clicked one 15-min interval) - create a 30-minute entry from the clicked time
        const endTimeMinutes = startMinutes + 30;
        const endHour = Math.floor(endTimeMinutes / 60);
        const endMin = endTimeMinutes % 60;
        finalEndTime = `${String(endHour).padStart(2, "0")}:${String(endMin).padStart(2, "0")}`;
        setSelectedEndTime(finalEndTime);
      } else if (duration < 15) {
        // Dragged but too short
        setMessage("Minimum entry duration is 15 minutes");
        setSelectedStartTime("");
        setSelectedEndTime("");
        return;
      }

      // Open details page - times are now correct
      setShowDetails(true);
    }
  };

  // Long-press to drag pattern for mobile
  const handleTouchStart = (time: string, e?: React.TouchEvent) => {
    if (!e || e.touches.length === 0) return;
    if (isApprovedReadOnly) return;
    // Don't allow selection if no staff is selected (use effectiveStaff so editing works)
    if (!effectiveStaff || effectiveStaff.length === 0) {
      return;
    }

    const touch = e.touches[0];
    // Ignore touches on the scrollbar (same logic as handleMouseDown)
    const el = timelineRef.current;
    if (el) {
      const rect = el.getBoundingClientRect();
      const touchX = touch.clientX - rect.left;
      const scrollbarWidth = el.offsetWidth - el.clientWidth;
      const scrollbarZone = 25;
      const isScrollbarTouch =
        scrollbarWidth > 0
          ? touchX >= el.clientWidth
          : touchX >= rect.width - scrollbarZone;
      if (isScrollbarTouch) {
        lastMouseDownWasScrollbarRef.current = true;
        return;
      }
    }
    touchStartPositionRef.current = { x: touch.clientX, y: touch.clientY };

    // If already in tap selection mode, this is the second tap for end time
    if (tapSelectionMode && selectedStartTime && !isDragMode) {
      // Clear any pending long-press timer
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current);
        longPressTimerRef.current = null;
      }

      // Calculate end time as the end of the tapped interval (+15 minutes)
      const [hour, min] = time.split(":").map(Number);
      const totalMinutes = hour * 60 + min + 15;
      const endHour = Math.floor(totalMinutes / 60);
      const endMin = totalMinutes % 60;
      const endTime = `${String(endHour).padStart(2, "0")}:${String(endMin).padStart(2, "0")}`;

      // Ensure end time is after start time
      const [startHour, startMin] = selectedStartTime.split(":").map(Number);
      const startMinutes = startHour * 60 + startMin;
      const endMinutes = hour * 60 + min;

      if (endMinutes > startMinutes) {
        setSelectedEndTime(endTime);
        setTapSelectionMode(false);
        if (tapTimeoutRef.current) {
          clearTimeout(tapTimeoutRef.current);
        }
        // Open details
        setTimeout(() => {
          setShowDetails(true);
        }, 100);
      } else {
        // If tapping earlier time, treat as new start selection
        setSelectedStartTime(time);
        const newEndTime = `${String(endHour).padStart(2, "0")}:${String(endMin).padStart(2, "0")}`;
        setSelectedEndTime(newEndTime);
      }
      return;
    }

    // Start long-press timer (300ms) to enter drag mode
    longPressTimerRef.current = setTimeout(() => {
      // Long press detected - enter drag mode
      setIsDragMode(true);
      setIsDragging(true);
      setDragStartTime(time);
      setSelectedStartTime(time);
      setTapSelectionMode(false); // Cancel tap mode

      // Calculate initial end time
      const [hour, min] = time.split(":").map(Number);
      const totalMinutes = hour * 60 + min + 15;
      const endHour = Math.floor(totalMinutes / 60);
      const endMin = totalMinutes % 60;
      const initialEndTime = `${String(endHour).padStart(2, "0")}:${String(endMin).padStart(2, "0")}`;
      setSelectedEndTime(initialEndTime);

      // Vibrate feedback if available (optional)
      if (navigator.vibrate) {
        navigator.vibrate(50);
      }
    }, 300); // 300ms hold = drag mode
  };

  // Handle touch move - cancel long press if moving too much, or update selection if in drag mode
  const handleTouchMove = (e: React.TouchEvent) => {
    if (!touchStartPositionRef.current || e.touches.length === 0) return;

    const touch = e.touches[0];
    const deltaX = Math.abs(touch.clientX - touchStartPositionRef.current.x);
    const deltaY = Math.abs(touch.clientY - touchStartPositionRef.current.y);
    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

    // If moved more than 10px before long-press completes, cancel it (user is scrolling)
    if (distance > 10 && longPressTimerRef.current && !isDragMode) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
      return;
    }

    // If in drag mode, update the selection
    // Note: touchAction: 'none' on container prevents scrolling, no preventDefault needed
    if (isDragMode && isDragging && dragStartTime) {
      const element = document.elementFromPoint(touch.clientX, touch.clientY);
      const timeBlock = element?.closest("[data-time]");

      if (timeBlock) {
        const time = timeBlock.getAttribute("data-time");
        if (time) {
          // Calculate end time as the end of the 15-minute interval
          const [hour, min] = time.split(":").map(Number);
          const totalMinutes = hour * 60 + min + 15;
          const endHour = Math.floor(totalMinutes / 60);
          const endMin = totalMinutes % 60;
          const adjustedEndTime = `${String(endHour).padStart(2, "0")}:${String(endMin).padStart(2, "0")}`;

          if (time >= dragStartTime) {
            setSelectedStartTime(dragStartTime);
            setSelectedEndTime(adjustedEndTime);
          } else {
            const [dragHour, dragMin] = dragStartTime.split(":").map(Number);
            const dragTotalMinutes = dragHour * 60 + dragMin + 15;
            const dragEndHour = Math.floor(dragTotalMinutes / 60);
            const dragEndMin = dragTotalMinutes % 60;
            const adjustedDragEndTime = `${String(dragEndHour).padStart(2, "0")}:${String(dragEndMin).padStart(2, "0")}`;

            setSelectedStartTime(time);
            setSelectedEndTime(adjustedDragEndTime);
          }
        }
      }
    }
  };

  // Handle touch end
  const handleTouchEnd = () => {
    // Clear long-press timer if still pending
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }

    // If in drag mode, finish the selection
    if (isDragMode && selectedStartTime && selectedEndTime) {
      setIsDragMode(false);
      setIsDragging(false);

      // Ensure valid selection
      const [startHour, startMin] = selectedStartTime.split(":").map(Number);
      const [endHour, endMin] = selectedEndTime.split(":").map(Number);
      const startMinutes = startHour * 60 + startMin;
      const endMinutes = endHour * 60 + endMin;

      if (endMinutes > startMinutes) {
        // Valid selection - open details
        setTimeout(() => {
          setShowDetails(true);
        }, 100);
      } else {
        // Invalid - clear
        setSelectedStartTime("");
        setSelectedEndTime("");
      }

      touchStartPositionRef.current = null;
      return;
    }

    // If not in drag mode and no tap mode active, this was a quick tap
    // Enter tap-to-select mode
    if (!isDragMode && !tapSelectionMode && selectedStartTime) {
      setTapSelectionMode(true);

      // Auto-exit tap mode after 10 seconds
      if (tapTimeoutRef.current) {
        clearTimeout(tapTimeoutRef.current);
      }
      tapTimeoutRef.current = setTimeout(() => {
        setTapSelectionMode(false);
        setSelectedStartTime("");
        setSelectedEndTime("");
      }, 10000);
    }

    setIsDragMode(false);
    setIsDragging(false);
    touchStartPositionRef.current = null;
  };

  const handleDayClick = (dateString: string) => {
    if (multiDayMode) {
      // In multi-day mode, toggle selection
      setSelectedDays((prev) => {
        const next = new Set(prev);
        if (next.has(dateString)) {
          next.delete(dateString);
        } else {
          next.add(dateString);
        }
        return next;
      });
    } else {
      // Normal mode - select single date
      setSelectedDate(dateString);
      // Don't show details yet - wait for time selection in day planner
    }
  };

  const handleDayLongPressStart = (
    e: React.MouseEvent | React.TouchEvent,
    dateString: string,
  ) => {
    // Prevent default to avoid text selection and context menu
    e.preventDefault();
    e.stopPropagation();

    // Don't enter multi-day mode if already in details view or editing
    if (showDetails || editingEntryData) {
      return;
    }

    // Start long press timer
    multiDayLongPressStartRef.current = { dateString, startTime: Date.now() };
    multiDayLongPressTimerRef.current = setTimeout(() => {
      // Enter multi-day mode
      setMultiDayMode(true);
      setSelectedDays(new Set([dateString])); // Start with the long-pressed day selected
      multiDayLongPressStartRef.current = null;

      // Haptic feedback on mobile if available
      if ("vibrate" in navigator) {
        navigator.vibrate(50);
      }
    }, 600); // 600ms for long press (slightly longer for mobile)
  };

  const handleDayLongPressEnd = (
    e: React.MouseEvent | React.TouchEvent,
    dateString: string,
  ) => {
    e.preventDefault();
    e.stopPropagation();

    // Clear long press timer if it hasn't fired yet
    if (multiDayLongPressTimerRef.current) {
      clearTimeout(multiDayLongPressTimerRef.current);
      multiDayLongPressTimerRef.current = null;
    }

    // If we're already in multi-day mode, toggle selection
    if (multiDayMode) {
      handleDayClick(dateString);
    } else if (multiDayLongPressStartRef.current) {
      // If long press didn't complete, it was just a normal click
      const duration = Date.now() - multiDayLongPressStartRef.current.startTime;
      if (duration < 600) {
        // Normal click - handle normally
        handleDayClick(dateString);
      }
    }

    multiDayLongPressStartRef.current = null;
  };

  const handleDayLongPressCancel = () => {
    // Cancel long press if mouse/touch leaves the element
    if (multiDayLongPressTimerRef.current) {
      clearTimeout(multiDayLongPressTimerRef.current);
      multiDayLongPressTimerRef.current = null;
    }
    multiDayLongPressStartRef.current = null;
  };

  const exitMultiDayMode = () => {
    setMultiDayMode(false);
    setSelectedDays(new Set());
  };

  const applyMultiDaySelection = () => {
    // When user creates a work entry, apply it to all selected days
    // This will be handled when saving entries - we'll need to duplicate entries for each selected day
    exitMultiDayMode();
  };

  // Handle opening entry details for editing
  const handleEntryDoubleClick = (
    entry: any,
    timesheet?: any,
    isLocalEntry?: boolean,
  ) => {
    if (isApprovedReadOnly) return;
    // Prevent opening details if we just finished dragging
    if (justFinishedDragRef.current) {
      return;
    }

    // Clear any pending click timeout
    if (entryClickTimeoutRef.current) {
      clearTimeout(entryClickTimeoutRef.current);
      entryClickTimeoutRef.current = null;
    }

    // Set entry data for editing
    if (isLocalEntry) {
      setEditingEntryData({ entry, isLocal: true });
    } else {
      setEditingEntryData({ entry, timesheet, isLocal: false });
    }

    // Extract times from entry (handle both ISO date strings and "HH:mm" time strings)
    const parseEntryTime = (t: any): string => {
      if (t == null || t === "") return "";
      const d = new Date(t);
      if (!isNaN(d.getTime()))
        return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
      if (typeof t === "string" && /^\d{1,2}:\d{2}(:\d{2})?$/.test(t)) {
        const [h, m] = t.split(":").map(Number);
        return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
      }
      return "";
    };
    const startStr = parseEntryTime(entry.startTime);
    const endStr = parseEntryTime(entry.endTime);
    setSelectedStartTime(startStr);
    setSelectedEndTime(endStr);
    setEntryType(entry.entryType || "work");

    // Extract project ID (handle both populated object and string ID)
    const projectId =
      typeof entry.projectId === "object" && entry.projectId?._id
        ? entry.projectId._id
        : entry.projectId || "";
    setSelectedProject(projectId);

    // Extract task ID (handle both populated object and string ID)
    const taskId =
      typeof entry.taskId === "object" && entry.taskId?._id
        ? entry.taskId._id
        : entry.taskId || "";
    setSelectedTask(taskId);

    // Extract general work item ID (handle both populated object and string ID)
    const generalWorkItemId =
      typeof entry.generalWorkItemId === "object" &&
      entry.generalWorkItemId?._id
        ? entry.generalWorkItemId._id
        : typeof entry.generalWorkItem === "object" &&
            entry.generalWorkItem?._id
          ? entry.generalWorkItem._id
          : entry.generalWorkItemId || "";
    setSelectedGeneralWorkItem(generalWorkItemId);

    // Extract equipment IDs (handle both array of objects and array of IDs)
    const equipmentIds = Array.isArray(entry.equipment)
      ? entry.equipment
          .map((eq: any) => (typeof eq === "object" && eq._id ? eq._id : eq))
          .filter((id: any) => id)
      : [];
    setSelectedEquipment(equipmentIds);

    // Extract materials (handle both array of objects with quantity/unit and array of IDs)
    const materialData = Array.isArray(entry.materials)
      ? entry.materials
          .map((mat: any) => {
            if (typeof mat === "object" && mat._id) {
              // If it's an object with quantity and unit, use it directly
              if (mat.quantity && mat.unit) {
                const materialItem = materials.find(
                  (m: any) => m._id === mat._id,
                );
                return {
                  _id: mat._id,
                  quantity: mat.quantity,
                  unit: mat.unit,
                  name: materialItem?.name || "",
                };
              }
              // If it's an object but no quantity/unit, treat as ID
              const materialItem = materials.find(
                (m: any) => m._id === mat._id,
              );
              if (materialItem) {
                return {
                  _id: mat._id,
                  quantity: 1, // Default quantity
                  unit: materialItem.unit || "",
                  name: materialItem.name,
                };
              }
              return null;
            } else {
              // It's just an ID
              const materialItem = materials.find((m: any) => m._id === mat);
              if (materialItem) {
                return {
                  _id: mat,
                  quantity: 1, // Default quantity
                  unit: materialItem.unit || "",
                  name: materialItem.name,
                };
              }
              return null;
            }
          })
          .filter((m: any) => m !== null)
      : [];
    setSelectedMaterials(materialData);

    setNotes(entry.notes || "");
    setTravelStartLocation(entry.travelStartLocation || "");
    setTravelEndLocation(entry.travelEndLocation || "");

    // Set selected staff for local entries
    if (isLocalEntry && entry.staffId && onStaffChange) {
      onStaffChange([entry.staffId]);
    }

    // Open details page
    setShowDetails(true);

    // Extract material IDs for logging
    const materialIds = materialData.map((m: any) => m._id);

    console.log("📝 [ENTRY EDIT] Opening entry for editing:", {
      entry,
      timesheet,
      isLocalEntry,
      projectId,
      taskId,
      generalWorkItemId,
      equipmentIds,
      materialIds,
    });
  };

  // Handle dragging local (unsaved) work entries (resize only, no move)
  const handleLocalEntryMouseDown = (
    e: React.MouseEvent,
    entry: any,
    entryIndex: number,
    mode: "resize-start" | "resize-end",
  ) => {
    if (isApprovedReadOnly) return;
    e.stopPropagation();
    e.preventDefault();

    // Clear any selected times to prevent handleMouseUp from opening details
    setSelectedStartTime("");
    setSelectedEndTime("");

    setIsDraggingEntry(true);
    setDragEntryMode(mode);
    setDragEntryStartY(e.clientY);
    setEditingWorkEntry({ entry, entryIndex, isLocal: true });

    // Store original times
    const startDate = new Date(entry.startTime);
    const endDate = new Date(entry.endTime);
    const startTime = `${String(startDate.getHours()).padStart(2, "0")}:${String(startDate.getMinutes()).padStart(2, "0")}`;
    const endTime = `${String(endDate.getHours()).padStart(2, "0")}:${String(endDate.getMinutes()).padStart(2, "0")}`;
    setDragEntryOriginalStart(startTime);
    setDragEntryOriginalEnd(endTime);

    console.log("🖱️ [LOCAL ENTRY DRAG] Started dragging local entry:", {
      mode,
      entry,
      startTime,
      endTime,
    });
  };

  // Handle dragging work entries (resize only, no move)
  const handleEntryMouseDown = (
    e: React.MouseEvent,
    entry: any,
    timesheet: any,
    entryIndex: number,
    mode: "resize-start" | "resize-end",
  ) => {
    if (isApprovedReadOnly) return;
    e.stopPropagation();
    e.preventDefault();

    // Clear any selected times to prevent handleMouseUp from opening details
    setSelectedStartTime("");
    setSelectedEndTime("");

    setIsDraggingEntry(true);
    setDragEntryMode(mode);
    setDragEntryStartY(e.clientY);
    setEditingWorkEntry({ entry, timesheet, entryIndex, isLocal: false });

    // Store original times
    const startDate = new Date(entry.startTime);
    const endDate = new Date(entry.endTime);
    const startTime = `${String(startDate.getHours()).padStart(2, "0")}:${String(startDate.getMinutes()).padStart(2, "0")}`;
    const endTime = `${String(endDate.getHours()).padStart(2, "0")}:${String(endDate.getMinutes()).padStart(2, "0")}`;
    setDragEntryOriginalStart(startTime);
    setDragEntryOriginalEnd(endTime);

    console.log("🖱️ [ENTRY DRAG] Started dragging entry:", {
      mode,
      entry,
      startTime,
      endTime,
    });
  };

  const handleEntryMouseMove = (e: MouseEvent | React.MouseEvent) => {
    if (!isDraggingEntry || !editingWorkEntry || !dragEntryMode) return;

    e.preventDefault();
    e.stopPropagation();

    // Find which time block we're over
    const timeBlocks = Array.from(
      document.querySelectorAll("[data-time]"),
    ) as HTMLElement[];
    const mouseY = "clientY" in e ? e.clientY : (e as React.MouseEvent).clientY;

    let closestTimeBlock: HTMLElement | null = null;
    let closestDistance = Infinity;

    timeBlocks.forEach((block) => {
      const rect = block.getBoundingClientRect();
      const blockCenterY = rect.top + rect.height / 2;
      const distance = Math.abs(mouseY - blockCenterY);

      if (distance < closestDistance) {
        closestDistance = distance;
        closestTimeBlock = block;
      }
    });

    if (closestTimeBlock !== null && closestTimeBlock !== undefined) {
      const newTime = (closestTimeBlock as HTMLElement).getAttribute(
        "data-time",
      );
      if (newTime) {
        const { entry, timesheet, entryIndex } = editingWorkEntry;
        const startDate = new Date(entry.startTime);
        const endDate = new Date(entry.endTime);
        let newStartTime = `${String(startDate.getHours()).padStart(2, "0")}:${String(startDate.getMinutes()).padStart(2, "0")}`;
        let newEndTime = `${String(endDate.getHours()).padStart(2, "0")}:${String(endDate.getMinutes()).padStart(2, "0")}`;

        if (dragEntryMode === "resize-start") {
          newStartTime = newTime;
          // Ensure start is before end
          if (newStartTime >= newEndTime) {
            newStartTime = newEndTime;
          }
        } else if (dragEntryMode === "resize-end") {
          newEndTime = newTime;
          // Ensure end is after start
          if (newEndTime <= newStartTime) {
            newEndTime = newStartTime;
          }
        }

        const [newStartHour, newStartMin] = newStartTime.split(":").map(Number);
        const [newEndHour, newEndMin] = newEndTime.split(":").map(Number);
        const newStartDate = new Date(startDate);
        newStartDate.setHours(newStartHour, newStartMin, 0, 0);
        const newEndDate = new Date(endDate);
        newEndDate.setHours(newEndHour, newEndMin, 0, 0);

        // Update local state for visual feedback
        setEditingWorkEntry({
          ...editingWorkEntry,
          entry: {
            ...entry,
            startTime: newStartDate.toISOString(),
            endTime: newEndDate.toISOString(),
          },
        });
      }
    }
  };

  const handleEntryMouseUp = async () => {
    if (!isDraggingEntry || !editingWorkEntry || !dragEntryMode) return;

    setIsDraggingEntry(false);
    setDragEntryMode(null);

    const { entry, timesheet, entryIndex } = editingWorkEntry;

    // Calculate new times
    const startDate = new Date(entry.startTime);
    const endDate = new Date(entry.endTime);
    const [startHour, startMin] = [
      startDate.getHours(),
      startDate.getMinutes(),
    ];
    const [endHour, endMin] = [endDate.getHours(), endDate.getMinutes()];

    const totalMinutes = endHour * 60 + endMin - (startHour * 60 + startMin);
    const totalHours = totalMinutes / 60;

    // Validate minimum duration (15 minutes)
    if (totalMinutes < 15) {
      setMessage("Minimum entry duration is 15 minutes");
      setEditingWorkEntry(null);
      return;
    }

    // Update work entry in backend
    try {
      setLoading(true);
      const token = localStorage.getItem("token");

      // Get the timesheet's work entries
      const updatedWorkEntries = [...(timesheet.workEntries || [])];
      updatedWorkEntries[entryIndex] = {
        ...updatedWorkEntries[entryIndex],
        startTime: entry.startTime,
        endTime: entry.endTime,
        hours: totalHours,
      };

      // Recalculate total hours
      const newTotalHours = updatedWorkEntries.reduce(
        (sum: number, e: any) => sum + (e.hours || 0),
        0,
      );

      // Update timesheet
      const response = await fetch(
        `${apiUrl}/api/timesheets/${timesheet._id}`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            workEntries: updatedWorkEntries,
            totalHours: newTotalHours,
            startTime: updatedWorkEntries[0]?.startTime || timesheet.startTime,
            endTime:
              updatedWorkEntries[updatedWorkEntries.length - 1]?.endTime ||
              timesheet.endTime,
          }),
        },
      );

      if (!response.ok) throw new Error("Failed to update work entry");

      // Refresh timesheets
      await fetchDateTimesheets(selectedDate);
    } catch (error) {
      console.error("Error updating work entry:", error);
      setMessage("Failed to update work entry");
    }
  };

  const handleDeleteEntry = (
    entry: any,
    timesheet: any,
    entryIndex: number,
  ) => {
    setConfirmMessage("Are you sure you want to delete this entry?");
    const executeDelete = async () => {
      setShowConfirmDialog(false);
      const updatedWorkEntries = [...(timesheet.workEntries || [])];
      updatedWorkEntries.splice(entryIndex, 1);
      const newTotalHours = updatedWorkEntries.reduce(
        (sum: number, entry: any) => sum + (entry.hours || 0),
        0,
      );

      // Optimistic update FIRST - remove entry from UI immediately
      setDateTimesheets((prev) =>
        prev.map((ts: any) => {
          if (ts._id !== timesheet._id) return ts;
          return {
            ...ts,
            workEntries: updatedWorkEntries,
            totalHours: newTotalHours,
          };
        }),
      );
      setMessage("Work entry deleted successfully");

      try {
        const token = localStorage.getItem("token");
        const response = await fetch(
          `${apiUrl}/api/timesheets/${timesheet._id}`,
          {
            method: "PUT",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              workEntries: updatedWorkEntries,
              totalHours: newTotalHours,
            }),
          },
        );
        if (!response.ok) throw new Error("Failed to delete work entry");

        const result = await response.json();
        if (result.data) {
          setDateTimesheets((prev) =>
            prev.map((ts: any) =>
              ts._id === timesheet._id ? result.data : ts,
            ),
          );
        }

        const staffKey =
          selectedStaff && selectedStaff.length > 0
            ? selectedStaff.sort().join(",")
            : "all";
        const cacheKey = CACHE_KEYS.TIMESHEETS_BY_STAFF(
          selectedDate,
          staffKey.split(",").filter(Boolean),
        );
        offlineDataCache.remove(cacheKey);
        fetchDateTimesheets(selectedDate).catch(() => {});

        window.dispatchEvent(new CustomEvent("timesheetSaved"));
      } catch (error) {
        console.error("Error deleting work entry:", error);
        setMessage("Failed to delete work entry");
        setDateTimesheets((prev) =>
          prev.map((ts: any) => {
            if (ts._id !== timesheet._id) return ts;
            return {
              ...ts,
              workEntries: timesheet.workEntries || [],
              totalHours: timesheet.totalHours ?? 0,
            };
          }),
        );
      }
    };
    setConfirmCallback(() => executeDelete);
    setShowConfirmDialog(true);
  };

  const handleStartRecording = async () => {
    // Disable voice transcription when offline
    if (!isOnline) {
      setMessage(
        t("timesheet.voiceTranscriptionOffline") ||
          "Voice transcription unavailable offline",
      );
      return;
    }

    if (isRecording) {
      // Stop recording
      setIsRecording(false);
      return;
    }

    setIsRecording(true);

    try {
      // Check if browser supports speech recognition
      const SpeechRecognition =
        (window as any).SpeechRecognition ||
        (window as any).webkitSpeechRecognition;

      if (!SpeechRecognition) {
        setMessage("Speech recognition not supported in this browser");
        setIsRecording(false);
        return;
      }

      const recognition = new SpeechRecognition();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = "en-US";

      recognition.onresult = (event: any) => {
        let finalTranscript = "";
        let interimTranscript = "";

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcript + " ";
          } else {
            interimTranscript += transcript;
          }
        }

        if (finalTranscript) {
          setNotes((prev) => prev + finalTranscript);
        }
      };

      recognition.onerror = (event: any) => {
        console.error("Speech recognition error:", event.error);
        setMessage(`Speech recognition error: ${event.error}`);
        setIsRecording(false);
      };

      recognition.onend = () => {
        setIsRecording(false);
      };

      recognition.start();
    } catch (error) {
      console.error("Failed to start speech recognition:", error);
      setMessage("Failed to start speech recognition");
      setIsRecording(false);
    }
  };

  const handleFormatWithAI = async () => {
    // Disable AI formatting when offline
    if (!isOnline) {
      setMessage(
        t("timesheet.aiFormattingOffline") ||
          "AI formatting unavailable offline",
      );
      return;
    }

    setIsFormattingAI(true);
    setOriginalNotes(notes);

    try {
      const token = localStorage.getItem("token");

      // Gather context for AI
      const selectedProj = projects.find((p) => p._id === selectedProject);
      const selectedTsk = tasks.find((t) => t._id === selectedTask);
      const selectedEquip = equipment.filter((e) =>
        selectedEquipment.includes(e._id),
      );
      const selectedMats = materials.filter((m) =>
        selectedMaterials.includes(m._id),
      );

      // Calculate hours
      const [startHour, startMin] = selectedStartTime.split(":").map(Number);
      const [endHour, endMin] = selectedEndTime.split(":").map(Number);
      const totalMinutes = endHour * 60 + endMin - (startHour * 60 + startMin);
      const hours = totalMinutes / 60;

      const context = {
        notes: notes.trim(),
        project: selectedProj
          ? {
              name: selectedProj.name,
              projectNumber: selectedProj.projectNumber,
            }
          : null,
        task: selectedTsk ? { title: selectedTsk.title } : null,
        equipment: selectedEquip.map((e) => e.name),
        materials: selectedMats.map((m) => m.name),
        hours: hours,
        startTime: selectedStartTime,
        endTime: selectedEndTime,
        date: selectedDate,
      };

      const response = await fetch(`${apiUrl}/api/ai/format-notes`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(context),
      });

      const data = await response.json();

      // Check for token errors (402 status)
      if (handleTokenError(response, data)) {
        // Token purchase modal will be shown automatically
        setMessage(data.message || "Insufficient tokens. Please purchase more tokens to continue using AI features.");
        setIsFormattingAI(false);
        return;
      }

      if (!response.ok || !data.success) {
        throw new Error(data.message || "Failed to format notes");
      }

      setAiFormattedNotes(data.formattedNotes || data.data?.formattedNotes);
      setShowAIPreview(true);
    } catch (error) {
      console.error("Failed to format with AI:", error);
      setMessage(error instanceof Error ? error.message : "Failed to format notes with AI. Please try again.");
    } finally {
      setIsFormattingAI(false);
    }
  };

  const handleAcceptAI = () => {
    setNotes(aiFormattedNotes);
    setShowAIPreview(false);
    setAiFormattedNotes("");
    setOriginalNotes("");
  };

  const handleRevertAI = () => {
    setNotes(originalNotes);
    setShowAIPreview(false);
    setAiFormattedNotes("");
    setOriginalNotes("");
  };

  // Calculate the number of unique timesheets that will be created
  const getUniqueTimesheetCount = (): number => {
    const uniqueTimesheets = new Set<string>();
    localWorkEntries.forEach((entry: any) => {
      // Handle multi-staff entries
      const staffIds = entry.staffIds || (entry.staffId ? [entry.staffId] : []);
      staffIds.forEach((staffId: string) => {
        const key = `${staffId}-${entry.date}`;
        uniqueTimesheets.add(key);
      });
    });
    return uniqueTimesheets.size;
  };

  // Check if a time range overlaps with any existing entries
  const checkTimeOverlap = (
    startTime: Date,
    endTime: Date,
    excludeEntryId?: string,
    excludeEntryIndex?: number,
  ): boolean => {
    // Check against local entries
    for (let i = 0; i < localWorkEntries.length; i++) {
      if (excludeEntryIndex !== undefined && i === excludeEntryIndex) continue;

      const entry = localWorkEntries[i];
      const entryStart = new Date(entry.startTime);
      const entryEnd = new Date(entry.endTime);

      // Check if times overlap (they overlap if one starts before the other ends)
      if (startTime < entryEnd && endTime > entryStart) {
        return true;
      }
    }

    // Check against saved entries
    const timesheetsArray = Array.isArray(dateTimesheets) ? dateTimesheets : [];
    const timesheetsToCheck = editingTimesheet
      ? timesheetsArray.filter((ts: any) => ts._id === editingTimesheet._id)
      : timesheetsArray;

    for (const timesheet of timesheetsToCheck) {
      if (!timesheet.workEntries || timesheet.workEntries.length === 0)
        continue;

      for (let i = 0; i < timesheet.workEntries.length; i++) {
        const entry = timesheet.workEntries[i];
        const entryStart = new Date(entry.startTime);
        const entryEnd = new Date(entry.endTime);

        // Skip if this is the entry we're editing
        if (excludeEntryId && entry._id === excludeEntryId) continue;
        if (
          editingEntryData &&
          editingEntryData.timesheet?._id === timesheet._id &&
          i === editingEntryData.entryIndex
        )
          continue;

        // Check if times overlap
        if (startTime < entryEnd && endTime > entryStart) {
          return true;
        }
      }
    }

    return false;
  };

  // Save pending entry changes from drag resize
  const handleSavePendingChanges = async () => {
    if (!pendingEntryChanges) return;

    try {
      setLoading(true);
      const token = localStorage.getItem("token");

      // Find the timesheet
      const timesheet = dateTimesheets.find(
        (ts: any) => ts._id === pendingEntryChanges.timesheetId,
      );
      if (!timesheet) {
        setMessage("Timesheet not found");
        setPendingEntryChanges(null);
        return;
      }

      // Get the timesheet's work entries
      const updatedWorkEntries = [...(timesheet.workEntries || [])];
      const editedEntry = updatedWorkEntries[pendingEntryChanges.entryIndex];

      // Update the edited entry with new times
      updatedWorkEntries[pendingEntryChanges.entryIndex] = {
        ...editedEntry,
        startTime: pendingEntryChanges.startTime,
        endTime: pendingEntryChanges.endTime,
        hours: pendingEntryChanges.hours,
      };

      // Handle overlapping entries - split or adjust them
      const startTimeDate = new Date(pendingEntryChanges.startTime);
      const endTimeDate = new Date(pendingEntryChanges.endTime);
      const entriesToAdd: any[] = [];

      // Process each entry to handle overlaps (excluding the edited entry itself)
      for (let i = updatedWorkEntries.length - 1; i >= 0; i--) {
        // Skip the entry being edited
        if (i === pendingEntryChanges.entryIndex) continue;

        const entry = updatedWorkEntries[i];
        const entryStart = new Date(entry.startTime);
        const entryEnd = new Date(entry.endTime);

        // Check if times overlap
        if (startTimeDate < entryEnd && endTimeDate > entryStart) {
          // Overlap detected - need to split or adjust
          console.log("🔀 [SPLIT] Overlap detected with entry:", {
            entry,
            newStart: startTimeDate,
            newEnd: endTimeDate,
          });

          // Case 1: Edited entry completely covers existing entry - remove it
          if (startTimeDate <= entryStart && endTimeDate >= entryEnd) {
            console.log("🔀 [SPLIT] Removing completely covered entry");
            updatedWorkEntries.splice(i, 1);
            // Adjust entry index if we removed an entry before the edited one
            if (i < pendingEntryChanges.entryIndex) {
              pendingEntryChanges.entryIndex--;
            }
          }
          // Case 2: Edited entry is completely inside existing entry - split into two
          else if (startTimeDate > entryStart && endTimeDate < entryEnd) {
            console.log("🔀 [SPLIT] Splitting entry into two parts");

            // Keep first part (before edited entry) - only if >= 15 mins
            const firstPartMinutes =
              (startTimeDate.getTime() - entryStart.getTime()) / (1000 * 60);
            if (firstPartMinutes >= 15) {
              updatedWorkEntries[i] = {
                ...entry,
                endTime: startTimeDate.toISOString(),
                hours: firstPartMinutes / 60,
              };
            } else {
              // Remove entry if first part is too short
              updatedWorkEntries.splice(i, 1);
              if (i < pendingEntryChanges.entryIndex) {
                pendingEntryChanges.entryIndex--;
              }
            }

            // Add second part (after edited entry) - only if >= 15 mins
            const secondPartMinutes =
              (entryEnd.getTime() - endTimeDate.getTime()) / (1000 * 60);
            if (secondPartMinutes >= 15) {
              entriesToAdd.push({
                ...entry,
                _id: entry._id ? `${entry._id}-split-${Date.now()}` : undefined,
                startTime: endTimeDate.toISOString(),
                hours: secondPartMinutes / 60,
              });
            }
          }
          // Case 3: Edited entry overlaps start of existing entry - adjust existing entry's start
          else if (startTimeDate <= entryStart && endTimeDate > entryStart) {
            console.log("🔀 [SPLIT] Adjusting entry start time");
            const remainingMinutes =
              (entryEnd.getTime() - endTimeDate.getTime()) / (1000 * 60);
            if (remainingMinutes >= 15) {
              updatedWorkEntries[i] = {
                ...entry,
                startTime: endTimeDate.toISOString(),
                hours: remainingMinutes / 60,
              };
            } else {
              // Remove entry if remaining part is too short
              updatedWorkEntries.splice(i, 1);
              if (i < pendingEntryChanges.entryIndex) {
                pendingEntryChanges.entryIndex--;
              }
            }
          }
          // Case 4: Edited entry overlaps end of existing entry - adjust existing entry's end
          else if (startTimeDate < entryEnd && endTimeDate >= entryEnd) {
            console.log("🔀 [SPLIT] Adjusting entry end time");
            const remainingMinutes =
              (startTimeDate.getTime() - entryStart.getTime()) / (1000 * 60);
            if (remainingMinutes >= 15) {
              updatedWorkEntries[i] = {
                ...entry,
                endTime: startTimeDate.toISOString(),
                hours: remainingMinutes / 60,
              };
            } else {
              // Remove entry if remaining part is too short
              updatedWorkEntries.splice(i, 1);
              if (i < pendingEntryChanges.entryIndex) {
                pendingEntryChanges.entryIndex--;
              }
            }
          }
        }
      }

      // Insert split entries after the edited entry
      if (entriesToAdd.length > 0) {
        updatedWorkEntries.splice(
          pendingEntryChanges.entryIndex + 1,
          0,
          ...entriesToAdd,
        );
      }

      // Recalculate total hours
      const newTotalHours = updatedWorkEntries.reduce(
        (sum: number, e: any) => sum + (e.hours || 0),
        0,
      );

      // Update timesheet
      const response = await fetch(
        `${apiUrl}/api/timesheets/${timesheet._id}`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            workEntries: updatedWorkEntries,
            totalHours: newTotalHours,
            startTime: updatedWorkEntries[0]?.startTime || timesheet.startTime,
            endTime:
              updatedWorkEntries[updatedWorkEntries.length - 1]?.endTime ||
              timesheet.endTime,
          }),
        },
      );

      if (!response.ok) throw new Error("Failed to update work entry");

      // Refresh timesheets
      await fetchDateTimesheets(selectedDate);

      // Dispatch event to refresh timesheet lists
      window.dispatchEvent(new CustomEvent("timesheetSaved"));

      // Clear pending changes and local entries
      setPendingEntryChanges(null);
      setLocalWorkEntries([]);

      console.log(
        "✅ [SAVE PENDING] Saved pending entry changes:",
        pendingEntryChanges,
      );

      // Close the form and go back
      onClose();
    } catch (error) {
      console.error("❌ [SAVE PENDING] Error saving pending changes:", error);
      setMessage("Failed to save changes");
      setLoading(false);
    }
  };

  const handleSaveDetails = async () => {
    // When editing, fall back to entry times if form state is not yet set
    const entry = editingEntryData?.entry;
    const parseTime = (t: any): string => {
      if (t == null || t === "") return "";
      const d = new Date(t);
      if (!isNaN(d.getTime()))
        return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
      if (typeof t === "string" && /^\d{1,2}:\d{2}/.test(t)) {
        const [h, m] = t.split(":").map(Number);
        return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
      }
      return "";
    };
    const startToUse =
      selectedStartTime || (entry ? parseTime(entry.startTime) : "");
    const endToUse =
      selectedEndTime || (entry ? parseTime(entry.endTime) : "");
    if (!startToUse || !endToUse) {
      setMessage("Please select a time range");
      return;
    }

    // Validate minimum duration (15 minutes)
    const [startHour, startMin] = startToUse.split(":").map(Number);
    const [endHour, endMin] = endToUse.split(":").map(Number);
    const totalMinutes = endHour * 60 + endMin - (startHour * 60 + startMin);

    if (totalMinutes < 15) {
      setMessage("Minimum entry duration is 15 minutes");
      return;
    }

    // Validate based on entry type
    if (entryType === "work" && !selectedProject) {
      setMessage("Please select a project");
      return;
    }
    if (entryType === "work" && !selectedTask) {
      setMessage("Please select a task");
      return;
    }
    if (entryType === "general" && !selectedGeneralWorkItem) {
      setMessage("Please select a general work item");
      return;
    }

    // If editing an existing entry, update it
    if (editingEntryData) {
      // Check if it's a local entry (not saved yet)
      if (editingEntryData.isLocal) {
        const { entry } = editingEntryData;

        // Calculate hours (using already validated totalMinutes from above)
        const totalHours = totalMinutes / 60;

        // Create Date objects using local date components
        const [year, month, day] = selectedDate.split("-").map(Number);
        const startTimeDate = new Date(
          year,
          month - 1,
          day,
          startHour,
          startMin,
          0,
          0,
        );
        const endTimeDate = new Date(
          year,
          month - 1,
          day,
          endHour,
          endMin,
          0,
          0,
        );

        // Find and update the local entry
        const entryIndex = localWorkEntries.findIndex(
          (e: any) => e.id === entry.id,
        );
        if (entryIndex === -1) {
          setMessage("Entry not found");
          return;
        }

        const updatedEntries = [...localWorkEntries];
        updatedEntries[entryIndex] = {
          ...updatedEntries[entryIndex],
          entryType: entryType,
          projectId: entryType === "work" ? selectedProject : undefined,
          taskId: entryType === "work" ? selectedTask || undefined : undefined,
          generalWorkItemId:
            entryType === "general" ? selectedGeneralWorkItem : undefined,
          equipment:
            entryType !== "break" && entryType !== "travel"
              ? equipment.filter((e: any) => selectedEquipment.includes(e._id))
              : [],
          materials:
            entryType !== "break" && entryType !== "travel"
              ? selectedMaterials.map((m) => ({
                  _id: m._id,
                  quantity: m.quantity,
                  unit: m.unit,
                }))
              : [],
          notes: notes,
          startTime: startTimeDate.toISOString(),
          endTime: endTimeDate.toISOString(),
          startTimeLocal: `${String(startHour).padStart(2, "0")}:${String(startMin).padStart(2, "0")}`,
          endTimeLocal: `${String(endHour).padStart(2, "0")}:${String(endMin).padStart(2, "0")}`,
          workHours: totalHours,
          breakHours: entryType === "break" ? totalHours : 0,
          totalHours: totalHours,
          project:
            entryType === "work"
              ? projects.find((p: any) => p._id === selectedProject)
              : null,
          task:
            entryType === "work" && selectedTask
              ? tasks.find((t: any) => t._id === selectedTask)
              : null,
          generalWorkItem:
            entryType === "general"
              ? generalWorkItems.find(
                  (g: any) => g._id === selectedGeneralWorkItem,
                )
              : null,
        };

        setLocalWorkEntries(updatedEntries);
        setEditingEntryData(null);
        setShowDetails(false);

        // Clear form
        setSelectedProject("");
        setSelectedTask("");
        setSelectedEquipment([]);
        setSelectedMaterials([]);
        setNotes("");
        setSelectedStartTime("");
        setSelectedEndTime("");
        setShowAIPreview(false);
        setTaskSelectionConfirmed(false);

        console.log("✅ [UPDATE LOCAL ENTRY] Updated local work entry:", {
          entryIndex,
          newStartTime: startTimeDate.toISOString(),
          newEndTime: endTimeDate.toISOString(),
          totalHours,
        });

        return;
      }

      // It's a saved entry - update in backend
      const { entry, timesheet } = editingEntryData;

      // Find the entry index in the timesheet
      const entryIndex =
        timesheet.workEntries?.findIndex(
          (e: any) =>
            e._id?.toString() === entry._id?.toString() ||
            (e.startTime === entry.startTime && e.endTime === entry.endTime),
        ) ?? -1;

      if (entryIndex === -1) {
        setMessage("Entry not found");
        return;
      }

      // Calculate hours (using already validated totalMinutes from above)
      const totalHours = totalMinutes / 60;

      // Create Date objects using local date components
      const [year, month, day] = selectedDate.split("-").map(Number);
      const startTimeDate = new Date(
        year,
        month - 1,
        day,
        startHour,
        startMin,
        0,
        0,
      );
      const endTimeDate = new Date(year, month - 1, day, endHour, endMin, 0, 0);

      try {
        setLoading(true);
        const token = localStorage.getItem("token");

        // Get the timesheet's work entries
        const updatedWorkEntries = [...(timesheet.workEntries || [])];
        updatedWorkEntries[entryIndex] = {
          ...updatedWorkEntries[entryIndex],
          projectId: selectedProject,
          taskId: selectedTask || undefined,
          equipment: selectedEquipment,
          materials: selectedMaterials.map((m) => ({
            _id: m._id,
            quantity: m.quantity,
            unit: m.unit,
          })),
          notes: notes,
          startTime: startTimeDate.toISOString(),
          endTime: endTimeDate.toISOString(),
          hours: totalHours,
        };

        // Recalculate total hours
        const newTotalHours = updatedWorkEntries.reduce(
          (sum: number, e: any) => sum + (e.hours || 0),
          0,
        );

        // Update timesheet
        const response = await fetch(
          `${apiUrl}/api/timesheets/${timesheet._id}`,
          {
            method: "PUT",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              workEntries: updatedWorkEntries,
              totalHours: newTotalHours,
              startTime:
                updatedWorkEntries[0]?.startTime || timesheet.startTime,
              endTime:
                updatedWorkEntries[updatedWorkEntries.length - 1]?.endTime ||
                timesheet.endTime,
            }),
          },
        );

        if (!response.ok) throw new Error("Failed to update work entry");

        // Refresh timesheets
        await fetchDateTimesheets(selectedDate);
        setEditingEntryData(null);
        setShowDetails(false);

        // Clear form
        setSelectedProject("");
        setSelectedTask("");
        setSelectedEquipment([]);
        setSelectedMaterials([]);
        setNotes("");
        setSelectedStartTime("");
        setSelectedEndTime("");
        setShowAIPreview(false);
        setTaskSelectionConfirmed(false);

        console.log("✅ [UPDATE ENTRY] Updated work entry:", {
          entryIndex,
          newStartTime: startTimeDate.toISOString(),
          newEndTime: endTimeDate.toISOString(),
          totalHours,
        });
      } catch (error) {
        console.error("❌ [UPDATE ENTRY] Error updating work entry:", error);
        setMessage("Failed to update work entry");
      } finally {
        setLoading(false);
      }

      return;
    }

    // New entry - add to local state
    // Ensure we have staff selected (use effectiveStaff so editing works)
    if (!effectiveStaff || effectiveStaff.length === 0) {
      setMessage("Please select at least one staff member");
      return;
    }

    // Calculate hours (using already validated totalMinutes from above)
    const totalHours = totalMinutes / 60;

    // Determine which dates to create entries for
    const datesToCreate =
      multiDayMode && selectedDays.size > 0
        ? Array.from(selectedDays).sort()
        : [selectedDate];

    // Start with existing entries
    let updatedLocalEntries = [...localWorkEntries];
    const entriesToAdd: any[] = [];

    // Process each date separately to handle overlaps correctly
    datesToCreate.forEach((dateString: string) => {
      // Create Date objects using local date components for this date
      const [year, month, day] = dateString.split("-").map(Number);
      const startTimeDate = new Date(
        year,
        month - 1,
        day,
        startHour,
        startMin,
        0,
        0,
      );
      const endTimeDate = new Date(year, month - 1, day, endHour, endMin, 0, 0);

      // Handle overlapping entries for THIS specific date - split or adjust them
      // Process entries in reverse order so we can safely remove items
      for (let i = updatedLocalEntries.length - 1; i >= 0; i--) {
        const entry = updatedLocalEntries[i];
        // Only check overlaps for entries on the same date
        if (entry.date !== dateString) {
          continue;
        }

        const entryStart = new Date(entry.startTime);
        const entryEnd = new Date(entry.endTime);

        // Check if times overlap
        if (startTimeDate < entryEnd && endTimeDate > entryStart) {
          // Overlap detected - need to split or adjust
          console.log("🔀 [SPLIT] Overlap detected with local entry:", {
            entry,
            newStart: startTimeDate,
            newEnd: endTimeDate,
            date: dateString,
          });

          // Case 1: New entry completely covers existing entry - remove it
          if (startTimeDate <= entryStart && endTimeDate >= entryEnd) {
            console.log("🔀 [SPLIT] Removing completely covered entry");
            updatedLocalEntries.splice(i, 1);
            continue;
          }
          // Case 2: New entry is completely inside existing entry - split into two
          else if (startTimeDate > entryStart && endTimeDate < entryEnd) {
            console.log("🔀 [SPLIT] Splitting entry into two parts");

            // Keep first part (before new entry) - only if >= 15 mins
            const firstPartMinutes =
              (startTimeDate.getTime() - entryStart.getTime()) / (1000 * 60);
            if (firstPartMinutes >= 15) {
              updatedLocalEntries[i] = {
                ...entry,
                endTime: startTimeDate.toISOString(),
                endTimeLocal: `${String(startTimeDate.getHours()).padStart(2, "0")}:${String(startTimeDate.getMinutes()).padStart(2, "0")}`,
                totalHours: firstPartMinutes / 60,
                workHours: firstPartMinutes / 60,
              };
            } else {
              // Remove entry if first part is too short
              updatedLocalEntries.splice(i, 1);
              continue;
            }

            // Add second part (after new entry) - only if >= 15 mins
            const secondPartMinutes =
              (entryEnd.getTime() - endTimeDate.getTime()) / (1000 * 60);
            if (secondPartMinutes >= 15) {
              entriesToAdd.push({
                ...entry,
                id: `local-${Date.now()}-${Math.random()}-split`,
                startTime: endTimeDate.toISOString(),
                startTimeLocal: `${String(endTimeDate.getHours()).padStart(2, "0")}:${String(endTimeDate.getMinutes()).padStart(2, "0")}`,
                totalHours: secondPartMinutes / 60,
                workHours: secondPartMinutes / 60,
              });
            }
          }
          // Case 3: New entry overlaps start of existing entry - adjust existing entry's start
          else if (startTimeDate <= entryStart && endTimeDate > entryStart) {
            console.log("🔀 [SPLIT] Adjusting entry start time");
            const remainingMinutes =
              (entryEnd.getTime() - endTimeDate.getTime()) / (1000 * 60);
            if (remainingMinutes >= 15) {
              updatedLocalEntries[i] = {
                ...entry,
                startTime: endTimeDate.toISOString(),
                startTimeLocal: `${String(endTimeDate.getHours()).padStart(2, "0")}:${String(endTimeDate.getMinutes()).padStart(2, "0")}`,
                totalHours: remainingMinutes / 60,
                workHours: remainingMinutes / 60,
              };
            } else {
              // Remove entry if remaining part is too short
              updatedLocalEntries.splice(i, 1);
              continue;
            }
          }
          // Case 4: New entry overlaps end of existing entry - adjust existing entry's end
          else if (startTimeDate < entryEnd && endTimeDate >= entryEnd) {
            console.log("🔀 [SPLIT] Adjusting entry end time");
            const remainingMinutes =
              (startTimeDate.getTime() - entryStart.getTime()) / (1000 * 60);
            if (remainingMinutes >= 15) {
              updatedLocalEntries[i] = {
                ...entry,
                endTime: startTimeDate.toISOString(),
                endTimeLocal: `${String(startTimeDate.getHours()).padStart(2, "0")}:${String(startTimeDate.getMinutes()).padStart(2, "0")}`,
                totalHours: remainingMinutes / 60,
                workHours: remainingMinutes / 60,
              };
            } else {
              // Remove entry if remaining part is too short
              updatedLocalEntries.splice(i, 1);
              continue;
            }
          }
        }
      }

      // Create the new entry for this date
      const newEntry = {
        id: `local-${Date.now()}-${Math.random()}-${dateString}`, // Temporary ID with date suffix
        date: dateString, // Use the specific date for this entry
        staffIds: effectiveStaff, // Array of staff IDs (use effectiveStaff so editing can add tasks)
        staffGroups: selectedStaffGroups, // Array of selected groups
        entryType: entryType,
        projectId: entryType === "work" ? selectedProject : undefined,
        taskId: entryType === "work" ? selectedTask || undefined : undefined,
        generalWorkItemId:
          entryType === "general" ? selectedGeneralWorkItem : undefined,
        equipment:
          entryType !== "break" && entryType !== "travel"
            ? equipment.filter((e: any) => selectedEquipment.includes(e._id))
            : [],
        materials:
          entryType !== "break" && entryType !== "travel"
            ? selectedMaterials.map((m: any) => ({
                _id: m._id,
                quantity: m.quantity ?? 1,
                unit: m.unit ?? "",
              }))
            : [],
        travelStartLocation:
          entryType === "travel" ? travelStartLocation : undefined,
        travelEndLocation:
          entryType === "travel" ? travelEndLocation : undefined,
        travelDistance: entryType === "travel" ? travelDistance : undefined,
        travelDistanceUnit:
          entryType === "travel" ? travelDistanceUnit : undefined,
        notes: notes,
        workHours: totalHours,
        breakHours: entryType === "break" ? totalHours : 0,
        totalHours: totalHours,
        startTime: startTimeDate.toISOString(),
        endTime: endTimeDate.toISOString(),
        startTimeLocal: `${String(startHour).padStart(2, "0")}:${String(startMin).padStart(2, "0")}`,
        endTimeLocal: `${String(endHour).padStart(2, "0")}:${String(endMin).padStart(2, "0")}`,
        project:
          entryType === "work"
            ? projects.find((p: any) => p._id === selectedProject)
            : null,
        task:
          entryType === "work" && selectedTask
            ? tasks.find((t: any) => t._id === selectedTask)
            : null,
        generalWorkItem:
          entryType === "general"
            ? generalWorkItems.find(
                (g: any) => g._id === selectedGeneralWorkItem,
              )
            : null,
      };

      entriesToAdd.push(newEntry);
    });

    console.log("🔵 [ADD ENTRY] Adding work entries to local state:", {
      entriesCount: entriesToAdd.length,
      dates: datesToCreate,
      staffCount: effectiveStaff.length,
      adjustedEntries: updatedLocalEntries.length,
    });

    setLocalWorkEntries([...updatedLocalEntries, ...entriesToAdd]);

    // Exit multi-day mode after creating entries
    if (multiDayMode) {
      exitMultiDayMode();
    }

    // Preserve selectedDate before clearing other fields
    const savedDate = selectedDate;

    // Go back to day planner view to add more entries
    // #region agent log
    fetch('http://127.0.0.1:7242/ingest/cd2d1da9-654c-485a-b113-7fb2dd09c624',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'TimesheetForm.tsx:addAnotherEntry',message:'setShowDetails(false) caller',data:{caller:'addAnotherEntry'},timestamp:Date.now(),hypothesisId:'H2'})}).catch(()=>{});
    // #endregion
    setShowDetails(false);
    setSelectedProject("");
    setSelectedTask("");
    setSelectedEquipment([]);
    setSelectedMaterials([]);
    setNotes("");
    setSelectedStartTime("");
    setSelectedEndTime("");
    setShowAIPreview(false);
    setTaskSelectionConfirmed(false);
  };

  const handleBackFromDetails = async () => {
    // #region agent log
    fetch('http://127.0.0.1:7242/ingest/cd2d1da9-654c-485a-b113-7fb2dd09c624',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'TimesheetForm.tsx:handleBackFromDetails',message:'setShowDetails(false) caller',data:{caller:'handleBackFromDetails'},timestamp:Date.now(),hypothesisId:'H2'})}).catch(()=>{});
    // #endregion
    setShowDetails(false);

    // Clear editing state - entries remain editable via drag handles regardless
    setEditingEntryData(null);

    // Clear form fields
    setEntryType("work");
    setSelectedProject("");
    setSelectedTask("");
    setSelectedGeneralWorkItem("");
    setSelectedEquipment([]);
    setSelectedMaterials([]);
    setNotes("");
    setSelectedStartTime("");
    setSelectedEndTime("");

    setProjectSearch("");
    setTaskSearch("");
    setEquipmentSearch("");
    setMaterialSearch("");
    setStaffSearch("");
    setShowProjectSearch(false);
    setShowTaskSearch(false);
    setShowEquipmentSearch(false);
    setShowMaterialSearch(false);
    setShowStaffSearch(false);

    // Refresh timesheets to ensure entries are up to date and editable
    if (selectedDate) {
      await fetchDateTimesheets(selectedDate);
    }
  };

  const handleDeleteLocalEntry = (entryIndex: number) => {
    setConfirmMessage("Are you sure you want to remove this entry?");
    const executeRemove = () => {
      setShowConfirmDialog(false);
      const updatedEntries = [...localWorkEntries];
      updatedEntries.splice(entryIndex, 1);
      setLocalWorkEntries(updatedEntries);
      setMessage(t("timesheet.entryRemoved") || "Entry removed");
    };
    setConfirmCallback(() => executeRemove);
    setShowConfirmDialog(true);
  };

  const handleStaffGroupToggle = (group: any) => {
    if (!onStaffChange) return;

    // Get all staff IDs in this group (extract ID if populated object)
    const groupStaffIds = group.staffIds
      .filter((s: any) =>
        typeof s === "string" ? true : s?.isActive !== false,
      )
      .map((s: any) => (typeof s === "string" ? s : s._id));

    // Check if this group is already selected
    const groupSelected = selectedStaffGroups.some((g) => g._id === group._id);

    if (groupSelected) {
      // Remove this group and its staff
      setSelectedStaffGroups(
        selectedStaffGroups.filter((g) => g._id !== group._id),
      );
      onStaffChange(selectedStaff.filter((id) => !groupStaffIds.includes(id)));
    } else {
      // Add this group and its staff (avoid duplicates)
      setSelectedStaffGroups([...selectedStaffGroups, group]);
      const combined = [...selectedStaff, ...groupStaffIds];
      const newSelection = Array.from(new Set(combined));
      onStaffChange(newSelection);
    }
  };

  // Filter functions - only filter if search has 3+ characters
  const filteredProjects =
    projectSearch.length >= 3
      ? projects.filter(
          (p: any) =>
            p.name.toLowerCase().includes(projectSearch.toLowerCase()) ||
            p.projectNumber.toLowerCase().includes(projectSearch.toLowerCase()),
        )
      : projects;

  const filteredTasks =
    taskSearch.length >= 3
      ? tasks.filter((t: any) =>
          t.title.toLowerCase().includes(taskSearch.toLowerCase()),
        )
      : tasks;

  const filteredEquipment =
    equipmentSearch.length >= 3
      ? equipment.filter((e: any) =>
          e.name.toLowerCase().includes(equipmentSearch.toLowerCase()),
        )
      : equipment;

  const filteredMaterials =
    materialSearch.length >= 3
      ? materials.filter((m: any) =>
          m.name.toLowerCase().includes(materialSearch.toLowerCase()),
        )
      : materials;

  const filteredStaff =
    staffSearch.length >= 3
      ? staffList.filter(
          (s: any) =>
            s.firstName.toLowerCase().includes(staffSearch.toLowerCase()) ||
            s.lastName.toLowerCase().includes(staffSearch.toLowerCase()) ||
            `${s.firstName} ${s.lastName}`
              .toLowerCase()
              .includes(staffSearch.toLowerCase()),
        )
      : staffList;

  const filteredStaffGroups =
    staffSearch.length >= 3
      ? staffGroups.filter((g: any) =>
          g.name.toLowerCase().includes(staffSearch.toLowerCase()),
        )
      : staffGroups;

  // Subscribe to offline sync status
  useEffect(() => {
    const unsubscribe = offlineSync.subscribe((status) => {
      setIsOnline(status.isOnline);
      setPendingSyncCount(status.pendingCount);
    });

    // Initial status check
    const status = offlineSync.getStatus();
    setIsOnline(status.isOnline);
    setPendingSyncCount(status.pendingCount);

    return unsubscribe;
  }, []);

  const saveTimesheets = async (status: "draft" | "submitted") => {
    // When editing existing timesheet, update its status directly
    if (editingTimesheet && localWorkEntries.length === 0) {
      const ts = (dateTimesheets || []).find(
        (t: any) => t._id === editingTimesheet._id,
      );
      if (
        ts &&
        ts.workEntries &&
        Array.isArray(ts.workEntries) &&
        ts.workEntries.length > 0
      ) {
        setLoading(true);
        setMessage("");
        try {
          const token = localStorage.getItem("token");
          if (!token) {
            setMessage(t("offline.noAuthToken"));
            setLoading(false);
            return;
          }
          const response = await fetch(
            `${apiUrl}/api/timesheets/${editingTimesheet._id}`,
            {
              method: "PUT",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({ status }),
            },
          );
          const data = await response.json();
          if (data.success) {
            setMessage(
              status === "submitted"
                ? t("timesheet.timesheetSubmittedSuccess") || "Timesheet submitted successfully"
                : t("timesheet.timesheetSavedSuccess") || "Timesheet saved successfully",
            );
            await fetchDateTimesheets(selectedDate);
            window.dispatchEvent(new CustomEvent("timesheetSaved"));
            onClose?.();
          } else {
            setMessage(data.message || t("common.error"));
          }
        } catch (error) {
          console.error("Error updating timesheet status:", error);
          setMessage(t("common.error"));
        } finally {
          setLoading(false);
        }
        return;
      }
    }

    // Require work entries to be added before creating timesheet
    if (localWorkEntries.length === 0) {
      setMessage(t("timesheet.addWorkEntriesFirst"));
      return;
    }

    setLoading(true);
    setMessage("");

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        setMessage(t("offline.noAuthToken"));
        setLoading(false);
        return;
      }

      // Group entries by date AND staff to avoid accumulating entries incorrectly
      // Key format: `${date}_${staffId}`
      const groupedEntries = new Map<
        string,
        { date: string; staffId: string; entries: any[] }
      >();

      localWorkEntries.forEach((entry) => {
        const staffIds =
          entry.staffIds || (entry.staffId ? [entry.staffId] : []);
        staffIds.forEach((staffId: string) => {
          const key = `${entry.date}_${staffId}`;
          if (!groupedEntries.has(key)) {
            groupedEntries.set(key, {
              date: entry.date,
              staffId: staffId,
              entries: [],
            });
          }
          groupedEntries.get(key)!.entries.push(entry);
        });
      });

      console.log(
        `🔵 [CREATE TIMESHEET] Grouped ${localWorkEntries.length} entries into ${groupedEntries.size} timesheets (by date+staff)`,
      );

      // Process each grouped timesheet sequentially to avoid race conditions
      const results: any[] = [];
      const groupedArray = Array.from(groupedEntries.entries());
      for (let i = 0; i < groupedArray.length; i++) {
        const [key, group] = groupedArray[i];
        const { date, staffId, entries } = group;

        // Calculate total hours for this group
        const totalHours = entries.reduce(
          (sum: number, entry: any) => sum + (entry.totalHours || 0),
          0,
        );
        const totalBreakHours = entries.reduce(
          (sum: number, entry: any) => sum + (entry.breakHours || 0),
          0,
        );

        // Create work entries array for this timesheet
        const workEntries = entries.map((entry: any) => ({
          entryType: entry.entryType || "work",
          projectId: entry.entryType === "work" ? entry.projectId : undefined,
          taskId: entry.entryType === "work" ? entry.taskId : undefined,
          generalWorkItemId:
            entry.entryType === "general" ? entry.generalWorkItemId : undefined,
          equipment: entry.equipment || [],
          materials: entry.materials || [],
          startTime: entry.startTime,
          endTime: entry.endTime,
          hours: entry.totalHours || 0,
          notes: entry.notes || "",
        }));

        // Send first entry to create/update timesheet, then add remaining entries
        // We'll send them sequentially to ensure proper accumulation
        for (let i = 0; i < entries.length; i++) {
          const entry = entries[i];
          const isFirstEntry = i === 0;

          // Always create timesheet as draft first if it has equipment/materials
          // We'll update status to submitted after adding equipment/materials
          const hasEquipmentOrMaterials =
            (entry.equipment && entry.equipment.length > 0) ||
            (entry.materials && entry.materials.length > 0);

          // When user clicked Submit: use "submitted" for first entry unless we need draft to add equipment/materials first
          const timesheetStatus =
            isFirstEntry
              ? status === "submitted" && !hasEquipmentOrMaterials
                ? "submitted"
                : hasEquipmentOrMaterials
                  ? "draft"
                  : status
              : "draft";

          const timesheetData = {
            staffId: staffId,
            date: date,
            entryType: entry.entryType || "work",
            projectId: entry.entryType === "work" ? entry.projectId : undefined,
            taskId: entry.entryType === "work" ? entry.taskId : undefined,
            generalWorkItemId:
              entry.entryType === "general"
                ? entry.generalWorkItemId
                : undefined,
            equipment: entry.equipment || [],
            materials: entry.materials || [],
            notes: entry.notes,
            workHours: entry.totalHours,
            breakHours: entry.breakHours || 0,
            totalHours: entry.totalHours,
            startTime: entry.startTime,
            endTime: entry.endTime,
            status: timesheetStatus,
          };

          const result = await offlineSync.saveTimesheet(
            timesheetData,
            timesheetStatus,
            token,
          );

          if (!result.success) {
            throw new Error(
              result.error ||
                `Failed to save timesheet for ${date} (staff: ${staffId})`,
            );
          }

          // Create TimesheetEquipment and TimesheetMaterial records after timesheet is saved
          // Get timesheet ID - use from first entry result, or from subsequent entries if timesheet already exists
          let timesheetId: string | null = null;
          if (isFirstEntry && result.data) {
            // First entry - get ID from result
            timesheetId = result.data._id || result.data.id || null;
            if (!timesheetId && result.data.timesheet) {
              timesheetId =
                result.data.timesheet._id || result.data.timesheet.id || null;
            }
          } else {
            // Subsequent entry - fetch the timesheet ID from the date/staff
            try {
              const timesheetLookupResponse = await fetch(
                `${process.env.REACT_APP_API_URL || "http://localhost:5000"}/api/timesheets?date=${date}&staffId=${staffId}&limit=1`,
                {
                  headers: {
                    Authorization: `Bearer ${token}`,
                  },
                },
              );
              if (timesheetLookupResponse.ok) {
                const lookupData = await timesheetLookupResponse.json();
                if (lookupData.data && lookupData.data.length > 0) {
                  timesheetId =
                    lookupData.data[0]._id || lookupData.data[0].id || null;
                }
              }
            } catch (error) {
              console.error("Error looking up timesheet ID:", error);
            }
          }

          // Create equipment and material records if we have a timesheet ID
          if (
            timesheetId &&
            entry.equipment &&
            Array.isArray(entry.equipment) &&
            entry.equipment.length > 0
          ) {
            for (const equipmentId of entry.equipment) {
              const equipmentItem = equipment.find(
                (e: any) => e._id === equipmentId,
              );
              if (equipmentItem) {
                try {
                  const equipmentPayload: any = {
                    equipmentName: equipmentItem.name,
                    equipmentType: equipmentItem.equipmentType,
                    projectId: entry.projectId || undefined,
                    taskId: entry.taskId || undefined,
                    accountCode: equipmentItem.accountCode,
                    taxType: equipmentItem.taxType,
                    notes: "",
                  };

                  // Use work hours from the entry for equipment
                  if (equipmentItem.chargeOutType === "By Hour") {
                    equipmentPayload.hours = entry.totalHours;
                  } else {
                    // For "By Day", calculate days from hours (assuming 8 hours per day)
                    equipmentPayload.days = Math.ceil(entry.totalHours / 8);
                  }

                  const equipmentResponse = await fetch(
                    `${process.env.REACT_APP_API_URL || "http://localhost:5000"}/api/timesheets/${timesheetId}/equipment`,
                    {
                      method: "POST",
                      headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                      },
                      body: JSON.stringify(equipmentPayload),
                    },
                  );

                  if (!equipmentResponse.ok) {
                    const errorText = await equipmentResponse.text();
                    console.error(
                      "Failed to create equipment record:",
                      errorText,
                    );
                  } else {
                    console.log(
                      "✅ Created equipment record:",
                      equipmentItem.name,
                    );
                  }
                } catch (error) {
                  console.error("Error creating equipment record:", error);
                }
              }
            }
          }

          // Materials are now created by the backend when saving the timesheet (with quantity preserved)
          // Note: If timesheet was created as draft due to equipment/materials,
          // user will need to submit/approve it separately for costs to show in project reports

          // Only add result once per timesheet (on first entry)
          if (isFirstEntry) {
            results.push(result);
          }
        }
      }

      console.log(
        `✅ [CREATE TIMESHEET] Successfully ${isOnline ? "saved" : "queued"} timesheets with status '${status}':`,
        results.length,
      );

      // When user clicked Submit: ensure each created timesheet is submitted (covers draft-created due to equipment/materials or any missed status)
      if (status === "submitted" && isOnline && results.length > 0) {
        for (const result of results) {
          const timesheetId =
            result?.data?._id || result?.data?.id || result?.data?.timesheet?._id || result?.data?.timesheet?.id;
          if (timesheetId) {
            try {
              const submitResponse = await fetch(
                `${apiUrl}/api/timesheets/${timesheetId}/submit`,
                {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                  },
                },
              );
              const submitData = await submitResponse.json();
              if (!submitData.success) {
                console.warn(
                  "[CREATE TIMESHEET] Submit follow-up failed for",
                  timesheetId,
                  submitData.message,
                );
              }
            } catch (err) {
              console.warn("[CREATE TIMESHEET] Submit follow-up error:", err);
            }
          }
        }
      }

      // Show appropriate message
      if (!isOnline) {
        const plural = results.length === 1 ? "" : "s";
        setMessage(
          t("offline.savedOffline", { count: results.length, plural }),
        );
      }

      // Clear local work entries
      setLocalWorkEntries([]);

      const savedDates = groupedArray.map(([, g]) => g.date);

      // If online, close immediately. If offline, wait a bit to show message
      setTimeout(
        () => {
          if (isOnline) {
            onSave?.({ dates: savedDates });
            onClose();
          } else {
            // Keep form open when offline so user can see the message
            // User can manually close or it will sync when online
          }
        },
        isOnline ? 100 : 2000,
      );
    } catch (error) {
      const errorMsg =
        error instanceof Error ? error.message : t("offline.saveFailed");
      setMessage(errorMsg);
      console.error(
        `❌ [CREATE TIMESHEET] Error ${status === "draft" ? "saving" : "submitting"} timesheet:`,
        error,
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSaveDraft = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    await saveTimesheets("draft");
  };

  // Calculate submission summary - group by date AND staff
  const calculateSubmissionSummary = () => {
    // Group by date and staff: key = `${date}_${staffId}`
    const dateStaffMap = new Map<
      string,
      {
        date: string;
        staffId: string;
        hours: number;
        entries: Array<{
          startTime: string;
          endTime: string;
          hours: number;
          type?: string;
          staffId?: string;
          project?: any;
          task?: any;
          generalWorkItem?: any;
          equipment?: any[];
          materials?: any[];
          notes?: string;
        }>;
      }
    >();

    // Calculate total hours - sum of all staff hours (each staff member's hours count separately)
    let totalHoursFromEntries = 0;

    localWorkEntries.forEach((entry: any) => {
      const date = entry.date;
      const hours = entry.totalHours || 0;
      const startTime = entry.startTime || "";
      const endTime = entry.endTime || "";
      const entryType = entry.entryType || "work";
      const staffIds = entry.staffIds || (entry.staffId ? [entry.staffId] : []);

      // Count hours for each staff member separately
      // If an entry has multiple staff, multiply hours by staff count for total
      const staffCount = staffIds.length || 1;
      totalHoursFromEntries += hours * staffCount;

      staffIds.forEach((staffId: string) => {
        const key = `${date}_${staffId}`;
        if (!dateStaffMap.has(key)) {
          dateStaffMap.set(key, { date, staffId, hours: 0, entries: [] });
        }

        const dateStaffData = dateStaffMap.get(key)!;
        // Add full hours for this staff member (they worked the full duration)
        dateStaffData.hours += hours;
        dateStaffData.entries.push({
          startTime,
          endTime,
          hours, // Full hours for this staff member
          type: entryType,
          staffId,
          project: entry.project,
          task: entry.task,
          generalWorkItem: entry.generalWorkItem,
          equipment: entry.equipment || [],
          materials: entry.materials || [],
          notes: entry.notes,
        });
      });
    });

    // Convert to array and sort by date, then by staff name
    const dateStaffGroups = Array.from(dateStaffMap.values()).sort((a, b) => {
      const dateCompare = a.date.localeCompare(b.date);
      if (dateCompare !== 0) return dateCompare;
      // If same date, sort by staff name
      const staffA = staffList.find((s: any) => s._id === a.staffId);
      const staffB = staffList.find((s: any) => s._id === b.staffId);
      const nameA = staffA
        ? `${staffA.firstName} ${staffA.lastName}`
        : a.staffId;
      const nameB = staffB
        ? `${staffB.firstName} ${staffB.lastName}`
        : b.staffId;
      return nameA.localeCompare(nameB);
    });

    // Use the total from unique entries, not the sum of all staff hours
    const totalHours = totalHoursFromEntries;

    return {
      dateStaffGroups,
      totalHours,
      totalDates: new Set(dateStaffGroups.map((d) => d.date)).size,
    };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Submit directly without showing summary dialog
    // Users can view summary via the preview button at the bottom
    await saveTimesheets("submitted");
  };

  // Handle close with confirmation if there are unsaved entries
  const handleClose = () => {
    const hasUnsavedEntries =
      localWorkEntries.length > 0 || pendingEntryChanges !== null;

    if (hasUnsavedEntries) {
      const entryCount = localWorkEntries.length;
      const dateCount = new Set(localWorkEntries.map((e: any) => e.date)).size;
      const message =
        entryCount === 1
          ? t("timesheet.confirmCloseSingleEntry")
          : dateCount === 1
            ? t("timesheet.confirmCloseMultipleEntries", { count: entryCount })
            : t("timesheet.confirmCloseMultipleDates", {
                entryCount,
                dateCount,
              });

      setConfirmMessage(message);
      const executeClose = () => {
        setShowConfirmDialog(false);
        setLocalWorkEntries([]);
        setPendingEntryChanges(null);
        onClose();
      };
      setConfirmCallback(() => executeClose);
      setShowConfirmDialog(true);
    } else {
      onClose();
    }
  };

  return (
    <>
      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 rounded-full bg-error-100 dark:bg-gray-700 dark:border dark:border-error-800/50 flex items-center justify-center">
                  <X className="w-5 h-5 text-error-600 dark:text-error-400" />
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                  {t("common.confirmAction")}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  {confirmMessage}
                </p>
                <div className="flex items-center justify-end gap-3">
                  <button
                    onClick={() => {
                      setShowConfirmDialog(false);
                      setConfirmCallback(null);
                      setConfirmMessage("");
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                  >
                    {t("common.cancel")}
                  </button>
                  <button
                    onClick={async () => {
                      if (confirmCallback) {
                        await confirmCallback();
                      }
                      setConfirmCallback(null);
                      setConfirmMessage("");
                    }}
                    className="px-4 py-2 text-sm font-medium text-white bg-error-600 hover:bg-error-700 rounded-lg transition-colors"
                  >
                    {t("common.confirm")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Material Quantity Modal */}
      {showMaterialQuantityModal && pendingMaterial && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {t("timesheet.enterMaterialQuantity") ||
                  "Enter Material Quantity"}
              </h3>
              <button
                onClick={() => {
                  setShowMaterialQuantityModal(false);
                  setPendingMaterial(null);
                  setMaterialQuantity("");
                }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="mb-4">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                <span className="font-medium text-gray-900 dark:text-gray-100">
                  {pendingMaterial.name}
                </span>
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t("timesheet.quantity") || "Quantity"}
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={materialQuantity}
                  onChange={(e) => setMaterialQuantity(e.target.value)}
                  placeholder="0.00"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      const qty = parseFloat(materialQuantity);
                      if (!isNaN(qty) && qty > 0) {
                        setSelectedMaterials([
                          ...selectedMaterials,
                          {
                            _id: pendingMaterial._id,
                            quantity: qty,
                            unit: pendingMaterial.unit,
                            name: pendingMaterial.name,
                          },
                        ]);
                        setShowMaterialQuantityModal(false);
                        setPendingMaterial(null);
                        setMaterialQuantity("");
                      }
                    }
                  }}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t("timesheet.unit") || "Unit"}
                </label>
                <input
                  type="text"
                  value={pendingMaterial.unit || ""}
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-100 dark:bg-gray-600 text-gray-600 dark:text-gray-400 text-sm cursor-not-allowed"
                />
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 mt-6">
              <button
                type="button"
                onClick={() => {
                  setShowMaterialQuantityModal(false);
                  setPendingMaterial(null);
                  setMaterialQuantity("");
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
              >
                {t("common.cancel")}
              </button>
              <button
                type="button"
                onClick={() => {
                  const qty = parseFloat(materialQuantity);
                  if (isNaN(qty) || qty <= 0) {
                    setMessage(
                      t("timesheet.invalidQuantity") ||
                        "Please enter a valid quantity greater than 0",
                    );
                    return;
                  }
                  setSelectedMaterials([
                    ...selectedMaterials,
                    {
                      _id: pendingMaterial._id,
                      quantity: qty,
                      unit: pendingMaterial.unit,
                      name: pendingMaterial.name,
                    },
                  ]);
                  setShowMaterialQuantityModal(false);
                  setPendingMaterial(null);
                  setMaterialQuantity("");
                }}
                className="px-4 py-2 text-sm font-medium text-white bg-accent-600 hover:bg-accent-700 rounded-lg transition-colors"
              >
                {t("common.ok") || "OK"}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl h-[96vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-3">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {showDetails
                    ? t("timesheet.workTimeEntry")
                    : t("timesheet.timesheet")}
                </h2>
              </div>
              <button
                onClick={handleClose}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors flex-shrink-0"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
          </div>

          {isApprovedReadOnly && (
            <div className="px-4 py-2 bg-amber-50 dark:bg-gray-700 dark:border dark:border-amber-800/50 border-b border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-200 text-sm font-medium flex items-center gap-2">
              <Info className="w-4 h-4 flex-shrink-0" />
              {t("timesheet.approvedReadOnly") || "This timesheet is approved and cannot be edited."}
            </div>
          )}

          {/* Form */}
          <form
            onSubmit={handleSubmit}
            className="flex-1 flex flex-col overflow-hidden"
          >
            {!showDetails ? (
              <>
                {/* Pay Period Days Selector */}
                <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-center flex-1">
                      <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        {payPeriodDays.length > 0 && (
                          <span>
                            {formatDateUtil(payPeriodDays[0].date)} -{" "}
                            {formatDateUtil(
                              payPeriodDays[payPeriodDays.length - 1].date,
                            )}
                          </span>
                        )}
                      </h3>
                    </div>
                    {multiDayMode && (
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-purple-600 dark:text-purple-400 font-medium">
                          {selectedDays.size}{" "}
                          {selectedDays.size === 1
                            ? t("common.day") || "day"
                            : t("common.days") || "days"}{" "}
                          {t("common.selected") || "selected"}
                        </span>
                        <button
                          type="button"
                          onClick={exitMultiDayMode}
                          className="px-2 py-1 text-xs font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors"
                        >
                          {t("common.cancel") || "Cancel"}
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {/* Previous Period Button */}
                    <button
                      type="button"
                      onClick={() => {
                        console.log(
                          "📅 [NAVIGATION] Clicked LEFT arrow, current offset:",
                          currentPeriodOffset,
                          "-> new offset:",
                          currentPeriodOffset - 1,
                        );
                        setCurrentPeriodOffset(currentPeriodOffset - 1);
                      }}
                      className="flex-shrink-0 p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                      title="Previous period"
                    >
                      &lt;
                    </button>

                    {/* Days */}
                    <div className="flex gap-1 flex-1">
                      {payPeriodDays.map((day) => {
                        // Calculate hours and staff for this day from localWorkEntries
                        const dayEntries = localWorkEntries.filter(
                          (entry: any) => entry.date === day.dateString,
                        );
                        const dayHours = dayEntries.reduce(
                          (sum: number, entry: any) =>
                            sum + (entry.totalHours || 0),
                          0,
                        );

                        // Get unique staff IDs for this day
                        const dayStaffIds = new Set<string>();
                        dayEntries.forEach((entry: any) => {
                          const staffIds =
                            entry.staffIds ||
                            (entry.staffId ? [entry.staffId] : []);
                          staffIds.forEach((id: string) => dayStaffIds.add(id));
                        });
                        const staffCount = dayStaffIds.size;
                        const isSelected = multiDayMode
                          ? selectedDays.has(day.dateString)
                          : day.dateString === selectedDate;
                        const isInMultiDayMode = multiDayMode;
                        const isLongPressing =
                          multiDayLongPressTimerRef.current &&
                          multiDayLongPressStartRef.current?.dateString ===
                            day.dateString;

                        return (
                          <button
                            key={day.dateString}
                            ref={day.isToday ? todayRef : null}
                            type="button"
                            onClick={(e) => {
                              // Only handle click if not in multi-day mode (multi-day mode uses long press end)
                              if (!multiDayMode) {
                                handleDayClick(day.dateString);
                              }
                            }}
                            onMouseDown={(e) => {
                              if (
                                !showDetails &&
                                !editingEntryData &&
                                !day.hasTimesheet
                              ) {
                                handleDayLongPressStart(e, day.dateString);
                              }
                            }}
                            onMouseUp={(e) => {
                              if (!showDetails && !editingEntryData) {
                                handleDayLongPressEnd(e, day.dateString);
                              }
                            }}
                            onMouseLeave={handleDayLongPressCancel}
                            onTouchStart={(e) => {
                              if (
                                !showDetails &&
                                !editingEntryData &&
                                !day.hasTimesheet
                              ) {
                                handleDayLongPressStart(e, day.dateString);
                              }
                            }}
                            onTouchEnd={(e) => {
                              if (!showDetails && !editingEntryData) {
                                handleDayLongPressEnd(e, day.dateString);
                              }
                            }}
                            onTouchCancel={handleDayLongPressCancel}
                            disabled={false}
                            className={`flex-1 flex flex-col items-center justify-center min-w-0 py-2 px-1 rounded-lg border-2 transition-all relative ${
                              isSelected
                                ? isInMultiDayMode
                                  ? "border-purple-500 bg-purple-100 dark:bg-purple-900/30 ring-2 ring-purple-400 dark:ring-purple-500 shadow-md"
                                  : "border-accent-500 bg-accent-50 dark:bg-gray-700 dark:border dark:border-accent-800/50"
                                : day.hasTimesheet
                                  ? "border-green-300 bg-green-50 dark:bg-gray-700 dark:border dark:border-success-800/50 opacity-50"
                                  : day.isToday
                                    ? "border-primary-300 bg-primary-50 dark:bg-gray-700 dark:border dark:border-primary-800/50"
                                    : "border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500"
                            } ${day.hasTimesheet && !isSelected ? "cursor-not-allowed" : "cursor-pointer"} ${isInMultiDayMode ? "select-none" : ""} ${isLongPressing ? "scale-105 ring-2 ring-purple-300 dark:ring-purple-600" : ""}`}
                          >
                            <span className="text-[9px] text-gray-500 dark:text-gray-400 truncate w-full text-center">
                              {formatDateUtil(day.date)}
                            </span>
                            <span
                              className={`text-sm font-semibold ${
                                day.dateString === selectedDate
                                  ? "text-accent-600 dark:text-accent-400"
                                  : "text-gray-900 dark:text-gray-100"
                              }`}
                            >
                              {day.date.getDate()}
                            </span>
                            {day.isToday && (
                              <span className="text-[8px] font-semibold text-primary-600 dark:text-primary-400">
                                {t("timesheet.today")}
                              </span>
                            )}
                            {day.hasTimesheet && (
                              <div className="w-1 h-1 rounded-full bg-success-500 mt-0.5"></div>
                            )}
                            {dayHours > 0 && (
                              <div className="absolute -top-1 -right-1 flex flex-col gap-0.5 items-end">
                                <span className="bg-accent-600 dark:bg-accent-500 text-white text-[9px] font-semibold px-1 py-0.5 rounded-full min-w-[1.5rem] flex items-center justify-center">
                                  {dayHours.toFixed(2)}h
                                </span>
                                {staffCount > 1 && (
                                  <span className="bg-purple-600 dark:bg-purple-500 text-white text-[8px] font-semibold px-1 py-0.5 rounded-full flex items-center justify-center gap-0.5">
                                    <Users className="w-2.5 h-2.5" />
                                    {staffCount}
                                  </span>
                                )}
                              </div>
                            )}
                          </button>
                        );
                      })}
                    </div>

                    {/* Next Period Button */}
                    <button
                      type="button"
                      onClick={() => {
                        console.log(
                          "📅 [NAVIGATION] Clicked RIGHT arrow, current offset:",
                          currentPeriodOffset,
                          "-> new offset:",
                          currentPeriodOffset + 1,
                        );
                        setCurrentPeriodOffset(currentPeriodOffset + 1);
                      }}
                      className="flex-shrink-0 p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                      title="Next period"
                    >
                      &gt;
                    </button>
                  </div>
                </div>

                {/* Staff with Existing Timesheets Indicator - Only show in creation mode */}
                {!editingTimesheet &&
                  (() => {
                    // Get staff members who have timesheets for the selected date
                    const staffTimesheetPairs = (
                      Array.isArray(dateTimesheets) ? dateTimesheets : []
                    )
                      .map((ts: any) => {
                        const rawId = ts.staffId?._id ?? ts.staffId;
                        const staffId = rawId != null ? String(rawId) : "";
                        const staffMember = staffList.find(
                          (s: any) => s._id != null && String(s._id) === staffId,
                        );
                        return { staffMember, timesheet: ts };
                      })
                      .filter((pair: any) => pair.staffMember);

                    if (staffTimesheetPairs.length === 0) return null;

                    const maxVisible = 8;
                    const visiblePairs = showAllStaffWithTimesheets
                      ? staffTimesheetPairs
                      : staffTimesheetPairs.slice(0, maxVisible);
                    const hasMore = staffTimesheetPairs.length > maxVisible;

                    const handleStaffClick = (pair: any) => {
                      const staffId =
                        typeof pair.staffMember._id === "string"
                          ? pair.staffMember._id
                          : pair.staffMember._id?.toString?.() ||
                            pair.staffMember._id;
                      const timesheetDate = pair.timesheet.date
                        ? new Date(pair.timesheet.date)
                        : new Date();
                      const timesheetId =
                        typeof pair.timesheet._id === "string"
                          ? pair.timesheet._id
                          : pair.timesheet._id?.toString?.() || pair.timesheet._id;
                      onClose?.();
                      setTimeout(() => {
                        window.dispatchEvent(
                          new CustomEvent("openTimesheetForm", {
                            detail: {
                              staffId,
                              date: timesheetDate,
                              timesheetId,
                            },
                          }),
                        );
                      }, 150);
                    };

                    return (
                      <div className="px-4 py-2.5 border-b border-gray-200 dark:border-gray-700 bg-amber-50 dark:bg-gray-700 dark:border dark:border-amber-800/50">
                        <div className="flex items-center gap-2">
                          <div className="flex-shrink-0">
                            <h3 className="text-xs font-semibold text-amber-900 dark:text-amber-200">
                              {t("timesheet.staffWithTimesheetsForDay")}:
                            </h3>
                          </div>
                          <div className="flex items-center gap-1.5 flex-wrap">
                            {visiblePairs.map((pair: any) => {
                              const initials =
                                `${pair.staffMember.firstName?.[0] || ""}${pair.staffMember.lastName?.[0] || ""}`.toUpperCase();
                              return (
                                <button
                                  key={pair.staffMember._id}
                                  onClick={() => handleStaffClick(pair)}
                                  className="w-7 h-7 rounded-full bg-amber-600 dark:bg-amber-700 hover:bg-amber-700 dark:hover:bg-amber-600 flex items-center justify-center text-white text-xs font-semibold transition-colors cursor-pointer border-2 border-amber-800 dark:border-amber-500"
                                  title={`${pair.staffMember.firstName} ${pair.staffMember.lastName} - ${t("timesheet.alreadyHasTimesheet")}`}
                                >
                                  {initials}
                                </button>
                              );
                            })}
                            {hasMore && !showAllStaffWithTimesheets && (
                              <button
                                onClick={() =>
                                  setShowAllStaffWithTimesheets(true)
                                }
                                className="px-2 py-1 text-xs font-medium text-amber-900 dark:text-amber-200 hover:text-amber-700 dark:hover:text-amber-100 transition-colors"
                              >
                                +{staffTimesheetPairs.length - maxVisible} more
                              </button>
                            )}
                            {showAllStaffWithTimesheets && hasMore && (
                              <button
                                onClick={() =>
                                  setShowAllStaffWithTimesheets(false)
                                }
                                className="px-2 py-1 text-xs font-medium text-amber-900 dark:text-amber-200 hover:text-amber-700 dark:hover:text-amber-100 transition-colors"
                              >
                                show less
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                {/* Staff Selection - Only show when creating new timesheets, not when editing */}
                {!editingTimesheet && onStaffChange && (
                  <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Staff *
                      </label>
                      <button
                        type="button"
                        onClick={() => setShowStaffSearch(!showStaffSearch)}
                        className="p-1 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                        title="Search staff"
                      >
                        <Search className="w-4 h-4" />
                      </button>
                    </div>

                    {/* Search input */}
                    {showStaffSearch && (
                      <input
                        type="text"
                        placeholder="Search staff or groups (min 3 chars)..."
                        value={staffSearch}
                        onChange={(e) => setStaffSearch(e.target.value)}
                        className="w-full px-3 py-2 mb-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm"
                      />
                    )}

                    <div
                      className={`flex flex-wrap gap-2 overflow-hidden ${!staffExpanded ? "max-h-[80px]" : ""}`}
                    >
                      {/* Staff Groups */}
                      {filteredStaffGroups.map((group: any) => {
                        const groupStaffIds = group.staffIds
                          .filter((s: any) =>
                            typeof s === "string"
                              ? true
                              : s?.isActive !== false,
                          )
                          .map((s: any) => (typeof s === "string" ? s : s._id));
                        const allSelected = groupStaffIds.every((id: string) =>
                          selectedStaff.includes(id),
                        );
                        const someSelected = groupStaffIds.some((id: string) =>
                          selectedStaff.includes(id),
                        );
                        const groupColor = group.color || "#8b5cf6"; // Default to purple if no color

                        return (
                          <button
                            key={group._id}
                            type="button"
                            onClick={() => handleStaffGroupToggle(group)}
                            className={`inline-flex items-center gap-1 px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                              allSelected
                                ? "bg-white dark:bg-gray-800 shadow-md"
                                : someSelected
                                  ? "bg-white dark:bg-gray-800 shadow-sm"
                                  : "bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500"
                            }`}
                            style={{
                              borderColor:
                                allSelected || someSelected
                                  ? groupColor
                                  : undefined,
                              borderWidth:
                                allSelected || someSelected ? "2px" : undefined,
                            }}
                          >
                            <Users
                              className="w-4 h-4"
                              style={{ color: groupColor }}
                            />
                            <span className="text-gray-900 dark:text-gray-100">
                              {group.name}
                            </span>
                            {allSelected && (
                              <Check
                                className="w-4 h-4"
                                style={{ color: groupColor }}
                              />
                            )}
                          </button>
                        );
                      })}

                      {/* Individual Staff */}
                      {filteredStaff
                        .filter((s: any) => s.isActive)
                        .map((staffMember: any) => {
                          // Check if this is the current user's staff
                          const userStr = localStorage.getItem("user");
                          let isCurrentUserStaff = false;
                          if (userStr) {
                            const user = JSON.parse(userStr);
                            // Try multiple matching strategies to identify current user's staff record
                            isCurrentUserStaff =
                              (staffMember.userId &&
                                user.userId &&
                                staffMember.userId === user.userId) ||
                              (staffMember.email &&
                                user.email &&
                                staffMember.email.toLowerCase() ===
                                  user.email.toLowerCase()) ||
                              (staffMember.firstName &&
                                staffMember.lastName &&
                                user.firstName &&
                                user.lastName &&
                                staffMember.firstName.toLowerCase() ===
                                  user.firstName.toLowerCase() &&
                                staffMember.lastName.toLowerCase() ===
                                  user.lastName.toLowerCase()) ||
                              (staffMember.userId &&
                                user._id &&
                                staffMember.userId === user._id);
                          }

                          // Check if this staff already has a timesheet for the selected date (normalize to string for ObjectId/string comparison)
                          const staffIdStr = staffMember._id != null ? String(staffMember._id) : "";
                          const hasTimesheetForDate =
                            staffIdStr &&
                            dateTimesheets.some((ts: any) => {
                              const tsId = ts.staffId?._id ?? ts.staffId;
                              return tsId != null && String(tsId) === staffIdStr;
                            });

                          // Check if this staff member is part of a selected group
                          const isPartOfSelectedGroup =
                            selectedStaffGroups.some((group) => {
                              const groupStaffIds = group.staffIds
                                .filter((s: any) =>
                                  typeof s === "string"
                                    ? true
                                    : s?.isActive !== false,
                                )
                                .map((s: any) =>
                                  typeof s === "string" ? s : s._id,
                                );
                              return groupStaffIds.includes(staffMember._id);
                            });

                          // Disable if: (1) they already have a timesheet for this day (no exception - prevent duplicate timesheets), OR (2) they're part of a selected group, OR (3) user doesn't have "Add All" permission and this is NOT their own staff record
                          const shouldDisable =
                            hasTimesheetForDate ||
                            isPartOfSelectedGroup ||
                            (!canAddForOthers && !isCurrentUserStaff);
                          const isSelected = selectedStaff.includes(
                            staffMember._id,
                          );

                          return (
                            <button
                              key={staffMember._id}
                              type="button"
                              disabled={shouldDisable}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                // #region agent log
                                fetch('http://127.0.0.1:7242/ingest/cd2d1da9-654c-485a-b113-7fb2dd09c624',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'TimesheetForm.tsx:staffButtonClick',message:'Staff button click',data:{staffId:staffMember._id,staffName:`${staffMember.firstName} ${staffMember.lastName}`,isSelected,isCurrentUserStaff,hasTimesheetForDate,shouldDisable,selectedDate},timestamp:Date.now(),hypothesisId:'H1'})}).catch(()=>{});
                                // #endregion
                                if (shouldDisable) return;
                                if (isSelected) {
                                  // Current user clicking their own (selected) staff: do nothing so they stay on timeline and can pick time like other staff. Don't rely on hasTimesheetForDate — it can be false while dateTimesheets is still loading.
                                  if (isCurrentUserStaff) {
                                    return;
                                  }
                                  // #region agent log
                                  fetch('http://127.0.0.1:7242/ingest/cd2d1da9-654c-485a-b113-7fb2dd09c624',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'TimesheetForm.tsx:staffDeselect',message:'Deselecting staff (branch taken)',data:{staffId:staffMember._id},timestamp:Date.now(),hypothesisId:'H1'})}).catch(()=>{});
                                  // #endregion
                                  onStaffChange(
                                    selectedStaff.filter(
                                      (id) => id !== staffMember._id,
                                    ),
                                  );
                                } else {
                                  onStaffChange([
                                    ...selectedStaff,
                                    staffMember._id,
                                  ]);
                                }
                              }}
                              className={`inline-flex items-center gap-1 px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                                shouldDisable
                                  ? "bg-gray-200 dark:bg-gray-600 text-gray-400 dark:text-gray-500 border border-gray-300 dark:border-gray-500 cursor-not-allowed opacity-60"
                                  : isSelected
                                    ? "bg-accent-500 dark:bg-accent-600 text-white shadow-md"
                                    : "bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 hover:bg-gray-200 dark:hover:bg-gray-600"
                              }`}
                              title={
                                !canAddForOthers && !isCurrentUserStaff
                                  ? "You can only create timesheets for yourself"
                                  : hasTimesheetForDate
                                    ? t(
                                        "timesheet.cannotSelectStaffWithTimesheet",
                                      )
                                    : isPartOfSelectedGroup
                                      ? "Part of selected group"
                                      : ""
                              }
                            >
                              <span>
                                {staffMember.firstName} {staffMember.lastName}
                              </span>
                              {isSelected && <Check className="w-4 h-4" />}
                              {hasTimesheetForDate && (
                                <span className="text-xs ml-1">✓</span>
                              )}
                            </button>
                          );
                        })}
                    </div>

                    {/* View More/Less Button - only show if there are many items */}
                    {filteredStaffGroups.length +
                      filteredStaff.filter((s: any) => s.isActive).length >
                      6 && (
                      <button
                        type="button"
                        onClick={() => setStaffExpanded(!staffExpanded)}
                        className="mt-2 text-sm text-accent-600 dark:text-accent-400 hover:underline"
                      >
                        {staffExpanded ? "View Less" : "View More"}
                      </button>
                    )}
                  </div>
                )}

                {/* Time Block Selector - Day Planner Style */}
                <div
                  className={`relative ${isFullscreen ? "fixed inset-0 z-50 bg-white dark:bg-gray-800 overflow-auto" : "p-4 flex-1 flex flex-col min-h-0"}`}
                >
                  <div
                    className={`flex items-center justify-between mb-3 ${isFullscreen ? "p-4 border-b border-gray-200 dark:border-gray-700" : ""}`}
                  >
                    <div className="flex items-center gap-3">
                      <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        {t("timesheet.workTime")}
                      </h3>
                      <button
                        type="button"
                        onClick={() => setIsFullscreen(!isFullscreen)}
                        disabled={effectiveStaff.length === 0}
                        className="p-1.5 text-gray-500 dark:text-gray-400 hover:text-accent-600 dark:hover:text-accent-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title={
                          isFullscreen
                            ? t("timesheet.exitFullscreen")
                            : t("timesheet.fullscreen")
                        }
                      >
                        {isFullscreen ? (
                          <Minimize2 className="w-4 h-4" />
                        ) : (
                          <Maximize2 className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                    {selectedStartTime && selectedEndTime && (
                      <span className="text-xs font-medium text-accent-600 dark:text-accent-400">
                        {formatTimeDisplay(selectedStartTime)} -{" "}
                        {formatTimeDisplay(selectedEndTime)}(
                        {(parseInt(selectedEndTime.split(":")[0]) * 60 +
                          parseInt(selectedEndTime.split(":")[1]) -
                          (parseInt(selectedStartTime.split(":")[0]) * 60 +
                            parseInt(selectedStartTime.split(":")[1]))) /
                          60}
                        h)
                      </span>
                    )}
                  </div>

                  {/* Disabled overlay when no staff selected (skip when editing - staff comes from timesheet) */}
                  {selectedStaff.length === 0 && !editingTimesheet && (
                    <div className="absolute inset-0 bg-gray-100/80 dark:bg-gray-900/80 backdrop-blur-sm z-10 flex items-center justify-center rounded-lg">
                      <div className="text-center p-6">
                        <Users className="w-12 h-12 mx-auto mb-3 text-gray-400 dark:text-gray-500" />
                        <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          {t("timesheet.selectStaffFirst")}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {t("timesheet.selectStaffFirstMessage")}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Visual Day Timeline */}
                  <div
                    className={`flex gap-3 flex-1 min-h-0 ${isFullscreen ? "p-4" : ""}`}
                  >
                    {/* Hour labels - synced scrollable container */}
                    <div
                      ref={labelsContainerRef}
                      className="overflow-y-auto scrollbar-hide text-[10px] text-gray-600 dark:text-gray-400 h-full"
                      style={{ width: "30px" }}
                    >
                      <div className="relative" style={{ height: "720px" }}>
                        {timeBlocks
                          .filter((time) => time.endsWith(":00"))
                          .map((time, idx) => {
                            const hour = parseInt(time.split(":")[0]);
                            const displayHour =
                              hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
                            const period = hour >= 12 ? "PM" : "AM";
                            const totalHours = 24;
                            const position = (idx / totalHours) * 100;

                            // Check if this is the default work start or end time
                            const isWorkTime =
                              time === workHourStart || time === workHourEnd;

                            return (
                              <div
                                key={time}
                                className={`absolute right-0 text-right whitespace-nowrap ${isWorkTime ? "text-accent-600 dark:text-accent-400 font-semibold" : ""}`}
                                style={{
                                  top: `${position}%`,
                                  transform: "translateY(-50%)",
                                }}
                              >
                                {displayHour} {period}
                              </div>
                            );
                          })}
                      </div>
                    </div>

                    {/* Timeline bar - scrollbar-gutter reserves space so scrollbar doesn't overlay time blocks */}
                    <div
                      ref={timelineRef}
                      className="flex-1 relative border-2 border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 h-full overflow-y-auto"
                      style={{ scrollbarGutter: "stable" }}
                      onMouseDown={handleTimelineMouseDown}
                      onMouseUp={handleMouseUp}
                      onTouchMove={handleTouchMove}
                      onTouchEnd={handleTouchEnd}
                      onScroll={(e) => {
                        // Sync hour labels scroll with timeline scroll
                        if (labelsContainerRef.current) {
                          labelsContainerRef.current.scrollTop =
                            e.currentTarget.scrollTop;
                        }
                      }}
                    >
                      <div className="relative" style={{ minHeight: "720px" }}>
                        {/* Hour grid lines - only show at 30-minute intervals */}
                        <div className="absolute inset-0">
                          {timeBlocks.map((time, idx) => {
                            const totalSlots = timeBlocks.length - 1;
                            const position = (idx / totalSlots) * 100;
                            const isHourMark = time.endsWith(":00");
                            const isHalfHourMark = time.endsWith(":30");

                            // Only render lines at 30-minute intervals
                            if (!isHourMark && !isHalfHourMark) return null;

                            // Check if this is the default work start or end time
                            const isWorkStartTime = time === workHourStart;
                            const isWorkEndTime = time === workHourEnd;
                            const isWorkTimeBoundary =
                              isWorkStartTime || isWorkEndTime;

                            return (
                              <div
                                key={`grid-${time}`}
                                className={`absolute left-0 right-0 ${
                                  isWorkTimeBoundary
                                    ? "border-t-2 border-accent-600 dark:border-blue-400"
                                    : isHourMark
                                      ? "border-t-2 border-gray-300 dark:border-gray-600"
                                      : "border-t border-gray-200 dark:border-gray-700"
                                }`}
                                style={{ top: `${position}%` }}
                              />
                            );
                          })}
                        </div>

                        {/* Selectable time blocks */}
                        <div className="absolute inset-0">
                          {timeBlocks.slice(0, -1).map((time, idx) => {
                            const totalSlots = timeBlocks.length - 1;
                            const blockHeight = 100 / totalSlots;
                            const position = (idx / totalSlots) * 100;
                            const isSelected =
                              isTimeInRange(time) || time === selectedEndTime;
                            const isStart = time === selectedStartTime;
                            const isEnd = time === selectedEndTime;
                            const isDisabled =
                              effectiveStaff.length === 0 || isApprovedReadOnly;

                            return (
                              <div
                                key={time}
                                data-time={time}
                                onMouseDown={
                                  isDisabled
                                    ? undefined
                                    : (e) => handleMouseDown(time, e)
                                }
                                onMouseEnter={
                                  isDisabled
                                    ? undefined
                                    : (e) => handleMouseEnter(time, e)
                                }
                                onTouchStart={
                                  isDisabled
                                    ? undefined
                                    : (e) => {
                                        handleTouchStart(time, e);
                                      }
                                }
                                className={`absolute left-0 right-0 select-none transition-all duration-150 ${
                                  isDisabled
                                    ? "cursor-not-allowed"
                                    : `cursor-pointer ${
                                        isSelected
                                          ? isDragMode
                                            ? "bg-purple-400/30 dark:bg-purple-500/40"
                                            : "bg-blue-400/25 dark:bg-blue-500/35"
                                          : tapSelectionMode && isStart
                                            ? "bg-green-400/30 dark:bg-green-500/40"
                                            : "hover:bg-gray-100/60 dark:hover:bg-gray-700/30"
                                      } ${isDragging || isDragMode ? "cursor-grabbing" : ""}`
                                }`}
                                style={{
                                  top: `${position}%`,
                                  height: `${blockHeight}%`,
                                  touchAction: isDisabled ? "auto" : "none", // Prevent scroll on time blocks for mobile drag
                                }}
                              >
                                {/* Start/End time markers */}
                                {isStart && (
                                  <div
                                    className={`absolute left-0 right-0 top-0 h-0.5 rounded-t ${
                                      tapSelectionMode
                                        ? "bg-green-500 dark:bg-green-400 shadow-sm"
                                        : "bg-blue-500 dark:bg-blue-400 shadow-sm"
                                    }`}
                                  />
                                )}
                                {isEnd && (
                                  <div className="absolute left-0 right-0 bottom-0 h-0.5 bg-blue-500 dark:bg-blue-400 rounded-b shadow-sm" />
                                )}
                                {/* Tap mode indicator - show on start time block */}
                                {tapSelectionMode && isStart && (
                                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                    <div className="bg-success-600 dark:bg-success-500 text-white text-xs px-2 py-1 rounded shadow-lg animate-pulse">
                                      {t("timesheet.tapEndTime")}
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>

                        {/* Local Work Entries Display (not saved yet) */}
                        {localWorkEntries.length > 0 &&
                          localWorkEntries
                            .filter((entry: any) => entry.date === selectedDate)
                            .map((entry: any, idx: number) => {
                              const startTime = entry.startTimeLocal;
                              const endTime = entry.endTimeLocal;

                              const totalSlots = timeBlocks.length - 1;
                              const startIdx = timeBlocks.indexOf(startTime);
                              const endIdx = timeBlocks.indexOf(endTime);

                              if (startIdx === -1 || endIdx === -1) return null;

                              const topPosition = (startIdx / totalSlots) * 100;
                              const bottomPosition =
                                (endIdx / totalSlots) * 100;
                              const height = bottomPosition - topPosition;

                              // Get all staff members for this entry (multi-staff support)
                              const staffIds =
                                entry.staffIds ||
                                (entry.staffId ? [entry.staffId] : []);
                              const staffMembers = staffList.filter((s: any) =>
                                staffIds.includes(s._id),
                              );
                              const entryStaffGroups = entry.staffGroups || [];

                              // Check if this entry is being edited
                              const isEditing =
                                editingWorkEntry?.isLocal &&
                                editingWorkEntry?.entryIndex === idx;
                              const displayEntry = isEditing
                                ? editingWorkEntry.entry
                                : entry;
                              const displayStartDate = new Date(
                                displayEntry.startTime,
                              );
                              const displayEndDate = new Date(
                                displayEntry.endTime,
                              );
                              const displayStartTimeStr = `${String(displayStartDate.getHours()).padStart(2, "0")}:${String(displayStartDate.getMinutes()).padStart(2, "0")}`;
                              const displayEndTimeStr = `${String(displayEndDate.getHours()).padStart(2, "0")}:${String(displayEndDate.getMinutes()).padStart(2, "0")}`;

                              // Recalculate position for display entry if editing
                              let displayTopPosition = topPosition;
                              let displayHeight = height;
                              if (isEditing) {
                                const displayStartIdx =
                                  timeBlocks.indexOf(displayStartTimeStr);
                                const displayEndIdx =
                                  timeBlocks.indexOf(displayEndTimeStr);
                                if (
                                  displayStartIdx !== -1 &&
                                  displayEndIdx !== -1 &&
                                  displayEndIdx > displayStartIdx
                                ) {
                                  displayTopPosition =
                                    (displayStartIdx / totalSlots) * 100;
                                  displayHeight =
                                    (displayEndIdx / totalSlots) * 100 -
                                    displayTopPosition;
                                }
                                // If recalculation fails, keep original position to prevent disappearing
                              }

                              // Determine colors for local entries based on entry type
                              const getLocalEntryColors = () => {
                                if (entry.entryType === "break") {
                                  return "bg-gradient-to-br from-red-50/95 to-red-100/95 dark:from-red-900/30 dark:to-red-800/40 border-l-4 border-red-500 dark:border-red-400";
                                } else if (entry.entryType === "general") {
                                  return "bg-gradient-to-br from-amber-50/95 to-amber-100/95 dark:from-amber-900/30 dark:to-amber-800/40 border-l-4 border-amber-500 dark:border-amber-400";
                                } else if (entry.entryType === "travel") {
                                  return "bg-gradient-to-br from-purple-50/95 to-purple-100/95 dark:from-purple-900/30 dark:to-purple-800/40 border-l-4 border-purple-500 dark:border-purple-400";
                                } else {
                                  return "bg-gradient-to-br from-blue-50/95 to-blue-100/95 dark:from-blue-900/30 dark:to-blue-800/40 border-l-4 border-blue-500 dark:border-blue-400";
                                }
                              };

                              // Calculate if entry is too small to show details
                              const entryHeightPx = (displayHeight / 100) * 720; // Total timeline height
                              const showDetails = entryHeightPx > 60; // Show full details if entry is taller than 60px (about 2 hours)
                              const showTimeOnly = entryHeightPx > 10; // Show at least time if entry is taller than 10px (about 20 mins)

                              return (
                                <div
                                  key={entry.id}
                                  className={`group absolute left-0 right-0 ${getLocalEntryColors()} rounded-lg shadow-sm hover:shadow-xl transition-all duration-200 ${isEditing ? "ring-2 ring-blue-500 dark:ring-blue-400 shadow-lg" : ""} backdrop-blur-sm hover:scale-[1.01] hover:z-30`}
                                  style={{
                                    top: `calc(${displayTopPosition}% + 1px)`,
                                    height: `calc(${displayHeight}% - 2px)`,
                                    zIndex: isEditing ? 20 : 9,
                                  }}
                                >
                                  {/* Hover Tooltip with Full Details */}
                                  <div className="absolute left-full ml-3 top-0 opacity-0 group-hover:opacity-100 pointer-events-none transition-all duration-200 z-50 group-hover:translate-x-1">
                                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 p-3 min-w-[250px] max-w-[350px]">
                                      <div className="space-y-2">
                                        <div className={`text-sm font-bold ${
                                          entry.entryType === "break"
                                            ? "text-red-700 dark:text-red-300"
                                            : entry.entryType === "general"
                                              ? "text-amber-700 dark:text-amber-300"
                                              : entry.entryType === "travel"
                                                ? "text-purple-700 dark:text-purple-300"
                                                : "text-blue-700 dark:text-blue-300"
                                        }`}>
                                          {formatTimeDisplay(displayStartTimeStr)} - {formatTimeDisplay(displayEndTimeStr)}
                                        </div>
                                        {(entryStaffGroups.length > 0 || (staffMembers && staffMembers.length > 0)) && (
                                          <div className="flex flex-wrap items-center gap-1.5 text-xs text-gray-700 dark:text-gray-300">
                                            <User className="w-3.5 h-3.5 opacity-70" />
                                            {entryStaffGroups.length > 0 
                                              ? entryStaffGroups.map((g: any) => g.name).join(", ")
                                              : staffMembers.map((s: any) => `${s.firstName} ${s.lastName}`).join(", ")
                                            }
                                          </div>
                                        )}
                                        {entry.project && (
                                          <div className="flex items-center gap-1.5 text-xs text-gray-700 dark:text-gray-300">
                                            <Folder className="w-3.5 h-3.5 opacity-70" />
                                            {entry.project.projectNumber || entry.project.name}
                                          </div>
                                        )}
                                        {entry.task && (
                                          <div className="flex items-center gap-1.5 text-xs text-gray-700 dark:text-gray-300">
                                            <CheckSquare className="w-3.5 h-3.5 opacity-70" />
                                            {entry.task.title}
                                          </div>
                                        )}
                                        {entry.generalWorkItem && (
                                          <div className="flex items-center gap-1.5 text-xs text-gray-700 dark:text-gray-300">
                                            <ClipboardList className="w-3.5 h-3.5 opacity-70" />
                                            {entry.generalWorkItem.name}
                                          </div>
                                        )}
                                        {entry.entryType === "break" && (
                                          <div className="flex items-center gap-1.5 text-xs text-red-700 dark:text-red-300">
                                            <Coffee className="w-3.5 h-3.5" />
                                            Break
                                          </div>
                                        )}
                                        {entry.entryType === "travel" && (
                                          <div className="flex items-center gap-1.5 text-xs text-purple-700 dark:text-purple-300">
                                            <Car className="w-3.5 h-3.5" />
                                            Travel
                                          </div>
                                        )}
                                        {entry.equipment?.length > 0 && (
                                          <div className="border-t border-gray-200 dark:border-gray-700 pt-2 mt-1">
                                            <div className="text-[10px] font-semibold text-gray-600 dark:text-gray-400 mb-1">Equipment:</div>
                                            {entry.equipment.map((eq: any) => (
                                              <div key={eq._id} className="flex items-center gap-1 text-xs text-gray-700 dark:text-gray-300">
                                                <Wrench className="w-3 h-3 opacity-70" />
                                                {eq.name}
                                              </div>
                                            ))}
                                          </div>
                                        )}
                                        {entry.materials?.length > 0 && (
                                          <div className="border-t border-gray-200 dark:border-gray-700 pt-2 mt-1">
                                            <div className="text-[10px] font-semibold text-gray-600 dark:text-gray-400 mb-1">Materials:</div>
                                            {entry.materials.map((mat: any) => (
                                              <div key={mat._id} className="flex items-center gap-1 text-xs text-gray-700 dark:text-gray-300">
                                                <Package className="w-3 h-3 opacity-70" />
                                                {mat.description}
                                              </div>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                  {/* Resize handles for local entries */}
                                  <>
                                    <div
                                      className={`absolute top-0 left-0 right-0 h-1 rounded-t-lg transition-all z-10 pointer-events-auto opacity-0 hover:opacity-100 ${
                                        entry.entryType === "break"
                                          ? "bg-red-500 dark:bg-red-400"
                                          : entry.entryType === "general"
                                            ? "bg-amber-500 dark:bg-amber-400"
                                            : entry.entryType === "travel"
                                              ? "bg-purple-500 dark:bg-purple-400"
                                              : "bg-blue-500 dark:bg-blue-400"
                                      }`}
                                      onMouseDown={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        handleLocalEntryMouseDown(
                                          e,
                                          entry,
                                          idx,
                                          "resize-start",
                                        );
                                      }}
                                      onClick={(e) => {
                                        // Prevent clicks immediately after dragging
                                        if (justFinishedDragRef.current) {
                                          e.stopPropagation();
                                          e.preventDefault();
                                          return;
                                        }
                                      }}
                                      onDoubleClick={(e) => {
                                        e.stopPropagation();
                                        // Prevent double-click if we just finished dragging
                                        if (justFinishedDragRef.current) {
                                          return;
                                        }
                                        handleEntryDoubleClick(
                                          entry,
                                          undefined,
                                          true,
                                        );
                                      }}
                                      style={{ cursor: "ns-resize" }}
                                      title="Drag to adjust start time or double-click to edit"
                                    />
                                    <div
                                      className={`absolute bottom-0 left-0 right-0 h-1 rounded-b-lg transition-all z-10 pointer-events-auto opacity-0 hover:opacity-100 ${
                                        entry.entryType === "break"
                                          ? "bg-red-500 dark:bg-red-400"
                                          : entry.entryType === "general"
                                            ? "bg-amber-500 dark:bg-amber-400"
                                            : entry.entryType === "travel"
                                              ? "bg-purple-500 dark:bg-purple-400"
                                              : "bg-blue-500 dark:bg-blue-400"
                                      }`}
                                      onMouseDown={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        handleLocalEntryMouseDown(
                                          e,
                                          entry,
                                          idx,
                                          "resize-end",
                                        );
                                      }}
                                      onClick={(e) => {
                                        // Prevent clicks immediately after dragging
                                        if (justFinishedDragRef.current) {
                                          e.stopPropagation();
                                          e.preventDefault();
                                          return;
                                        }
                                      }}
                                      onDoubleClick={(e) => {
                                        e.stopPropagation();
                                        // Prevent double-click if we just finished dragging
                                        if (justFinishedDragRef.current) {
                                          return;
                                        }
                                        handleEntryDoubleClick(
                                          entry,
                                          undefined,
                                          true,
                                        );
                                      }}
                                      style={{ cursor: "ns-resize" }}
                                      title="Drag to adjust end time or double-click to edit"
                                    />
                                  </>
                                  {/* Edit and Delete buttons for local entries */}
                                  <div
                                    className="absolute top-2 right-2 flex gap-1.5 z-20 opacity-0 group-hover:opacity-100 transition-opacity"
                                    data-entry-button
                                  >
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        handleEntryDoubleClick(
                                          entry,
                                          undefined,
                                          true,
                                        );
                                      }}
                                      onMouseUp={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                      }}
                                      className="p-1.5 bg-white/90 dark:bg-gray-800/90 hover:bg-white dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-md shadow-sm hover:shadow-md transition-all pointer-events-auto backdrop-blur-sm border border-gray-200 dark:border-gray-600"
                                      title="Edit entry"
                                    >
                                      <Edit className="w-3.5 h-3.5" />
                                    </button>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        handleDeleteLocalEntry(idx);
                                      }}
                                      onMouseUp={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                      }}
                                      className="p-1.5 bg-white/90 dark:bg-gray-800/90 hover:bg-red-50 dark:hover:bg-red-900/30 text-gray-700 dark:text-gray-200 hover:text-red-600 dark:hover:text-red-400 rounded-md shadow-sm hover:shadow-md transition-all pointer-events-auto backdrop-blur-sm border border-gray-200 dark:border-gray-600"
                                      title="Remove entry"
                                    >
                                      <X className="w-3.5 h-3.5" />
                                    </button>
                                  </div>
                                  <div className="absolute inset-0 px-2 py-1 overflow-hidden pointer-events-none flex flex-col justify-center gap-0.5">
                                    {showTimeOnly && (
                                      <div
                                        className={`${showDetails ? 'text-xs' : 'text-[11px]'} font-bold leading-tight truncate ${
                                          entry.entryType === "break"
                                            ? "text-red-800 dark:text-red-200"
                                            : entry.entryType === "general"
                                              ? "text-amber-800 dark:text-amber-200"
                                              : entry.entryType === "travel"
                                                ? "text-purple-800 dark:text-purple-200"
                                                : "text-blue-800 dark:text-blue-200"
                                        }`}
                                      >
                                        {formatTimeDisplay(displayStartTimeStr)} - {formatTimeDisplay(displayEndTimeStr)}
                                      </div>
                                    )}
                                    {showDetails && (
                                      <div className="flex flex-col gap-1">
                                        {/* Row 1: Staff groups or individual staff members */}
                                        {(entryStaffGroups.length > 0 ||
                                          (staffMembers &&
                                            staffMembers.length > 0)) && (
                                          <div className="flex flex-wrap items-center gap-1">
                                            {/* Show groups if present */}
                                            {entryStaffGroups.length > 0 &&
                                              entryStaffGroups.map(
                                                (group: any) => (
                                                  <span
                                                    key={group._id}
                                                    className="flex items-center gap-1 text-[10px] whitespace-nowrap font-medium text-gray-800 dark:text-gray-100"
                                                  >
                                                    <Users className="w-2.5 h-2.5 opacity-60" />{" "}
                                                    {group.name}
                                                  </span>
                                                ),
                                              )}
                                            {/* Show individual staff only if no groups are selected */}
                                            {entryStaffGroups.length === 0 &&
                                              staffMembers.map((staff: any) => (
                                                <span
                                                  key={staff._id}
                                                  className="flex items-center gap-1 text-[10px] whitespace-nowrap font-medium text-gray-800 dark:text-gray-100"
                                                >
                                                  <User className="w-2.5 h-2.5 opacity-60" />{" "}
                                                  {staff.firstName}{" "}
                                                  {staff.lastName}
                                                </span>
                                              ))}
                                          </div>
                                        )}

                                        {/* Row 2: Entry type with project/task info */}
                                        <div className="flex flex-wrap items-center gap-1.5">
                                          {(!entry.entryType ||
                                            entry.entryType === "work") &&
                                            entry.project && (
                                              <span className="flex items-center gap-1 text-[10px] text-gray-700 dark:text-gray-200 whitespace-nowrap truncate font-medium">
                                                <Folder className="w-2.5 h-2.5 opacity-60 flex-shrink-0" />
                                                {entry.project.projectNumber ||
                                                  entry.project.name}
                                              </span>
                                            )}
                                          {(!entry.entryType ||
                                            entry.entryType === "work") &&
                                            entry.task && (
                                              <span className="flex items-center gap-1 text-[10px] text-gray-700 dark:text-gray-200 whitespace-nowrap truncate">
                                                <CheckSquare className="w-2.5 h-2.5 opacity-60 flex-shrink-0" />
                                                {entry.task.title}
                                              </span>
                                            )}
                                          {entry.entryType === "general" &&
                                            entry.generalWorkItem && (
                                              <span className="flex items-center gap-1 text-[10px] text-gray-700 dark:text-gray-200 whitespace-nowrap truncate">
                                                <ClipboardList className="w-2.5 h-2.5 opacity-60 flex-shrink-0" />
                                                {entry.generalWorkItem.name}
                                              </span>
                                            )}
                                          {entry.entryType === "break" && (
                                            <span className="flex items-center gap-1 text-[10px] text-gray-700 dark:text-gray-200 whitespace-nowrap">
                                              <Coffee className="w-2.5 h-2.5 opacity-60" />{" "}
                                              Break
                                            </span>
                                          )}
                                          {entry.entryType === "travel" && (
                                            <span className="flex items-center gap-1 text-[10px] text-gray-700 dark:text-gray-200 whitespace-nowrap">
                                              <Car className="w-2.5 h-2.5 opacity-60" /> Travel
                                            </span>
                                          )}
                                        </div>

                                        {/* Row 3: Equipment */}
                                        {entry.equipment?.length > 0 && (
                                          <div className="flex flex-col gap-0.5">
                                            {entry.equipment.map((eq: any) => (
                                              <span
                                                key={eq._id}
                                                className={`flex items-center gap-1 text-[9px] ${
                                                  entry.entryType === "break"
                                                    ? "text-error-700 dark:text-error-300"
                                                    : entry.entryType ===
                                                        "general"
                                                      ? "text-warning-700 dark:text-warning-300"
                                                      : entry.entryType ===
                                                          "travel"
                                                        ? "text-purple-700 dark:text-purple-300"
                                                        : "text-accent-700 dark:text-accent-300"
                                                }`}
                                              >
                                                <Wrench className="w-2.5 h-2.5 flex-shrink-0" />{" "}
                                                {eq.name}
                                              </span>
                                            ))}
                                          </div>
                                        )}

                                        {/* Row 4: Materials */}
                                        {entry.materials?.length > 0 && (
                                          <div className="flex flex-col gap-0.5">
                                            {entry.materials.map((mat: any) => (
                                              <span
                                                key={mat._id}
                                                className={`flex items-center gap-1 text-[9px] ${
                                                  entry.entryType === "break"
                                                    ? "text-error-700 dark:text-error-300"
                                                    : entry.entryType ===
                                                        "general"
                                                      ? "text-warning-700 dark:text-warning-300"
                                                      : entry.entryType ===
                                                          "travel"
                                                        ? "text-purple-700 dark:text-purple-300"
                                                        : "text-accent-700 dark:text-accent-300"
                                                }`}
                                              >
                                                <Package className="w-2.5 h-2.5 flex-shrink-0" />{" "}
                                                {mat.name}
                                              </span>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })}

                        {/* Existing Timesheets Display - showing all work entries */}
                        {(() => {
                          console.log(
                            "🎨 [DISPLAY] Rendering timesheets, count:",
                            Array.isArray(dateTimesheets)
                              ? dateTimesheets.length
                              : 0,
                          );
                          console.log(
                            "🎨 [DISPLAY] Local work entries:",
                            localWorkEntries.length,
                          );
                          console.log(
                            "🎨 [DISPLAY] dateTimesheets:",
                            dateTimesheets,
                          );
                          // Filter to only show the editing timesheet if editing
                          // In creation mode (no editingTimesheet), don't show any existing timesheets
                          const timesheetsToDisplay = editingTimesheet
                            ? Array.isArray(dateTimesheets)
                              ? dateTimesheets.filter(
                                  (ts: any) => ts._id === editingTimesheet._id,
                                )
                              : []
                            : [];

                          return timesheetsToDisplay.flatMap(
                            (timesheet: any) => {
                              const staffMember =
                                typeof timesheet.staffId === "object"
                                  ? timesheet.staffId
                                  : null;

                              // Display workEntries if they exist (new structure)
                              // When workEntries is explicitly [] (empty), show nothing - don't fall back to legacy
                              const entries =
                                timesheet.workEntries &&
                                timesheet.workEntries.length > 0
                                  ? timesheet.workEntries
                                  : Array.isArray(timesheet.workEntries)
                                    ? []
                                    : [timesheet]; // Fallback to legacy only when workEntries doesn't exist

                              console.log("🎨 [DISPLAY] Timesheet:", {
                                id: timesheet._id,
                                date: timesheet.date,
                                workEntriesCount:
                                  timesheet.workEntries?.length || 0,
                                entriesCount: entries.length,
                                entries: entries.map((e: any) => ({
                                  startTime: e.startTime,
                                  endTime: e.endTime,
                                  hasStartTime: !!e.startTime,
                                  hasEndTime: !!e.endTime,
                                })),
                              });

                              return entries.map((entry: any, idx: number) => {
                                if (!entry.startTime || !entry.endTime) {
                                  console.warn(
                                    "⚠️ [DISPLAY] Entry skipped - missing startTime or endTime:",
                                    {
                                      entry,
                                      hasStartTime: !!entry.startTime,
                                      hasEndTime: !!entry.endTime,
                                    },
                                  );
                                  return null;
                                }

                                const startDate = new Date(entry.startTime);
                                const endDate = new Date(entry.endTime);
                                const startTime = `${String(startDate.getHours()).padStart(2, "0")}:${String(startDate.getMinutes()).padStart(2, "0")}`;
                                const endTime = `${String(endDate.getHours()).padStart(2, "0")}:${String(endDate.getMinutes()).padStart(2, "0")}`;

                                const totalSlots = timeBlocks.length - 1;
                                const startIdx = timeBlocks.indexOf(startTime);
                                const endIdx = timeBlocks.indexOf(endTime);

                                if (startIdx === -1 || endIdx === -1) {
                                  console.warn(
                                    "⚠️ [DISPLAY] Entry skipped - time not found in timeBlocks:",
                                    {
                                      startTime,
                                      endTime,
                                      startIdx,
                                      endIdx,
                                      timeBlocksSample: timeBlocks.slice(0, 10),
                                    },
                                  );
                                  return null;
                                }

                                console.log("✅ [DISPLAY] Rendering entry:", {
                                  startTime,
                                  endTime,
                                  startIdx,
                                  endIdx,
                                  timesheetId: timesheet._id,
                                });

                                const topPosition =
                                  (startIdx / totalSlots) * 100;
                                const bottomPosition =
                                  (endIdx / totalSlots) * 100;
                                const height = bottomPosition - topPosition;

                                const project =
                                  typeof entry.projectId === "object"
                                    ? entry.projectId
                                    : null;
                                const task =
                                  typeof entry.taskId === "object"
                                    ? entry.taskId
                                    : null;
                                const generalWorkItem =
                                  typeof entry.generalWorkItemId === "object"
                                    ? entry.generalWorkItemId
                                    : null;
                                const entryTypeValue =
                                  entry.entryType || "work";

                                // Check if this entry is being edited or has pending changes
                                const isEditing =
                                  editingWorkEntry?.timesheet?._id ===
                                    timesheet._id &&
                                  editingWorkEntry?.entryIndex === idx;
                                const hasPendingChanges =
                                  pendingEntryChanges?.timesheetId ===
                                    timesheet._id &&
                                  pendingEntryChanges?.entryIndex === idx;
                                const isDraft = timesheet.status === "draft";

                                // Use edited times if available, or pending changes, or original entry
                                let displayEntry = entry;
                                if (isEditing && editingWorkEntry?.entry) {
                                  displayEntry = editingWorkEntry.entry;
                                } else if (hasPendingChanges) {
                                  // Use pending changes for display
                                  displayEntry = {
                                    ...entry,
                                    startTime: pendingEntryChanges.startTime,
                                    endTime: pendingEntryChanges.endTime,
                                    hours: pendingEntryChanges.hours,
                                  };
                                }

                                const displayStartDate = new Date(
                                  displayEntry.startTime,
                                );
                                const displayEndDate = new Date(
                                  displayEntry.endTime,
                                );
                                const displayStartTimeStr = `${String(displayStartDate.getHours()).padStart(2, "0")}:${String(displayStartDate.getMinutes()).padStart(2, "0")}`;
                                const displayEndTimeStr = `${String(displayEndDate.getHours()).padStart(2, "0")}:${String(displayEndDate.getMinutes()).padStart(2, "0")}`;

                                // Recalculate position if editing or has pending changes
                                let displayTopPosition = topPosition;
                                let displayHeight = height;
                                if (isEditing || hasPendingChanges) {
                                  const displayStartIdx =
                                    timeBlocks.indexOf(displayStartTimeStr);
                                  const displayEndIdx =
                                    timeBlocks.indexOf(displayEndTimeStr);
                                  if (
                                    displayStartIdx !== -1 &&
                                    displayEndIdx !== -1 &&
                                    displayEndIdx > displayStartIdx
                                  ) {
                                    displayTopPosition =
                                      (displayStartIdx / totalSlots) * 100;
                                    displayHeight =
                                      (displayEndIdx / totalSlots) * 100 -
                                      displayTopPosition;
                                  }
                                  // If recalculation fails, keep original position to prevent disappearing
                                }

                                // Determine colors based on entry type
                                const getEntryColors = () => {
                                  if (entryTypeValue === "break") {
                                    return "bg-gradient-to-br from-red-50/95 to-red-100/95 dark:from-red-900/30 dark:to-red-800/40 border-l-4 border-red-500 dark:border-red-400";
                                  } else if (entryTypeValue === "general") {
                                    return "bg-gradient-to-br from-amber-50/95 to-amber-100/95 dark:from-amber-900/30 dark:to-amber-800/40 border-l-4 border-amber-500 dark:border-amber-400";
                                  } else if (entryTypeValue === "travel") {
                                    return "bg-gradient-to-br from-purple-50/95 to-purple-100/95 dark:from-purple-900/30 dark:to-purple-800/40 border-l-4 border-purple-500 dark:border-purple-400";
                                  } else {
                                    return "bg-gradient-to-br from-green-50/95 to-green-100/95 dark:from-green-900/30 dark:to-green-800/40 border-l-4 border-green-500 dark:border-green-400";
                                  }
                                };

                                // Calculate if entry is too small to show details
                                const entryHeightPx =
                                  (displayHeight / 100) * 720; // Total timeline height
                                const showDetails = entryHeightPx > 60; // Show full details if entry is taller than 60px (about 2 hours)
                                const showTimeOnly = entryHeightPx > 10; // Show at least time if entry is taller than 10px (about 20 mins)

                                return (
                                  <div
                                    key={`${timesheet._id}-${idx}`}
                                    className={`group absolute left-0 right-0 ${getEntryColors()} rounded-lg shadow-sm hover:shadow-xl transition-all duration-200 ${isEditing ? "ring-2 ring-blue-500 dark:ring-blue-400 shadow-lg" : hasPendingChanges ? "ring-2 ring-yellow-500 dark:ring-yellow-400" : ""} backdrop-blur-sm hover:scale-[1.01] hover:z-30`}
                                    style={{
                                      top: `calc(${displayTopPosition}% + 1px)`,
                                      height: `calc(${displayHeight}% - 2px)`,
                                      zIndex: isEditing
                                        ? 20
                                        : hasPendingChanges
                                          ? 15
                                          : 10,
                                    }}
                                  >
                                    {/* Hover Tooltip with Full Details */}
                                    <div className="absolute left-full ml-3 top-0 opacity-0 group-hover:opacity-100 pointer-events-none transition-all duration-200 z-50 group-hover:translate-x-1">
                                      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 p-3 min-w-[250px] max-w-[350px]">
                                        <div className="space-y-2">
                                          <div className={`text-sm font-bold ${
                                            entryTypeValue === "break"
                                              ? "text-red-700 dark:text-red-300"
                                              : entryTypeValue === "general"
                                                ? "text-amber-700 dark:text-amber-300"
                                                : entryTypeValue === "travel"
                                                  ? "text-purple-700 dark:text-purple-300"
                                                  : "text-green-700 dark:text-green-300"
                                          }`}>
                                            {formatTimeDisplay(displayStartTimeStr)} - {formatTimeDisplay(displayEndTimeStr)}
                                          </div>
                                          {staffMember && (
                                            <div className="flex items-center gap-1.5 text-xs text-gray-700 dark:text-gray-300">
                                              <User className="w-3.5 h-3.5 opacity-70" />
                                              {staffMember.firstName} {staffMember.lastName}
                                            </div>
                                          )}
                                          {isSystemAdmin && timesheet.accountId && (
                                            <div className="flex items-center gap-1.5 text-xs text-gray-700 dark:text-gray-300">
                                              <Building2 className="w-3.5 h-3.5 opacity-70" />
                                              {typeof timesheet.accountId === "object" ? timesheet.accountId.companyName : "Unknown Account"}
                                            </div>
                                          )}
                                          {project && (
                                            <div className="flex items-center gap-1.5 text-xs text-gray-700 dark:text-gray-300">
                                              <Folder className="w-3.5 h-3.5 opacity-70" />
                                              {project.projectNumber || project.name}
                                            </div>
                                          )}
                                          {task && (
                                            <div className="flex items-center gap-1.5 text-xs text-gray-700 dark:text-gray-300">
                                              <CheckSquare className="w-3.5 h-3.5 opacity-70" />
                                              {task.title}
                                            </div>
                                          )}
                                          {generalWorkItem && (
                                            <div className="flex items-center gap-1.5 text-xs text-gray-700 dark:text-gray-300">
                                              <ClipboardList className="w-3.5 h-3.5 opacity-70" />
                                              {generalWorkItem.name}
                                            </div>
                                          )}
                                          {entryTypeValue === "break" && (
                                            <div className="flex items-center gap-1.5 text-xs text-red-700 dark:text-red-300">
                                              <Coffee className="w-3.5 h-3.5" />
                                              Break
                                            </div>
                                          )}
                                          {entryTypeValue === "travel" && (
                                            <div className="flex items-center gap-1.5 text-xs text-purple-700 dark:text-purple-300">
                                              <Car className="w-3.5 h-3.5" />
                                              Travel
                                            </div>
                                          )}
                                          {entry.equipment?.length > 0 && (
                                            <div className="border-t border-gray-200 dark:border-gray-700 pt-2 mt-1">
                                              <div className="text-[10px] font-semibold text-gray-600 dark:text-gray-400 mb-1">Equipment:</div>
                                              {entry.equipment.map((eq: any) => (
                                                <div key={eq._id || eq.name} className="flex items-center gap-1 text-xs text-gray-700 dark:text-gray-300">
                                                  <Wrench className="w-3 h-3 opacity-70" />
                                                  {eq.name}
                                                </div>
                                              ))}
                                            </div>
                                          )}
                                          {entry.materials?.length > 0 && (
                                            <div className="border-t border-gray-200 dark:border-gray-700 pt-2 mt-1">
                                              <div className="text-[10px] font-semibold text-gray-600 dark:text-gray-400 mb-1">Materials:</div>
                                              {entry.materials.map((mat: any) => (
                                                <div key={mat._id || mat.description} className="flex items-center gap-1 text-xs text-gray-700 dark:text-gray-300">
                                                  <Package className="w-3 h-3 opacity-70" />
                                                  {mat.description}
                                                </div>
                                              ))}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                    {/* Resize handles for all timesheets */}
                                    <>
                                      <div
                                        className={`absolute top-0 left-0 right-0 h-1 rounded-t-lg transition-all z-10 pointer-events-auto opacity-0 hover:opacity-100 ${
                                          entryTypeValue === "break"
                                            ? "bg-red-500 dark:bg-red-400"
                                            : entryTypeValue === "general"
                                              ? "bg-amber-500 dark:bg-amber-400"
                                              : entryTypeValue === "travel"
                                                ? "bg-purple-500 dark:bg-purple-400"
                                                : "bg-green-500 dark:bg-green-400"
                                        }`}
                                        onMouseDown={(e) => {
                                          e.stopPropagation();
                                          e.preventDefault();
                                          handleEntryMouseDown(
                                            e,
                                            entry,
                                            timesheet,
                                            idx,
                                            "resize-start",
                                          );
                                        }}
                                        onClick={(e) => {
                                          // Prevent clicks immediately after dragging
                                          if (justFinishedDragRef.current) {
                                            e.stopPropagation();
                                            e.preventDefault();
                                            return;
                                          }
                                        }}
                                        onDoubleClick={(e) => {
                                          e.stopPropagation();
                                          // Prevent double-click if we just finished dragging
                                          if (justFinishedDragRef.current) {
                                            return;
                                          }
                                          handleEntryDoubleClick(
                                            entry,
                                            timesheet,
                                          );
                                        }}
                                        style={{ cursor: "ns-resize" }}
                                        title="Drag to adjust start time or double-click to edit"
                                      />
                                      <div
                                        className={`absolute bottom-0 left-0 right-0 h-1 rounded-b-lg transition-all z-10 pointer-events-auto opacity-0 hover:opacity-100 ${
                                          entryTypeValue === "break"
                                            ? "bg-red-500 dark:bg-red-400"
                                            : entryTypeValue === "general"
                                              ? "bg-amber-500 dark:bg-amber-400"
                                              : entryTypeValue === "travel"
                                                ? "bg-purple-500 dark:bg-purple-400"
                                                : "bg-green-500 dark:bg-green-400"
                                        }`}
                                        onMouseDown={(e) => {
                                          e.stopPropagation();
                                          e.preventDefault();
                                          handleEntryMouseDown(
                                            e,
                                            entry,
                                            timesheet,
                                            idx,
                                            "resize-end",
                                          );
                                        }}
                                        onClick={(e) => {
                                          // Prevent clicks immediately after dragging
                                          if (justFinishedDragRef.current) {
                                            e.stopPropagation();
                                            e.preventDefault();
                                            return;
                                          }
                                        }}
                                        onDoubleClick={(e) => {
                                          e.stopPropagation();
                                          // Prevent double-click if we just finished dragging
                                          if (justFinishedDragRef.current) {
                                            return;
                                          }
                                          handleEntryDoubleClick(
                                            entry,
                                            timesheet,
                                          );
                                        }}
                                        style={{ cursor: "ns-resize" }}
                                        title="Drag to adjust end time or double-click to edit"
                                      />
                                    </>
                                    {/* Edit and Delete buttons */}
                                    <div
                                      className="absolute top-2 right-2 flex gap-1.5 z-20 opacity-0 group-hover:opacity-100 transition-opacity"
                                      data-entry-button
                                    >
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          e.preventDefault();
                                          handleEntryDoubleClick(
                                            entry,
                                            timesheet,
                                          );
                                        }}
                                        onMouseUp={(e) => {
                                          e.stopPropagation();
                                          e.preventDefault();
                                        }}
                                        className="p-1.5 bg-white/90 dark:bg-gray-800/90 hover:bg-white dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-md shadow-sm hover:shadow-md transition-all pointer-events-auto backdrop-blur-sm border border-gray-200 dark:border-gray-600"
                                        title="Edit entry"
                                      >
                                        <Edit className="w-3.5 h-3.5" />
                                      </button>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          e.preventDefault();
                                          handleDeleteEntry(
                                            entry,
                                            timesheet,
                                            idx,
                                          );
                                        }}
                                        onMouseUp={(e) => {
                                          e.stopPropagation();
                                          e.preventDefault();
                                        }}
                                        className="p-1.5 bg-white/90 dark:bg-gray-800/90 hover:bg-red-50 dark:hover:bg-red-900/30 text-gray-700 dark:text-gray-200 hover:text-red-600 dark:hover:text-red-400 rounded-md shadow-sm hover:shadow-md transition-all pointer-events-auto backdrop-blur-sm border border-gray-200 dark:border-gray-600"
                                        title="Delete entry"
                                      >
                                        <X className="w-3.5 h-3.5" />
                                      </button>
                                    </div>
                                    <div className="absolute inset-0 px-2 py-1 overflow-hidden pointer-events-none flex flex-col justify-center gap-0.5">
                                      {showTimeOnly && (
                                        <div
                                          className={`${showDetails ? 'text-xs' : 'text-[11px]'} font-bold leading-tight truncate ${
                                            entryTypeValue === "break"
                                              ? "text-red-800 dark:text-red-200"
                                              : entryTypeValue === "general"
                                                ? "text-amber-800 dark:text-amber-200"
                                                : entryTypeValue === "travel"
                                                  ? "text-purple-800 dark:text-purple-200"
                                                  : "text-green-800 dark:text-green-200"
                                          }`}
                                        >
                                          {formatTimeDisplay(displayStartTimeStr)} - {formatTimeDisplay(displayEndTimeStr)}
                                        </div>
                                      )}
                                      {showDetails && (
                                        <div className="flex flex-col gap-1">
                                          {/* Row 1: Staff members and Account */}
                                          {staffMember && (
                                            <div className="flex flex-col gap-0.5">
                                              <span
                                                className="flex items-center gap-1 text-[10px] whitespace-nowrap font-medium text-gray-800 dark:text-gray-100"
                                              >
                                                <User className="w-2.5 h-2.5 opacity-60" />{" "}
                                                {staffMember.firstName}{" "}
                                                {staffMember.lastName}
                                              </span>
                                              {isSystemAdmin &&
                                                timesheet.accountId && (
                                                  <span
                                                    className="flex items-center gap-1 text-[10px] whitespace-nowrap font-medium text-gray-700 dark:text-gray-200"
                                                  >
                                                    <Building2 className="w-2.5 h-2.5 opacity-60" />
                                                    {typeof timesheet.accountId ===
                                                    "object"
                                                      ? timesheet.accountId
                                                          .companyName
                                                      : "Unknown Account"}
                                                  </span>
                                                )}
                                            </div>
                                          )}

                                          {/* Row 2: Entry type with project/task info */}
                                          <div className="flex flex-wrap items-center gap-1.5">
                                            {entryTypeValue === "work" &&
                                              project && (
                                                <span className="flex items-center gap-1 text-[10px] text-gray-700 dark:text-gray-200 whitespace-nowrap truncate font-medium">
                                                  <Folder className="w-2.5 h-2.5 opacity-60 flex-shrink-0" />
                                                  {project.projectNumber ||
                                                    project.name}
                                                </span>
                                              )}
                                            {entryTypeValue === "work" &&
                                              task && (
                                                <span className="flex items-center gap-1 text-[10px] text-gray-700 dark:text-gray-200 whitespace-nowrap truncate">
                                                  <CheckSquare className="w-2.5 h-2.5 opacity-60 flex-shrink-0" />
                                                  {task.title}
                                                </span>
                                              )}
                                            {entryTypeValue === "general" &&
                                              generalWorkItem && (
                                                <span className="flex items-center gap-1 text-[10px] text-gray-700 dark:text-gray-200 whitespace-nowrap truncate">
                                                  <ClipboardList className="w-2.5 h-2.5 opacity-60 flex-shrink-0" />
                                                  {generalWorkItem.name}
                                                </span>
                                              )}
                                            {entryTypeValue === "break" && (
                                              <span className="flex items-center gap-1 text-[10px] text-gray-700 dark:text-gray-200 whitespace-nowrap">
                                                <Coffee className="w-2.5 h-2.5 opacity-60" />{" "}
                                                Break
                                              </span>
                                            )}
                                            {entryTypeValue === "travel" && (
                                              <span className="flex items-center gap-1 text-[10px] text-gray-700 dark:text-gray-200 whitespace-nowrap">
                                                <Car className="w-2.5 h-2.5 opacity-60" />{" "}
                                                Travel
                                              </span>
                                            )}
                                          </div>

                                          {/* Row 3: Equipment */}
                                          {entry.equipment?.length > 0 && (
                                            <div className="flex flex-col gap-0.5">
                                              {entry.equipment.map(
                                                (eq: any) => (
                                                  <span
                                                    key={eq._id}
                                                    className={`flex items-center gap-1 text-[9px] ${
                                                      entryTypeValue === "break"
                                                        ? "text-error-700 dark:text-error-300"
                                                        : entryTypeValue ===
                                                            "general"
                                                          ? "text-warning-700 dark:text-warning-300"
                                                          : entryTypeValue ===
                                                              "travel"
                                                            ? "text-purple-700 dark:text-purple-300"
                                                            : "text-success-700 dark:text-success-300"
                                                    }`}
                                                  >
                                                    <Wrench className="w-2.5 h-2.5 flex-shrink-0" />{" "}
                                                    {eq.name}
                                                  </span>
                                                ),
                                              )}
                                            </div>
                                          )}

                                          {/* Row 4: Materials */}
                                          {entry.materials?.length > 0 && (
                                            <div className="flex flex-col gap-0.5">
                                              {entry.materials.map(
                                                (mat: any) => (
                                                  <span
                                                    key={mat._id}
                                                    className={`flex items-center gap-1 text-[9px] ${
                                                      entryTypeValue === "break"
                                                        ? "text-error-700 dark:text-error-300"
                                                        : entryTypeValue ===
                                                            "general"
                                                          ? "text-warning-700 dark:text-warning-300"
                                                          : entryTypeValue ===
                                                              "travel"
                                                            ? "text-purple-700 dark:text-purple-300"
                                                            : "text-success-700 dark:text-success-300"
                                                    }`}
                                                  >
                                                    <Package className="w-2.5 h-2.5 flex-shrink-0" />{" "}
                                                    {mat.name}
                                                  </span>
                                                ),
                                              )}
                                            </div>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                );
                              });
                            },
                          );
                        })()}

                        {/* Selected time block overlay */}
                        {selectedStartTime && selectedEndTime && (
                          <div className="absolute inset-0 pointer-events-none">
                            {(() => {
                              const totalSlots = timeBlocks.length - 1;
                              const startIdx =
                                timeBlocks.indexOf(selectedStartTime);
                              const endIdx =
                                timeBlocks.indexOf(selectedEndTime);

                              if (startIdx !== -1 && endIdx !== -1) {
                                const topPosition =
                                  (startIdx / totalSlots) * 100;
                                const bottomPosition =
                                  (endIdx / totalSlots) * 100;
                                const height = bottomPosition - topPosition;

                                return (
                                  <div
                                    className="absolute left-0 right-0 bg-gradient-to-br from-blue-50/95 to-blue-100/95 dark:from-blue-900/30 dark:to-blue-800/40 border-l-4 border-blue-500 dark:border-blue-400 rounded-lg shadow-lg backdrop-blur-sm"
                                    style={{
                                      top: `${topPosition}%`,
                                      height: `${height}%`,
                                    }}
                                  >
                                    <div className="absolute inset-0 flex items-center justify-center p-2">
                                      <div className="bg-white/95 dark:bg-gray-900/95 px-4 py-2 rounded-lg shadow-lg backdrop-blur-md border border-blue-300 dark:border-blue-600">
                                        <span className="text-sm font-semibold text-blue-700 dark:text-blue-300">
                                          {formatTimeDisplay(selectedStartTime)} -{" "}
                                          {formatTimeDisplay(selectedEndTime)}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                );
                              }
                              return null;
                            })()}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              // Work Time Entry Details Page
              <div className="p-4 space-y-4 overflow-y-auto flex-1">
                {/* Date and Time Display */}
                {selectedDate && (
                  <div className="pb-3 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-gray-700 dark:text-gray-300">
                        {(() => {
                          const [year, month, day] = selectedDate
                            .split("-")
                            .map(Number);
                          const dateObj = new Date(year, month - 1, day);
                          return formatDateUtil(dateObj);
                        })()}
                      </p>
                      <div className="flex items-center gap-2">
                        <input
                          type="time"
                          value={selectedStartTime}
                          onChange={(e) => setSelectedStartTime(e.target.value)}
                          className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        />
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          -
                        </span>
                        <input
                          type="time"
                          value={selectedEndTime}
                          onChange={(e) => setSelectedEndTime(e.target.value)}
                          className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Entry Type Toggle */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t("timesheet.entryType")} *
                  </label>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setEntryType("work");
                        setSelectedProject("");
                        setSelectedTask("");
                        setSelectedGeneralWorkItem("");
                      }}
                      className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                        entryType === "work"
                          ? "bg-success-500 dark:bg-success-600 text-white shadow-md"
                          : "bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 hover:bg-success-50 dark:hover:bg-success-900/20"
                      }`}
                    >
                      {t("timesheet.work")}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setEntryType("general");
                        setSelectedProject("");
                        setSelectedTask("");
                        setSelectedGeneralWorkItem("");
                      }}
                      className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                        entryType === "general"
                          ? "bg-warning-500 dark:bg-warning-600 text-white shadow-md"
                          : "bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 hover:bg-warning-50 dark:hover:bg-warning-900/20"
                      }`}
                    >
                      {t("timesheet.general")}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setEntryType("break");
                        setSelectedProject("");
                        setSelectedTask("");
                        setSelectedGeneralWorkItem("");
                      }}
                      className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                        entryType === "break"
                          ? "bg-error-500 dark:bg-error-600 text-white shadow-md"
                          : "bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 hover:bg-error-50 dark:hover:bg-error-900/20"
                      }`}
                    >
                      {t("timesheet.break")}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setEntryType("travel");
                        setSelectedProject("");
                        setSelectedTask("");
                        setSelectedGeneralWorkItem("");
                      }}
                      className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                        entryType === "travel"
                          ? "bg-purple-500 dark:bg-purple-600 text-white shadow-md"
                          : "bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 hover:bg-purple-50 dark:hover:bg-purple-900/20"
                      }`}
                    >
                      {t("timesheet.travel")}
                    </button>
                  </div>
                </div>

                {/* Project Selection - Only show for Work type */}
                {entryType === "work" && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        {t("timesheet.project")} *
                      </label>
                      <button
                        type="button"
                        onClick={() => setShowProjectSearch(!showProjectSearch)}
                        className="p-1 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                        title={t("timesheet.searchProjects")}
                      >
                        <Search className="w-4 h-4" />
                      </button>
                    </div>

                    {showProjectSearch && (
                      <input
                        type="text"
                        value={projectSearch}
                        onChange={(e) => setProjectSearch(e.target.value)}
                        placeholder={t("timesheet.typeToFilter")}
                        className="w-full px-3 py-2 mb-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
                      />
                    )}

                    <div className="flex flex-wrap gap-2 p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 max-h-60 overflow-y-auto">
                      {filteredProjects.length > 0 ? (
                        filteredProjects.map((project: any) => (
                          <button
                            key={project._id}
                            type="button"
                            onClick={() =>
                              setSelectedProject(
                                project._id === selectedProject
                                  ? ""
                                  : project._id,
                              )
                            }
                            className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                              selectedProject === project._id
                                ? "bg-accent-500 dark:bg-accent-600 text-white shadow-md"
                                : "bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 hover:bg-gray-200 dark:hover:bg-gray-600"
                            }`}
                          >
                            <span className="font-semibold">
                              {project.projectNumber}
                            </span>
                            <span className="opacity-90">{project.name}</span>
                            {selectedProject === project._id && (
                              <Check className="w-4 h-4" />
                            )}
                          </button>
                        ))
                      ) : (
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {projectSearch.length >= 3
                            ? t("timesheet.noMatchingProjectsFound")
                            : t("timesheet.noProjectsAvailable")}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* General Work Items Selection - Only show for General type */}
                {entryType === "general" && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t("timesheet.generalWorkItem")} *
                    </label>
                    <div className="flex flex-wrap gap-2 p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 max-h-48 overflow-y-auto">
                      {generalWorkItems.map((item: any) => (
                        <button
                          key={item._id}
                          type="button"
                          onClick={() =>
                            setSelectedGeneralWorkItem(
                              item._id === selectedGeneralWorkItem
                                ? ""
                                : item._id,
                            )
                          }
                          className={`inline-flex items-center gap-1 px-3 py-2 rounded-lg text-xs transition-all ${
                            selectedGeneralWorkItem === item._id
                              ? "bg-accent-500 dark:bg-accent-600 text-white shadow-md"
                              : "bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 hover:bg-gray-200 dark:hover:bg-gray-600"
                          }`}
                        >
                          {item.name}
                          {selectedGeneralWorkItem === item._id && (
                            <Check className="w-4 h-4" />
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Travel Address Selection - Only show for Travel type */}
                {entryType === "travel" && (
                  <>
                    {/* Start Address */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {t("timesheet.travelStartAddress")} *
                      </label>
                      <div className="space-y-2">
                        {/* Quick Address Buttons */}
                        <div className="flex flex-wrap gap-2 p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800">
                          <button
                            type="button"
                            onClick={async () => {
                              // Check if office address exists
                              const addr = account?.address;
                              if (addr) {
                                // Build address string from formatted or components
                                const addressStr =
                                  addr.formatted ||
                                  [
                                    addr.street,
                                    addr.city,
                                    addr.state,
                                    addr.postalCode,
                                    addr.country,
                                  ]
                                    .filter(Boolean)
                                    .join(", ");
                                if (addressStr) {
                                  // Address is valid, use it directly
                                  setTravelStartLocation(addressStr);
                                  setSelectedStartQuickAddress("office");
                                  if (travelEndLocation) {
                                    calculateDistance(
                                      addressStr,
                                      travelEndLocation,
                                    );
                                  }
                                  return;
                                }
                              }
                              // Show dialog to set office address
                              setOfficeAddress(
                                addr
                                  ? {
                                      formatted:
                                        addr.formatted ||
                                        [
                                          addr.street,
                                          addr.city,
                                          addr.state,
                                          addr.postalCode,
                                          addr.country,
                                        ]
                                          .filter(Boolean)
                                          .join(", "),
                                      streetNumber: addr.streetNumber,
                                      streetName: addr.streetName,
                                      street: addr.street,
                                      city: addr.city,
                                      state: addr.state,
                                      postalCode: addr.postalCode,
                                      country: addr.country,
                                      lat: addr.lat,
                                      lng: addr.lng,
                                    }
                                  : null,
                              );
                              setEditAddressType("start");
                              setShowEditOfficeDialog(true);
                            }}
                            className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all border ${
                              selectedStartQuickAddress === "office"
                                ? "bg-primary-100 dark:bg-primary-900/30 text-primary-900 dark:text-primary-100 border-primary-300 dark:border-primary-600"
                                : "bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600 hover:bg-gray-200 dark:hover:bg-gray-600"
                            }`}
                          >
                            <Building2 className="w-3 h-3" />
                            {t("timesheet.officeAddress")}
                          </button>
                          <button
                            type="button"
                            onClick={async () => {
                              // Check if home address exists and has formatted address
                              const addr = user?.address;
                              if (addr?.formatted) {
                                // Address is valid, use it directly
                                setTravelStartLocation(addr.formatted);
                                setSelectedStartQuickAddress("home");
                                if (travelEndLocation) {
                                  calculateDistance(
                                    addr.formatted,
                                    travelEndLocation,
                                  );
                                }
                              } else {
                                // Show dialog to set home address
                                setHomeAddress(
                                  addr
                                    ? {
                                        formatted:
                                          addr.formatted ||
                                          [
                                            addr.street,
                                            addr.city,
                                            addr.state,
                                            addr.postalCode,
                                            addr.country,
                                          ]
                                            .filter(Boolean)
                                            .join(", "),
                                        streetNumber: addr.streetNumber,
                                        streetName: addr.streetName,
                                        street: addr.street,
                                        city: addr.city,
                                        state: addr.state,
                                        postalCode: addr.postalCode,
                                        country: addr.country,
                                        lat: addr.lat,
                                        lng: addr.lng,
                                      }
                                    : null,
                                );
                                setEditAddressType("start");
                                setShowEditHomeDialog(true);
                              }
                            }}
                            className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all border ${
                              selectedStartQuickAddress === "home"
                                ? "bg-primary-100 dark:bg-primary-900/30 text-primary-900 dark:text-primary-100 border-primary-300 dark:border-primary-600"
                                : "bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600 hover:bg-gray-200 dark:hover:bg-gray-600"
                            }`}
                          >
                            <User className="w-3 h-3" />
                            {t("timesheet.homeAddress")}
                          </button>
                          {projects
                            .filter((p: any) => p.clientId)
                            .map((project: any) => (
                              <button
                                key={project._id}
                                type="button"
                                onClick={async () => {
                                  const clientId =
                                    typeof project.clientId === "object"
                                      ? project.clientId._id
                                      : project.clientId;
                                  const token = localStorage.getItem("token");
                                  try {
                                    const response = await fetch(
                                      `${apiUrl}/api/contacts/${clientId}`,
                                      {
                                        headers: {
                                          Authorization: `Bearer ${token}`,
                                        },
                                      },
                                    );
                                    if (response.ok) {
                                      const data = await response.json();
                                      const addr = data.data?.address;
                                      if (addr?.formatted) {
                                        // Address is valid, use it directly
                                        setTravelStartLocation(addr.formatted);
                                        setSelectedStartQuickAddress(
                                          project._id,
                                        );
                                        if (travelEndLocation) {
                                          calculateDistance(
                                            addr.formatted,
                                            travelEndLocation,
                                          );
                                        }
                                      } else if (addr) {
                                        // Fallback to building address from components
                                        const address = [
                                          addr.street,
                                          addr.city,
                                          addr.state,
                                          addr.postalCode,
                                          addr.country,
                                        ]
                                          .filter(Boolean)
                                          .join(", ");
                                        setTravelStartLocation(address);
                                        setSelectedStartQuickAddress(
                                          project._id,
                                        );
                                        if (travelEndLocation) {
                                          calculateDistance(
                                            address,
                                            travelEndLocation,
                                          );
                                        }
                                      }
                                    }
                                  } catch (error) {
                                    console.error(
                                      "Error fetching project address:",
                                      error,
                                    );
                                  }
                                }}
                                className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all border ${
                                  selectedStartQuickAddress === project._id
                                    ? "bg-primary-100 dark:bg-primary-900/30 text-primary-900 dark:text-primary-100 border-primary-300 dark:border-primary-600"
                                    : "bg-accent-100 dark:bg-accent-900/30 text-accent-900 dark:text-accent-100 border-blue-300 dark:border-accent-600 hover:bg-accent-200 dark:hover:bg-accent-900/50"
                                }`}
                              >
                                <Folder className="w-3 h-3" />
                                {project.projectNumber} - {project.name}
                              </button>
                            ))}
                          {customQuickAddresses.map((qa: any) => (
                            <button
                              key={qa.id}
                              type="button"
                              onClick={() => {
                                setTravelStartLocation(qa.address);
                                setSelectedStartQuickAddress(qa.id);
                                if (travelEndLocation) {
                                  calculateDistance(
                                    qa.address,
                                    travelEndLocation,
                                  );
                                }
                              }}
                              className={`relative inline-flex items-center gap-1.5 px-3 py-2 pr-6 rounded-lg text-xs font-medium transition-all border ${
                                selectedStartQuickAddress === qa.id
                                  ? "bg-primary-100 dark:bg-primary-900/30 text-primary-900 dark:text-primary-100 border-primary-300 dark:border-primary-600"
                                  : "bg-purple-100 dark:bg-purple-900/30 text-purple-900 dark:text-purple-100 border-purple-300 dark:border-purple-600 hover:bg-purple-200 dark:hover:bg-purple-900/50"
                              }`}
                            >
                              {qa.name}
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setAddressToDelete(qa.id);
                                }}
                                className="absolute top-0 right-0 p-0.5 text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                                title={t("common.delete") || "Delete"}
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </button>
                          ))}
                        </div>
                        <GoogleAddressAutocomplete
                          value={travelStartLocation}
                          onChange={(value) => {
                            setTravelStartLocation(value);
                            setSelectedStartQuickAddress(null); // Clear selection when manually changed
                          }}
                          disabled={!isOnline}
                          onBlur={(e) => {
                            handleAddressBlur(e.target.value, "start");
                          }}
                          placeholder={t("timesheet.enterOrSelectAddress")}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-500 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 text-sm"
                        />
                      </div>
                    </div>

                    {/* End Address */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {t("timesheet.travelEndAddress")} *
                      </label>
                      <div className="space-y-2">
                        {/* Quick Address Buttons */}
                        <div className="flex flex-wrap gap-2 p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800">
                          <button
                            type="button"
                            onClick={async () => {
                              // Check if office address exists
                              const addr = account?.address;
                              if (addr) {
                                // Build address string from formatted or components
                                const addressStr =
                                  addr.formatted ||
                                  [
                                    addr.street,
                                    addr.city,
                                    addr.state,
                                    addr.postalCode,
                                    addr.country,
                                  ]
                                    .filter(Boolean)
                                    .join(", ");
                                if (addressStr) {
                                  // Address is valid, use it directly
                                  setTravelEndLocation(addressStr);
                                  setSelectedEndQuickAddress("office");
                                  if (travelStartLocation) {
                                    calculateDistance(
                                      travelStartLocation,
                                      addressStr,
                                    );
                                  }
                                  return;
                                }
                              }
                              // Show dialog to set office address
                              setOfficeAddress(
                                addr
                                  ? {
                                      formatted:
                                        addr.formatted ||
                                        [
                                          addr.street,
                                          addr.city,
                                          addr.state,
                                          addr.postalCode,
                                          addr.country,
                                        ]
                                          .filter(Boolean)
                                          .join(", "),
                                      streetNumber: addr.streetNumber,
                                      streetName: addr.streetName,
                                      street: addr.street,
                                      city: addr.city,
                                      state: addr.state,
                                      postalCode: addr.postalCode,
                                      country: addr.country,
                                      lat: addr.lat,
                                      lng: addr.lng,
                                    }
                                  : null,
                              );
                              setEditAddressType("end");
                              setShowEditOfficeDialog(true);
                            }}
                            className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all border ${
                              selectedEndQuickAddress === "office"
                                ? "bg-primary-100 dark:bg-primary-900/30 text-primary-900 dark:text-primary-100 border-primary-300 dark:border-primary-600"
                                : "bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600 hover:bg-gray-200 dark:hover:bg-gray-600"
                            }`}
                          >
                            <Building2 className="w-3 h-3" />
                            {t("timesheet.officeAddress")}
                          </button>
                          <button
                            type="button"
                            onClick={async () => {
                              // Check if home address exists and has formatted address
                              const addr = user?.address;
                              if (addr?.formatted) {
                                // Address is valid, use it directly
                                setTravelEndLocation(addr.formatted);
                                setSelectedEndQuickAddress("home");
                                if (travelStartLocation) {
                                  calculateDistance(
                                    travelStartLocation,
                                    addr.formatted,
                                  );
                                }
                              } else {
                                // Show dialog to set home address
                                setHomeAddress(
                                  addr
                                    ? {
                                        formatted:
                                          addr.formatted ||
                                          [
                                            addr.street,
                                            addr.city,
                                            addr.state,
                                            addr.postalCode,
                                            addr.country,
                                          ]
                                            .filter(Boolean)
                                            .join(", "),
                                        streetNumber: addr.streetNumber,
                                        streetName: addr.streetName,
                                        street: addr.street,
                                        city: addr.city,
                                        state: addr.state,
                                        postalCode: addr.postalCode,
                                        country: addr.country,
                                        lat: addr.lat,
                                        lng: addr.lng,
                                      }
                                    : null,
                                );
                                setEditAddressType("end");
                                setShowEditHomeDialog(true);
                              }
                            }}
                            className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all border ${
                              selectedEndQuickAddress === "home"
                                ? "bg-primary-100 dark:bg-primary-900/30 text-primary-900 dark:text-primary-100 border-primary-300 dark:border-primary-600"
                                : "bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600 hover:bg-gray-200 dark:hover:bg-gray-600"
                            }`}
                          >
                            <User className="w-3 h-3" />
                            {t("timesheet.homeAddress")}
                          </button>
                          {projects
                            .filter((p: any) => p.clientId)
                            .map((project: any) => (
                              <button
                                key={project._id}
                                type="button"
                                onClick={async () => {
                                  const clientId =
                                    typeof project.clientId === "object"
                                      ? project.clientId._id
                                      : project.clientId;
                                  const token = localStorage.getItem("token");
                                  try {
                                    const response = await fetch(
                                      `${apiUrl}/api/contacts/${clientId}`,
                                      {
                                        headers: {
                                          Authorization: `Bearer ${token}`,
                                        },
                                      },
                                    );
                                    if (response.ok) {
                                      const data = await response.json();
                                      const addr = data.data?.address;
                                      if (addr?.formatted) {
                                        // Address is valid, use it directly
                                        setTravelEndLocation(addr.formatted);
                                        setSelectedEndQuickAddress(project._id);
                                        if (travelStartLocation) {
                                          calculateDistance(
                                            travelStartLocation,
                                            addr.formatted,
                                          );
                                        }
                                      } else if (addr) {
                                        // Fallback to building address from components
                                        const address = [
                                          addr.street,
                                          addr.city,
                                          addr.state,
                                          addr.postalCode,
                                          addr.country,
                                        ]
                                          .filter(Boolean)
                                          .join(", ");
                                        setTravelEndLocation(address);
                                        setSelectedEndQuickAddress(project._id);
                                        if (travelStartLocation) {
                                          calculateDistance(
                                            travelStartLocation,
                                            address,
                                          );
                                        }
                                      }
                                    }
                                  } catch (error) {
                                    console.error(
                                      "Error fetching project address:",
                                      error,
                                    );
                                  }
                                }}
                                className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all border ${
                                  selectedEndQuickAddress === project._id
                                    ? "bg-primary-100 dark:bg-primary-900/30 text-primary-900 dark:text-primary-100 border-primary-300 dark:border-primary-600"
                                    : "bg-accent-100 dark:bg-accent-900/30 text-accent-900 dark:text-accent-100 border-blue-300 dark:border-accent-600 hover:bg-accent-200 dark:hover:bg-accent-900/50"
                                }`}
                              >
                                <Folder className="w-3 h-3" />
                                {project.projectNumber} - {project.name}
                              </button>
                            ))}
                          {customQuickAddresses.map((qa: any) => (
                            <button
                              key={qa.id}
                              type="button"
                              onClick={() => {
                                setTravelEndLocation(qa.address);
                                setSelectedEndQuickAddress(qa.id);
                                if (travelStartLocation) {
                                  calculateDistance(
                                    travelStartLocation,
                                    qa.address,
                                  );
                                }
                              }}
                              className={`relative inline-flex items-center gap-1.5 px-3 py-2 pr-6 rounded-lg text-xs font-medium transition-all border ${
                                selectedEndQuickAddress === qa.id
                                  ? "bg-primary-100 dark:bg-primary-900/30 text-primary-900 dark:text-primary-100 border-primary-300 dark:border-primary-600"
                                  : "bg-purple-100 dark:bg-purple-900/30 text-purple-900 dark:text-purple-100 border-purple-300 dark:border-purple-600 hover:bg-purple-200 dark:hover:bg-purple-900/50"
                              }`}
                            >
                              {qa.name}
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setAddressToDelete(qa.id);
                                }}
                                className="absolute top-0 right-0 p-0.5 text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                                title={t("common.delete") || "Delete"}
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </button>
                          ))}
                        </div>
                        <GoogleAddressAutocomplete
                          value={travelEndLocation}
                          onChange={(value) => {
                            setTravelEndLocation(value);
                            setSelectedEndQuickAddress(null); // Clear selection when manually changed
                          }}
                          disabled={!isOnline}
                          onBlur={(e) => {
                            handleAddressBlur(e.target.value, "end");
                          }}
                          placeholder={t("timesheet.enterOrSelectAddress")}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-500 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 text-sm"
                        />
                      </div>
                    </div>

                    {/* Distance Display */}
                    {travelDistance > 0 && (
                      <div
                        className={`p-3 border rounded-lg ${
                          !isOnline
                            ? "bg-gray-100 dark:bg-gray-800 border-gray-300 dark:border-gray-600"
                            : "bg-purple-50 dark:bg-gray-700 dark:border dark:border-purple-800/50 border-purple-200 dark:border-purple-700"
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span
                            className={`text-sm font-medium ${
                              !isOnline
                                ? "text-gray-500 dark:text-gray-400 line-through"
                                : "text-purple-900 dark:text-purple-100"
                            }`}
                            title={
                              !isOnline
                                ? t("timesheet.distanceCalculationOffline") ||
                                  "Distance calculation unavailable offline"
                                : ""
                            }
                          >
                            {t("timesheet.travelDistance")}:
                          </span>
                          <span
                            className={`text-lg font-bold ${
                              !isOnline
                                ? "text-gray-500 dark:text-gray-400 line-through"
                                : "text-purple-700 dark:text-purple-300"
                            }`}
                            title={
                              !isOnline
                                ? t("timesheet.distanceCalculationOffline") ||
                                  "Distance calculation unavailable offline"
                                : ""
                            }
                          >
                            {travelDistance} {travelDistanceUnit}
                          </span>
                        </div>
                        {isCalculatingDistance && (
                          <div className="mt-2 text-xs text-purple-600 dark:text-purple-400">
                            {t("timesheet.calculatingDistance")}...
                          </div>
                        )}
                        {!isOnline && (
                          <div className="mt-2 text-xs text-gray-500 dark:text-gray-400 italic">
                            {t("timesheet.distanceCalculationOffline")}
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}

                {/* Task Selection - Only show for Work type */}
                {entryType === "work" &&
                  selectedProject &&
                  tasks.length > 0 && (
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                          {t("timesheet.task")} *
                        </label>
                        <button
                          type="button"
                          onClick={() => setShowTaskSearch(!showTaskSearch)}
                          className="p-1 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                          title={t("timesheet.searchTasks")}
                        >
                          <Search className="w-4 h-4" />
                        </button>
                      </div>

                      {showTaskSearch && (
                        <input
                          type="text"
                          value={taskSearch}
                          onChange={(e) => setTaskSearch(e.target.value)}
                          placeholder={t("timesheet.typeToFilter")}
                          className="w-full px-3 py-2 mb-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
                        />
                      )}

                      <div className="flex flex-wrap gap-2 p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 max-h-48 overflow-y-auto">
                        {filteredTasks.length > 0 ? (
                          filteredTasks.map((task: any) => (
                            <button
                              key={task._id}
                              type="button"
                              onClick={() =>
                                setSelectedTask(
                                  task._id === selectedTask ? "" : task._id,
                                )
                              }
                              className={`inline-flex items-center gap-1 px-3 py-2 rounded-lg text-xs transition-all ${
                                selectedTask === task._id
                                  ? "bg-accent-500 dark:bg-accent-600 text-white shadow-md"
                                  : "bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 hover:bg-gray-200 dark:hover:bg-gray-600"
                              }`}
                            >
                              {task.title}
                              {selectedTask === task._id && (
                                <Check className="w-4 h-4" />
                              )}
                            </button>
                          ))
                        ) : (
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {taskSearch.length >= 3
                              ? t("timesheet.noMatchingTasksFound")
                              : t("timesheet.noTasksAvailable")}
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                {/* Equipment, Materials, and Notes - Only show when task is selected */}
                {selectedTask && (
                  <>
                    {/* Equipment Multi-Select */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                          {t("timesheet.equipment")}
                        </label>
                        <button
                          type="button"
                          onClick={() =>
                            setShowEquipmentSearch(!showEquipmentSearch)
                          }
                          className="p-1 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                          title={t("timesheet.searchEquipment")}
                        >
                          <Search className="w-4 h-4" />
                        </button>
                      </div>

                      {showEquipmentSearch && (
                        <input
                          type="text"
                          value={equipmentSearch}
                          onChange={(e) => setEquipmentSearch(e.target.value)}
                          placeholder={t("timesheet.typeToFilter")}
                          className="w-full px-3 py-2 mb-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
                        />
                      )}

                      <div className="flex flex-wrap gap-2 p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 max-h-[85px] overflow-y-auto">
                        {filteredEquipment.length > 0 ? (
                          filteredEquipment.map((item: any) => (
                            <button
                              key={item._id}
                              type="button"
                              onClick={() => {
                                if (selectedEquipment.includes(item._id)) {
                                  setSelectedEquipment(
                                    selectedEquipment.filter(
                                      (id) => id !== item._id,
                                    ),
                                  );
                                } else {
                                  setSelectedEquipment([
                                    ...selectedEquipment,
                                    item._id,
                                  ]);
                                }
                              }}
                              className={`inline-flex items-center gap-1 px-3 py-2 rounded-lg text-xs transition-all ${
                                selectedEquipment.includes(item._id)
                                  ? "bg-accent-500 dark:bg-accent-600 text-white shadow-md"
                                  : "bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 hover:bg-gray-200 dark:hover:bg-gray-600"
                              }`}
                            >
                              {item.name}
                              {selectedEquipment.includes(item._id) && (
                                <Check className="w-4 h-4" />
                              )}
                            </button>
                          ))
                        ) : (
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {equipmentSearch.length >= 3
                              ? t("timesheet.noMatchingEquipmentFound")
                              : t("timesheet.noEquipmentAvailable")}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Materials Multi-Select */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                          {t("timesheet.materials")}
                        </label>
                        <button
                          type="button"
                          onClick={() =>
                            setShowMaterialSearch(!showMaterialSearch)
                          }
                          className="p-1 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                          title={t("timesheet.searchMaterials")}
                        >
                          <Search className="w-4 h-4" />
                        </button>
                      </div>

                      {showMaterialSearch && (
                        <input
                          type="text"
                          value={materialSearch}
                          onChange={(e) => setMaterialSearch(e.target.value)}
                          placeholder={t("timesheet.typeToFilter")}
                          className="w-full px-3 py-2 mb-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
                        />
                      )}

                      <div className="flex flex-wrap gap-2 p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 max-h-[85px] overflow-y-auto">
                        {filteredMaterials.length > 0 ? (
                          filteredMaterials.map((item: any) => {
                            const isSelected = selectedMaterials.some(
                              (m) => m._id === item._id,
                            );
                            const selectedMaterial = selectedMaterials.find(
                              (m) => m._id === item._id,
                            );
                            return (
                              <button
                                key={item._id}
                                type="button"
                                onClick={() => {
                                  if (isSelected) {
                                    setSelectedMaterials(
                                      selectedMaterials.filter(
                                        (m) => m._id !== item._id,
                                      ),
                                    );
                                  } else {
                                    // Show modal to enter quantity
                                    setPendingMaterial(item);
                                    setMaterialQuantity("");
                                    setShowMaterialQuantityModal(true);
                                  }
                                }}
                                className={`inline-flex items-center gap-1 px-3 py-2 rounded-lg text-xs transition-all ${
                                  isSelected
                                    ? "bg-accent-500 dark:bg-accent-600 text-white shadow-md"
                                    : "bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 hover:bg-gray-200 dark:hover:bg-gray-600"
                                }`}
                              >
                                <span>{item.name}</span>
                                {isSelected && selectedMaterial && (
                                  <>
                                    <span className="ml-1 opacity-90">
                                      ({selectedMaterial.quantity}{" "}
                                      {selectedMaterial.unit})
                                    </span>
                                    <Check className="w-4 h-4" />
                                  </>
                                )}
                              </button>
                            );
                          })
                        ) : (
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {materialSearch.length >= 3
                              ? t("timesheet.noMatchingMaterialsFound")
                              : t("timesheet.noMaterialsAvailable")}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Notes with Speech Recognition and AI Formatting */}
                    <AIDescriptionField
                      value={notes}
                      onChange={setNotes}
                      label={t("timesheet.notes")}
                      placeholder={t("timesheet.addNotesPlaceholder")}
                      rows={4}
                      context={{
                        project: selectedProject
                          ? projects.find((p: any) => p._id === selectedProject)
                          : null,
                        task: selectedTask
                          ? tasks.find((t: any) => t._id === selectedTask)
                          : null,
                        equipment: equipment
                          .filter((e: any) => selectedEquipment.includes(e._id))
                          .map((e: any) => e.name),
                        materials: selectedMaterials.map(
                          (m: any) => `${m.name} (${m.quantity} ${m.unit})`,
                        ),
                        hours: (() => {
                          const [startHour, startMin] = selectedStartTime
                            .split(":")
                            .map(Number);
                          const [endHour, endMin] = selectedEndTime
                            .split(":")
                            .map(Number);
                          const totalMinutes =
                            endHour * 60 + endMin - (startHour * 60 + startMin);
                          return totalMinutes / 60;
                        })(),
                        startTime: selectedStartTime,
                        endTime: selectedEndTime,
                        date: selectedDate,
                        entryType,
                      }}
                    />
                  </>
                )}
              </div>
            )}

            {/* Message */}
            {/* Offline Status Indicator */}
            {!isOnline && (
              <div className="mb-4 p-3 bg-yellow-50 dark:bg-gray-700 dark:border dark:border-warning-800/50 border border-yellow-200 dark:border-yellow-800 rounded-lg flex items-center gap-2">
                <WifiOff className="w-5 h-5 text-warning-600 dark:text-warning-400" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-warning-800 dark:text-warning-200">
                    {t("offline.workingOffline")}
                  </p>
                  {pendingSyncCount > 0 && (
                    <p className="text-xs text-warning-600 dark:text-warning-400 mt-1">
                      {t("offline.pendingSync", {
                        count: pendingSyncCount,
                        plural: pendingSyncCount === 1 ? "" : "s",
                      })}
                    </p>
                  )}
                </div>
              </div>
            )}
            {message && (
              <div className="p-4">
                <div
                  className={`p-3 rounded-lg text-sm flex items-start justify-between gap-2 ${
                    message.includes("success") ||
                    message.includes("successfully")
                      ? "bg-green-50 dark:bg-gray-700 dark:border dark:border-success-800/50 text-success-800 dark:text-success-200"
                      : "bg-red-50 dark:bg-gray-700 dark:border dark:border-error-800/50 text-error-800 dark:text-error-200"
                  }`}
                >
                  <span className="flex-1">{message}</span>
                  <button
                    onClick={() => setMessage("")}
                    className="flex-shrink-0 p-1 hover:opacity-70 transition-opacity"
                    aria-label={t("common.dismiss") || "Dismiss"}
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </form>

          {/* Actions Footer */}
          <div className="border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
            {/* Summary Preview Button */}
            {localWorkEntries.length > 0 && !showDetails && (
              <div className="px-4 pt-3 pb-2">
                <button
                  type="button"
                  onClick={() => setShowSubmissionSummary(true)}
                  className="w-full px-4 py-2.5 text-sm font-medium bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-900 dark:text-gray-100 rounded-lg transition-colors flex items-center justify-between group"
                >
                  <div className="flex items-center gap-2">
                    <Info className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                    <span className="font-semibold">
                      {t("timesheet.submissionSummary") || "Submission Summary"}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-xs">
                    {(() => {
                      const summary = calculateSubmissionSummary();
                      const uniqueStaffCount = new Set(
                        summary.dateStaffGroups.map((g: any) => g.staffId),
                      ).size;
                      return (
                        <>
                          <span className="text-gray-600 dark:text-gray-400">
                            {summary.totalDates}{" "}
                            {summary.totalDates === 1
                              ? t("common.date") || "date"
                              : t("common.dates") || "dates"}
                          </span>
                          <span className="text-gray-600 dark:text-gray-400">
                            •
                          </span>
                          {uniqueStaffCount > 0 && (
                            <>
                              <span className="text-gray-600 dark:text-gray-400">
                                {uniqueStaffCount}{" "}
                                {uniqueStaffCount === 1
                                  ? t("timesheet.staff") || "staff"
                                  : t("timesheet.staff") || "staff"}
                              </span>
                              <span className="text-gray-600 dark:text-gray-400">
                                •
                              </span>
                            </>
                          )}
                          <span className="font-bold text-accent-600 dark:text-accent-400">
                            {summary.totalHours.toFixed(2)}h
                          </span>
                        </>
                      );
                    })()}
                    <ChevronDown className="w-4 h-4 text-gray-600 dark:text-gray-400 group-hover:translate-y-0.5 transition-transform" />
                  </div>
                </button>
              </div>
            )}
            <div className="p-4 pt-2">
              <div className="flex gap-3">
                {!showDetails ? (
                  <>
                    <button
                      type="button"
                      onClick={handleClose}
                      className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-lg transition-colors"
                    >
                      {t("common.cancel")}
                    </button>
                    {pendingEntryChanges ? (
                      <button
                        type="button"
                        onClick={handleSavePendingChanges}
                        disabled={loading || isApprovedReadOnly}
                        className="flex-1 px-4 py-2.5 text-sm font-medium bg-accent-600 dark:bg-accent-600 text-white rounded-lg hover:bg-accent-700 dark:hover:bg-accent-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 transition-colors"
                        title={t("timesheet.savePendingChanges")}
                      >
                        {loading ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            <span>{t("common.saving")}</span>
                          </>
                        ) : (
                          <>
                            <Save className="w-4 h-4" />
                            <span>{t("timesheet.saveChanges")}</span>
                          </>
                        )}
                      </button>
                    ) : (
                      (() => {
                        // When editing an existing timesheet, enable if it has work entries; otherwise require local entries
                        const hasEntriesToSubmit = editingTimesheet
                          ? (() => {
                              const ts = (dateTimesheets || []).find(
                                (t: any) => t._id === editingTimesheet._id,
                              );
                              return (
                                ts &&
                                ts.workEntries &&
                                Array.isArray(ts.workEntries) &&
                                ts.workEntries.length > 0
                              );
                            })()
                          : localWorkEntries.length > 0;
                        return (
                          <>
                            <button
                              type="button"
                              onClick={handleSaveDraft}
                              disabled={
                                loading ||
                                !hasEntriesToSubmit ||
                                isApprovedReadOnly
                              }
                              className="flex-1 px-4 py-2.5 text-sm font-medium bg-gray-600 dark:bg-gray-600 text-white rounded-lg hover:bg-gray-700 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 transition-colors"
                              title={
                                loading
                                  ? t("timesheet.savingDraft")
                                  : !hasEntriesToSubmit
                                    ? t("timesheet.addWorkEntriesFirst")
                                    : t("timesheet.saveAsDraft")
                              }
                            >
                              {loading ? (
                                <>
                                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                  <span>{t("common.saving")}</span>
                                </>
                              ) : (
                                <>
                                  <Save className="w-4 h-4" />
                                  <span>{t("timesheet.saveAsDraft")}</span>
                                </>
                              )}
                            </button>
                            <button
                              type="submit"
                              disabled={
                                loading ||
                                !hasEntriesToSubmit ||
                                isApprovedReadOnly
                              }
                              onClick={handleSubmit}
                              className="flex-1 px-4 py-2.5 text-sm font-medium bg-accent-600 dark:bg-accent-600 text-white rounded-lg hover:bg-accent-700 dark:hover:bg-accent-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 transition-colors relative group"
                              title={
                                loading
                                  ? t("timesheet.submittingTimesheet")
                                  : !hasEntriesToSubmit
                                    ? t("timesheet.addWorkEntriesFirst")
                                    : t("timesheet.submit")
                              }
                            >
                              {loading ? (
                                <>
                                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                  <span>{t("timesheet.submitting")}</span>
                                </>
                              ) : (
                                <>
                                  <Save className="w-4 h-4" />
                                  <span>{t("timesheet.submit")}</span>
                                </>
                              )}
                              {!hasEntriesToSubmit && !loading && (
                                <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50 pointer-events-none">
                                  {t("timesheet.addWorkEntriesFirst")}
                                </span>
                              )}
                            </button>
                          </>
                        );
                      })()
                    )}
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={handleBackFromDetails}
                      className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-lg transition-colors"
                    >
                      {editingEntryData
                        ? t("common.cancel")
                        : t("common.back")}
                    </button>
                    {(() => {
                      // When editing an entry, use form state or fall back to entry times so the button is enabled as soon as the panel opens or after any edit
                      const entry = editingEntryData?.entry;
                      const fallbackStart =
                        entry?.startTime != null
                          ? (() => {
                              const d = new Date(entry.startTime);
                              if (!isNaN(d.getTime()))
                                return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
                              if (
                                typeof entry.startTime === "string" &&
                                /^\d{1,2}:\d{2}/.test(entry.startTime)
                              ) {
                                const [h, m] = entry.startTime
                                  .split(":")
                                  .map(Number);
                                return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
                              }
                              return "";
                            })()
                          : "";
                      const fallbackEnd =
                        entry?.endTime != null
                          ? (() => {
                              const d = new Date(entry.endTime);
                              if (!isNaN(d.getTime()))
                                return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
                              if (
                                typeof entry.endTime === "string" &&
                                /^\d{1,2}:\d{2}/.test(entry.endTime)
                              ) {
                                const [h, m] = entry.endTime
                                  .split(":")
                                  .map(Number);
                                return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
                              }
                              return "";
                            })()
                          : "";
                      const effectiveStart =
                        selectedStartTime || (editingEntryData ? fallbackStart : "");
                      const effectiveEnd =
                        selectedEndTime || (editingEntryData ? fallbackEnd : "");
                      const hasValidTime =
                        effectiveStart &&
                        effectiveEnd &&
                        /^\d{2}:\d{2}$/.test(effectiveStart) &&
                        /^\d{2}:\d{2}$/.test(effectiveEnd);
                      const isDisabled =
                        loading ||
                        !hasValidTime ||
                        (entryType === "work" && !selectedProject) ||
                        (entryType === "work" && !selectedTask) ||
                        (entryType === "general" && !selectedGeneralWorkItem);
                      return (
                    <button
                      type="button"
                      onClick={handleSaveDetails}
                      disabled={isDisabled}
                      className="flex-1 px-4 py-2.5 text-sm font-medium bg-accent-600 dark:bg-accent-600 text-white rounded-lg hover:bg-accent-700 dark:hover:bg-accent-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors relative group"
                      title={
                        loading
                          ? editingEntryData
                            ? t("timesheet.updating")
                            : t("common.saving")
                          : !hasValidTime
                            ? t("timesheet.pleaseSelectTimeRange")
                            : entryType === "work" && !selectedProject
                              ? t("timesheet.pleaseSelectProject")
                              : entryType === "work" && !selectedTask
                                ? t("timesheet.pleaseSelectTask")
                                : entryType === "general" &&
                                    !selectedGeneralWorkItem
                                  ? t("timesheet.pleaseSelectGeneralWorkItem")
                                  : editingEntryData
                                    ? t("timesheet.updateWorkTime")
                                    : t("timesheet.saveWorkTime")
                      }
                    >
                      {loading ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          <span>
                            {editingEntryData
                              ? t("timesheet.updating")
                              : t("common.saving")}
                          </span>
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4" />
                          <span>
                            {editingEntryData
                              ? t("timesheet.saveChanges")
                              : t("timesheet.addEntry")}
                          </span>
                        </>
                      )}
                      {(!selectedProject ||
                        !selectedStartTime ||
                        !selectedEndTime) &&
                        !loading && (
                          <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-50 pointer-events-none">
                            {!selectedProject
                              ? t("timesheet.selectProjectFirst")
                              : t("timesheet.selectTimeRange")}
                          </span>
                        )}
                    </button>
                      );
                    })()}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Save Address Dialog */}
      {showSaveAddressDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              {t("timesheet.saveQuickAddress")}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              {t("timesheet.saveQuickAddressDescription")}
            </p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t("timesheet.quickAddressName")}
                </label>
                <input
                  type="text"
                  value={addressName}
                  onChange={(e) => setAddressName(e.target.value)}
                  placeholder={t("timesheet.quickAddressNamePlaceholder")}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t("timesheet.address")}
                </label>
                <div className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-900 text-gray-700 dark:text-gray-300 text-sm">
                  {addressToSave}
                </div>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowSaveAddressDialog(false);
                  setAddressName("");
                  setAddressToSave("");
                }}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                {t("common.cancel")}
              </button>
              <button
                onClick={async () => {
                  if (addressName.trim()) {
                    await saveCustomQuickAddress(
                      addressName.trim(),
                      addressToSave,
                    );
                    setShowSaveAddressDialog(false);
                    setAddressName("");
                    setAddressToSave("");
                  }
                }}
                disabled={!addressName.trim()}
                className="flex-1 px-4 py-2 bg-accent-600 text-white rounded-lg hover:bg-accent-700 disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:cursor-not-allowed transition-colors"
              >
                {t("common.save")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Office Address Dialog */}
      {showEditOfficeDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              {t("timesheet.setOfficeAddress")}
            </h3>
            <div className="space-y-4">
              <GoogleAddressAutocomplete
                value={officeAddress?.formatted || ""}
                onChange={(value) => {
                  if (officeAddress) {
                    setOfficeAddress({ ...officeAddress, formatted: value });
                  } else {
                    setOfficeAddress({ formatted: value });
                  }
                }}
                disabled={!isOnline}
                onPlaceSelect={(details) => {
                  setOfficeAddress(details);
                }}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
                placeholder={t("timesheet.enterOrSelectAddress")}
                autoFocus
              />
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowEditOfficeDialog(false);
                  setOfficeAddress(null);
                }}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                {t("common.cancel")}
              </button>
              <button
                onClick={saveOfficeAddress}
                disabled={!officeAddress || !officeAddress.formatted}
                className="flex-1 px-4 py-2 bg-accent-600 text-white rounded-lg hover:bg-accent-700 disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:cursor-not-allowed transition-colors"
              >
                {t("common.save")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Home Address Dialog */}
      {showEditHomeDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              {t("timesheet.setHomeAddress")}
            </h3>
            <div className="space-y-4">
              <GoogleAddressAutocomplete
                value={homeAddress?.formatted || ""}
                onChange={(value) => {
                  if (homeAddress) {
                    setHomeAddress({ ...homeAddress, formatted: value });
                  } else {
                    setHomeAddress({ formatted: value });
                  }
                }}
                disabled={!isOnline}
                onPlaceSelect={(details) => {
                  setHomeAddress(details);
                }}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
                placeholder={t("timesheet.enterOrSelectAddress")}
                autoFocus
              />
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowEditHomeDialog(false);
                  setHomeAddress(null);
                }}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                {t("common.cancel")}
              </button>
              <button
                onClick={saveHomeAddress}
                disabled={!homeAddress || !homeAddress.formatted}
                className="flex-1 px-4 py-2 bg-accent-600 text-white rounded-lg hover:bg-accent-700 disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:cursor-not-allowed transition-colors"
              >
                {t("common.save")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Submission Summary Dialog */}
      <ConfirmDialog
        isOpen={showSubmissionSummary}
        title={t("timesheet.submissionSummary") || "Submission Summary"}
        message={(() => {
          const summary = calculateSubmissionSummary();
          const formatDate = (dateStr: string) => {
            const date = new Date(dateStr + "T00:00:00");
            return formatDateUtil(date);
          };

          return (
            <div className="flex flex-col h-full max-h-[60vh]">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-3 flex-shrink-0">
                {t("timesheet.submittingForDates", {
                  count: summary.totalDates,
                }) ||
                  `Submitting timesheets for ${summary.totalDates} date${summary.totalDates !== 1 ? "s" : ""}:`}
              </p>
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 space-y-3 overflow-y-auto flex-1 min-h-0">
                {summary.dateStaffGroups.map(
                  ({ date, staffId, hours, entries }) => {
                    const staffMember = staffList.find(
                      (s: any) => s._id === staffId,
                    );
                    const staffName = staffMember
                      ? `${staffMember.firstName} ${staffMember.lastName}`
                      : staffId;
                    const expandKey = `${date}_${staffId}`;
                    const isExpanded = expandedSummaryDates.has(expandKey);
                    const formatTime = (timeStr: string) => {
                      if (!timeStr) return "";
                      try {
                        const time = new Date(timeStr);
                        return time.toLocaleTimeString(dateLocale, {
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: false,
                        });
                      } catch {
                        return timeStr;
                      }
                    };

                    return (
                      <div
                        key={expandKey}
                        className="border-b border-gray-200 dark:border-gray-600 last:border-0 pb-2 last:pb-0"
                      >
                        <div
                          className="flex items-center justify-between py-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 rounded px-2 -mx-2"
                          onClick={() => {
                            setExpandedSummaryDates((prev) => {
                              const next = new Set(prev);
                              if (next.has(expandKey)) {
                                next.delete(expandKey);
                              } else {
                                next.add(expandKey);
                              }
                              return next;
                            });
                          }}
                        >
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                            <div className="flex flex-col">
                              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                {formatDate(date)}
                              </span>
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                {staffName}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                            <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                              {hours.toFixed(2)}{" "}
                              {t("timesheet.hours") || "hours"}
                            </span>
                            {entries.length > 0 && (
                              <button className="p-1 hover:bg-gray-200 dark:hover:bg-gray-500 rounded">
                                {isExpanded ? (
                                  <ChevronUp className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                                ) : (
                                  <ChevronDown className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                                )}
                              </button>
                            )}
                          </div>
                        </div>
                        {isExpanded && entries.length > 0 && (
                          <div className="ml-6 mt-2 space-y-1.5">
                            {entries.map((entry: any, idx: number) => {
                              const entryType = entry.type || "work";
                              // Color scheme based on entry type
                              const bgColor =
                                entryType === "break"
                                  ? "bg-red-50 dark:bg-gray-700 dark:border dark:border-error-800/50 border-red-200 dark:border-red-800"
                                  : entryType === "general"
                                    ? "bg-yellow-50 dark:bg-gray-700 dark:border dark:border-warning-800/50 border-yellow-200 dark:border-yellow-800"
                                    : entryType === "travel"
                                      ? "bg-purple-50 dark:bg-gray-700 dark:border dark:border-purple-800/50 border-purple-200 dark:border-purple-800"
                                      : "bg-green-50 dark:bg-gray-700 dark:border dark:border-success-800/50 border-green-200 dark:border-green-800";

                              const textColor =
                                entryType === "break"
                                  ? "text-error-900 dark:text-error-100"
                                  : entryType === "general"
                                    ? "text-warning-900 dark:text-warning-100"
                                    : entryType === "travel"
                                      ? "text-purple-900 dark:text-purple-100"
                                      : "text-success-900 dark:text-success-100";

                              const accentColor =
                                entryType === "break"
                                  ? "text-error-700 dark:text-error-300"
                                  : entryType === "general"
                                    ? "text-warning-700 dark:text-warning-300"
                                    : entryType === "travel"
                                      ? "text-purple-700 dark:text-purple-300"
                                      : "text-success-700 dark:text-success-300";

                              return (
                                <div
                                  key={idx}
                                  className={`${bgColor} border rounded-lg px-2 py-1.5 space-y-1`}
                                >
                                  <div className="flex items-center justify-between">
                                    <span
                                      className={`font-semibold text-xs ${textColor}`}
                                    >
                                      {formatTime(entry.startTime)} -{" "}
                                      {formatTime(entry.endTime)}
                                    </span>
                                    <span
                                      className={`font-bold text-xs ${textColor}`}
                                    >
                                      {entry.hours.toFixed(2)}h
                                    </span>
                                  </div>
                                  <div className="flex flex-col gap-0.5 text-[10px]">
                                    {entryType === "work" && entry.project && (
                                      <div
                                        className={`flex items-center gap-1 ${accentColor}`}
                                      >
                                        <Folder className="w-3 h-3 flex-shrink-0" />
                                        <span className="truncate">
                                          {entry.project.projectNumber ||
                                            entry.project.name}
                                        </span>
                                      </div>
                                    )}
                                    {entryType === "work" && entry.task && (
                                      <div
                                        className={`flex items-center gap-1 ${accentColor}`}
                                      >
                                        <CheckSquare className="w-3 h-3 flex-shrink-0" />
                                        <span className="truncate">
                                          {entry.task.title}
                                        </span>
                                      </div>
                                    )}
                                    {entryType === "general" &&
                                      entry.generalWorkItem && (
                                        <div
                                          className={`flex items-center gap-1 ${accentColor}`}
                                        >
                                          <ClipboardList className="w-3 h-3 flex-shrink-0" />
                                          <span className="truncate">
                                            {entry.generalWorkItem.name}
                                          </span>
                                        </div>
                                      )}
                                    {entryType === "break" && (
                                      <div
                                        className={`flex items-center gap-1 ${accentColor}`}
                                      >
                                        <Coffee className="w-3 h-3 flex-shrink-0" />
                                        <span>Break</span>
                                      </div>
                                    )}
                                    {entryType === "travel" && (
                                      <div
                                        className={`flex items-center gap-1 ${accentColor}`}
                                      >
                                        <Car className="w-3 h-3 flex-shrink-0" />
                                        <span>Travel</span>
                                      </div>
                                    )}
                                    {entry.equipment &&
                                      entry.equipment.length > 0 && (
                                        <div
                                          className={`flex items-center gap-1 flex-wrap ${accentColor}`}
                                        >
                                          <Wrench className="w-3 h-3 flex-shrink-0" />
                                          <span className="truncate">
                                            {entry.equipment
                                              .map((eq: any) =>
                                                typeof eq === "object"
                                                  ? eq.name
                                                  : eq,
                                              )
                                              .join(", ")}
                                          </span>
                                        </div>
                                      )}
                                    {entry.materials &&
                                      entry.materials.length > 0 && (
                                        <div
                                          className={`flex items-center gap-1 flex-wrap ${accentColor}`}
                                        >
                                          <Package className="w-3 h-3 flex-shrink-0" />
                                          <span className="truncate">
                                            {entry.materials
                                              .map((mat: any) =>
                                                typeof mat === "object"
                                                  ? mat.name
                                                  : mat,
                                              )
                                              .join(", ")}
                                          </span>
                                        </div>
                                      )}
                                    {entry.notes && (
                                      <div
                                        className={`text-[10px] ${accentColor} opacity-80 italic truncate`}
                                      >
                                        {entry.notes}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  },
                )}
              </div>
              <div className="flex items-center justify-between pt-2 border-t border-gray-200 dark:border-gray-700">
                <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  {t("timesheet.totalHours") || "Total Hours"}:
                </span>
                <span className="text-sm font-bold text-accent-600 dark:text-accent-400">
                  {summary.totalHours.toFixed(2)}{" "}
                  {t("timesheet.hours") || "hours"}
                </span>
              </div>
            </div>
          );
        })()}
        confirmLabel={t("common.close") || "Close"}
        cancelLabel={t("common.cancel")}
        type="info"
        onConfirm={() => setShowSubmissionSummary(false)}
        onCancel={() => setShowSubmissionSummary(false)}
      />

      {/* Delete Custom Address Confirmation Dialog */}
      <ConfirmDialog
        isOpen={addressToDelete !== null}
        title={t("timesheet.deleteQuickAddress") || "Delete Quick Address"}
        message={
          t("timesheet.confirmDeleteQuickAddress") ||
          "Are you sure you want to delete this quick address? This action cannot be undone."
        }
        confirmLabel={t("common.delete") || "Delete"}
        cancelLabel={t("common.cancel") || "Cancel"}
        type="danger"
        onConfirm={async () => {
          if (addressToDelete) {
            await deleteCustomQuickAddress(addressToDelete);
            // Clear selection if the deleted address was selected
            if (selectedStartQuickAddress === addressToDelete) {
              setSelectedStartQuickAddress(null);
            }
            if (selectedEndQuickAddress === addressToDelete) {
              setSelectedEndQuickAddress(null);
            }
            setAddressToDelete(null);
          }
        }}
        onCancel={() => setAddressToDelete(null)}
      />
    </>
  );
};
